import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { animate, utils } from "animejs";
import {
  deleteAdminThread,
  DRAFT_REPLY_AI_FALLBACK_META,
  fetchAdminThreadMembers,
  getAdminDraftReplyPersonas,
  postAdminClearThreadMessages,
  fetchAdminUser,
  fetchAdminUsers,
  postAdminDraftReply,
  postAdminUserMerge,
  putAdminUser,
  refreshAdminUserPhoneDuplicates,
} from "@/lib/api/admin";
import { ApiError } from "@/lib/api/client";
import {
  acknowledgeMessage,
  addThreadFlags,
  // DISABLED (for now): thread assignment — assignment lives on tasks only.
  // assignThread,
  clearThreadMessagesAfter,
  createInternalThread,
  deleteThread,
  deleteThreadMessage,
  editThreadMessage,
  ensurePatientThread,
  ensureAppThreadForPatient,
  fetchThread,
  fetchThreads,
  fetchThreadHistory,
  leaveInternalThread,
  markThreadRead,
  markThreadUnread,
  removeMessageReaction,
  removeThreadFlags,
  revokeWhatsAppMessage,
  setMessageReaction,
  setThreadStatus,
  // unassignThread,
  unsendInternalMessage,
  updateAdminThread,
} from "@/lib/api/threads";
import {
  markThreadReadLocally,
  markThreadUnreadLocally,
  noteDirectThreadIds,
  noteInternalThreadIds,
} from "@/lib/stores/chats-unread-store";
import {
  getWaGroupsUnreadThreadsCount,
  markWaGroupThreadReadLocally,
  noteWaGroupThreadIds,
  subscribeWaGroupsUnread,
} from "@/lib/stores/wa-groups-unread-store";
import {
  buildWaGroupNamesByJid,
  enrichWaGroupRosterNames,
  formatWaGroupSystemLine,
  getWaGroupInfo,
  isWaGroupMessage,
  isWaGroupThread,
  WA_GROUPS_LENS,
} from "@/lib/messages/wa-groups";
import {
  buildMergedGroupIndex,
  getMergedGroupSources,
  mergedGroupThreadIds,
} from "@/lib/messages/merged-wa-groups";
import { hasPermission } from "@/lib/stores/permissions-store";
import { createThreadTask, deleteTask, fetchThreadTasks, updateTask } from "@/lib/api/tasks";
import { fetchMessagesSettings } from "@/lib/api/settings";
import { businessReaction, normalizeReactions } from "@/lib/messages/reactions-helpers";
import {
  applyOptimisticInternalEdit,
  applyOptimisticWhatsAppEdit,
  hasOptimisticEditStamp,
  insertMessageSortedByCreatedAt,
  isWithinWhatsAppEditWindow,
  patchThreadRowForMessageUpdate,
  revertOptimisticEdit,
} from "@/lib/messages/message-edit-helpers";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { getThreadAssignee, isAssignedToUser } from "@/lib/thread-assignee";
import { fetchArchivedMessagesByPhone } from "@/lib/api/archived-messages";
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { fetchUserChatProfile, fetchUserChatProfiles } from "@/lib/api/users";
import {
  getChatConnectionStatus,
  sendConversationMessage,
  sendReadReceipt,
  sendReadReceipts,
  sendTyping,
  subscribeChatConnectionStatus,
  subscribeChatMessages,
  subscribeChatReceipts,
  subscribeUserThreadEvents,
} from "@/lib/realtime/chat-realtime-service";
import { queueDeliveredAck } from "@/lib/realtime/delivery-receipt-queue";
import { mergeReceiptUserIds } from "@/lib/messages/message-delivery-status";
import { subscribeUserRefresh } from "@/lib/realtime/user-realtime-bridge";
import { getAvailabilityMap, getOnlineUserIds, subscribePresence } from "@/lib/realtime/presence-service";
import { getTypingUserIds, subscribeTyping } from "@/lib/realtime/typing-store";
import {
  convertWhatsappWasStickerPayload,
  extractLottieAnimationData,
  localFileMediaPreviewVariant,
  localStickerPresentationMode,
  normalizeStickerBlobForDisplay,
  parseAttachments,
  readFileAsBase64Payload,
} from "@/lib/realtime/message-attachments";
import { imageFilesFromClipboardData } from "@/lib/clipboard/image-clipboard";
import {
  getCurrentUserIdForReadReceipts,
  messageShouldMarkRead,
  shouldAckDelivered,
  shouldFlagThreadUnreadFromWsMessage,
} from "@/lib/realtime/read-receipt-utils";
import { setChatNotificationMessagesRouteState } from "@/lib/notifications/chat-message-notifications";
import {
  consumePendingOpenThreadMessage,
  hasPendingOpenThreadMessage,
  subscribeOpenThreadMessage,
} from "@/lib/notifications/notification-navigation";
import {
  findMessageIndexInHistory,
  isStandaloneSystemLineMessage,
  parseMessageBodyText,
} from "@/lib/realtime/chat-message-helpers";
import { getUserIdsFromJwt } from "@/lib/realtime/jwt-user-ids";
import { formatAdminUserDisplayName, isPlatoSynced, primaryPhoneLineOrEmpty } from "@/lib/admin-user-display";
import {
  INTERNAL_THREADS_LIMIT,
  MESSAGES_RIGHT_PANEL_MODE_KEY,
  MESSAGES_RIGHT_PANEL_OPEN_KEY,
  THREAD_CHUNK_SIZE,
  THREAD_FETCH_MAX_LIMIT,
  THREAD_SEARCH_LIMIT,
} from "@/lib/messages/messages-constants";
import { useThreadListWindow } from "@/hooks/use-thread-list-window";
import {
  adminUserShowsAmbiguousPanel,
  BOT_ACK_DEFAULT_MAX_HOURS,
  BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
  buildThreadMemberRowsFromHistory,
  clusterHistoryMessages,
  fetchUserDisplayProfileEntries,
  fetchUserDisplayProfileEntry,
  formatBotDisplayLabel,
  getBotAckWaitingMinutes,
  getPatientReplyWaitingMinutes,
  getThreadDisplayName,
  getThreadSecondaryIdentity,
  getThreadRowId,
  getWhatsAppInboxWaitingMinutes,
  getWhatsAppPatientSenderLabel,
  getWhatsAppPatientPushName,
  isInternalDirectThread,
  isWhatsAppBridgeStubName,
  isWhatsAppStaffAnswerMessage,
  isSenderBotMessage,
  membershipSubtitle,
  normalizeApiThreadRow,
  normalizeLastMessageSender,
  parseThreadMemberIdsFromMembersPayload,
  resolveDevPatientImpersonationUserId,
  normalizeBotExternalMatchUserIds,
  normalizeMemberSource,
  PATIENT_REPLY_DEFAULT_MAX_HOURS,
  PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
  persistSelectedThreadId,
  readMessagesRightPanelOpen,
  readPersistedSelectedThreadId,
} from "@/lib/messages/messages-helpers";
import {
  getMutedChatThreadIds,
  getUserPreferences,
  setUserPreferences,
  subscribeUserPreferences,
  toggleChatMuted,
} from "@/lib/stores/user-preference-store";
import { useMergedWaGroupsView, useMergedWhatsAppView } from "@/hooks/use-user-preferences";
import {
  buildMergedClusters,
  buildMergedSources,
  buildReplyFromSources,
  collectPatientWhatsappThreadRows,
  isWhatsappThreadRow,
  threadWhatsappBotId,
} from "@/lib/messages/merged-threads";
import {
  getThreadGroupsConfig,
  hydrateThreadGroupsFromServer,
  subscribeThreadGroups,
} from "@/lib/stores/thread-groups-store";
import {
  adoptThreadsOverviewSnapshot,
  getThreadsOverviewSnapshot,
  refreshThreadsOverview,
} from "@/lib/stores/threads-overview-store";
import { useWorkspaceOwnerId } from "@/lib/workspaces/workspace-owner-context";
import { getThreadFlagStrings } from "@/lib/thread-flags";
import {
  getDirectChatAvailability,
  isDirectChatKnownDisabled,
  markDirectChatDisabledFromError,
  markDirectChatEnabled,
  subscribeDirectChatAvailability,
} from "@/lib/direct-chat-availability";
import { toast } from "sonner";

// Session-scoped cache of the "Rewrite with AI" persona/switch metadata
// (GET /api/admin/threads/draft-reply/options). Fetched lazily on first dialog open, best-effort:
// stays null after a failed/empty fetch so the next open retries.
// Cleared on EVERY auth change at module level: the pane-level auth listeners are owner-guarded
// (outgoing panes deliberately ignore the switch event) and hidden panes have no live listeners,
// so no pane can be trusted to clear this cross-user cache — persona/switch metadata is
// tenant/user-scoped and must never carry from user A into user B's rewrite dialog.
let draftReplyAiMetaCache = null;
if (typeof window !== "undefined") {
  subscribeAuthStateChange(() => {
    draftReplyAiMetaCache = null;
  });
}

/** Stable timestamp (ms) for sorting/threshold of a thread's last message; 0 when missing. */
function threadLastMessageMs(thread) {
  const ms = new Date(thread?.lastMessageAt || 0).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** Newest-first comparator for thread rows. */
function byLastMessageDesc(a, b) {
  return threadLastMessageMs(b) - threadLastMessageMs(a);
}

/** Stable empty list so internal scope / cleared filters never churn memo consumers. */
const EMPTY_ATTENTION_LIST = [];
// Stable empty array so memos that usually return "nothing" don't re-render consumers.
const EMPTY_ARRAY = [];

/** Messages fetched per history page (initial open and each "load older"). */
const HISTORY_PAGE_SIZE = 30;
/** Max threads whose history is kept warm in memory for instant re-open (LRU). */
const HISTORY_CACHE_MAX = 20;
/**
 * How long a landed jump keeps ownership of the viewport while the rest of the freshly loaded
 * history finishes laying out. Long enough to outlast image decoding in a large conversation, short
 * enough that it never feels like the chat is refusing to scroll; any real user input ends it early.
 */
const JUMP_SETTLE_MS = 900;
/**
 * Max threads fetched for one patient when building the merged WhatsApp view. A patient has one
 * thread per clinic number, so this is generous; it only bounds the request.
 */
const MERGED_PATIENT_THREAD_LIMIT = 25;

/**
 * Reconcile a freshly-fetched newest page into the currently-displayed history (a silent refresh
 * after a cache hit). If they overlap (share a message id) the older loaded pages are preserved and
 * the union is returned ascending; if they don't overlap, too many messages arrived while away and
 * we replace with the fresh page (gap:true) so nothing stale lingers between the two.
 *
 * Optimistic placeholders have NO server id (they key by clientId only) — they must never enter
 * the id-keyed union, where every id-less row collapses onto the '' key and all but one vanish;
 * they are carried through both paths untouched so the send/echo reconciliation (or the thaw
 * sweep's failed-marking) stays visible.
 */
function mergeHistoryNewest(prev, fresh) {
  if (!Array.isArray(fresh) || fresh.length === 0) return { items: prev, gap: false };
  const placeholders = prev.filter((m) => m?.metadata?.optimistic === true && !m?.id);
  const settledPrev = prev.filter((m) => !(m?.metadata?.optimistic === true && !m?.id));
  const prevIds = new Set(settledPrev.map((m) => String(m?.id || "")));
  const overlap = fresh.some((m) => prevIds.has(String(m?.id || "")));
  const sortAscending = (rows) =>
    rows.sort((a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime());
  if (!overlap) return { items: sortAscending([...fresh, ...placeholders]), gap: true };
  const byId = new Map(settledPrev.map((m) => [String(m?.id || ""), m]));
  for (const m of fresh) byId.set(String(m?.id || ""), m);
  return { items: sortAscending([...byId.values(), ...placeholders]), gap: false };
}

/**
 * True when the thread's last-message time falls within the selected range, compared by whole
 * local day: [from 00:00:00, to 23:59:59]. `to` falls back to `from` (single-day range). A thread
 * with no/invalid last-message time never matches while the filter is active.
 */
function threadLastMessageInRange(thread, range) {
  if (!range?.from) return true;
  const raw = thread?.lastMessageAt;
  const t = raw ? new Date(raw).getTime() : NaN;
  if (Number.isNaN(t)) return false;
  const from = new Date(range.from);
  from.setHours(0, 0, 0, 0);
  const to = new Date(range.to ?? range.from);
  to.setHours(23, 59, 59, 999);
  return t >= from.getTime() && t <= to.getTime();
}

// Seamless travel: the bubble lifts out of the textarea (where the text was typed) and glides to
// WhatsApp-style pop: the sent bubble scales in at its spot (its drop-shadow scales with it, so it
// "lifts off" the background). Short and snappy.
const OPTIMISTIC_POP_DURATION_MS = 500;
// Backstop: WS sends are fire-and-forget, so an echo that never lands would otherwise leave a
// placeholder stuck as "sending" forever.
const OPTIMISTIC_FAIL_TIMEOUT_MS = 10000;
// A reader near the chat bottom still counts as "at the bottom" for stick-to-bottom
// purposes; anyone further up into older history is never yanked down. The window is a
// quarter of the viewport with a 120px floor — a human parked "about a message" above
// the bottom expects the chat to keep following. The original 40px was so tight that a
// single trackpad nudge (or a momentum stop ~80px short) silently disarmed follow-mode,
// and from then on "new messages don't scroll" until the reader re-hit the exact bottom.
const CHAT_NEAR_BOTTOM_MIN_PX = 120;
function chatNearBottomPx(el) {
  return Math.max(CHAT_NEAR_BOTTOM_MIN_PX, Math.round((el?.clientHeight || 0) * 0.25));
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * "FLIP" a stick-to-bottom scroll. The caller snaps scrollTop to the bottom *instantly* (no
 * main-thread scroll animation — that's the jank/delay source); this hides the snap by offsetting
 * the content up by `delta` and animating the offset away, so the visible slide runs entirely on the
 * compositor as a `transform`. Smooth, and starts on the first frame (no delay). `utils.remove`
 * cancels any in-flight slide so a burst of messages doesn't fight over the transform.
 */
function flipSlide(contentEl, delta, duration, ease) {
  if (!contentEl || delta < 1) return;
  // A hidden window (backgrounded/occluded Electron window, background tab) never runs animation
  // frames: `animate` below would apply the invert offset and then park on it forever. A content
  // element left translated down by `delta` inflates the viewport's scrollable overflow, stranding
  // the just-snapped reader exactly `delta` px above the real bottom — the "background window
  // doesn't follow new messages" bug. The slide is invisible there anyway: drop it, clear any
  // previously parked offset, and let the caller's instant snap stand on its own.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    utils.remove(contentEl);
    contentEl.style.willChange = "";
    contentEl.style.transform = "";
    return;
  }
  utils.remove(contentEl);
  contentEl.style.willChange = "transform";
  // Pre-set the offset so the first paint after the snap shows the content unmoved, then animate
  // it away — no flash of the jumped position.
  contentEl.style.transform = `translateY(${delta}px)`;
  animate(contentEl, {
    translateY: [delta, 0],
    duration,
    ease,
    onComplete: () => {
      contentEl.style.willChange = "";
      contentEl.style.transform = "";
    },
  });
}

/**
 * Index of the oldest unreconciled optimistic placeholder that the given echo settles
 * (same sender + same body text). Placeholders carry no server id, so the normal
 * id/externalId dedup can't match them — this bridges placeholder → echo.
 */
function findOptimisticMatchIndex(list, msg) {
  const senderId = String(msg?.sender?.userId || "").trim();
  if (!senderId) return -1;
  const body = parseMessageBodyText(msg?.body);
  for (let k = 0; k < list.length; k++) {
    const m = list[k];
    if (m?.metadata?.optimistic !== true) continue;
    if (String(m?.sender?.userId || "").trim() !== senderId) continue;
    if (parseMessageBodyText(m?.body) === body) return k;
  }
  return -1;
}

export function useMessagesPage({ scope = "normal" } = {}) {
  // Three scopes share this hook:
  //   "normal"   — the patient Messages experience: folders, attention sections, the filter bar,
  //                the triage board, WhatsApp lenses, tasks.
  //   "internal" — staff-only Chats: one flat list, plus presence and typing.
  //   "direct"   — private 1:1 contact chats (metadata.conversationType === "app_direct"), found
  //                through the mobile app's address-book matcher.
  //
  // "internal" and "direct" are both SIMPLE lists — everything in the first bullet belongs to the
  // patient scope and must stay off both of them. That shared shape is `isScopedList`, and it is
  // what nearly every conditional below actually means; the handful that are genuinely about
  // staff chats (presence, typing) keep using `isInternalScope`.
  //
  // Kept in a ref so the many useCallback-wrapped handlers can read the current scope without
  // widening dependency arrays.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const isInternalScope = scope === "internal";
  const isDirectScope = scope === "direct";
  const isScopedList = isInternalScope || isDirectScope;
  // Same fact, readable from the many useCallback-wrapped handlers that hold scopeRef instead of
  // closing over the render value.
  const scopedListRef = useRef(isScopedList);
  scopedListRef.current = isScopedList;
  // Which pane owns a buffered notification jump ('normal' = patient Messages, 'internal' = Chats).
  // Declared up here because the persisted-selection restore below also reads it — a `const` further
  // down would be in the temporal dead zone when that effect's dependency array is evaluated.
  const openTargetScope = isDirectScope ? "direct" : isInternalScope ? "internal" : "normal";
  // The user this workspace pane belongs to ('' outside a pane = owner-always-matches). Constant
  // for a pane's lifetime; consumed by the auth-state listener below so an outgoing user's pane
  // never resets itself against the incoming user's session.
  const ownerUserId = useWorkspaceOwnerId();
  // Online presence for the Chats view: which co-workers currently hold a live socket, plus
  // their self-set availability (colors the dot). Only subscribed in internal scope to avoid
  // churn in Messages.
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [availabilityByUserId, setAvailabilityByUserId] = useState(() => ({}));
  useEffect(() => {
    if (scope !== "internal") return undefined;
    setOnlineUserIds(new Set(getOnlineUserIds()));
    setAvailabilityByUserId(getAvailabilityMap());
    return subscribePresence((ids) => {
      setOnlineUserIds(new Set(ids));
      setAvailabilityByUserId(getAvailabilityMap());
    });
  }, [scope]);
  // "Who is typing" (Chats only): a coarse tick re-derives the per-thread sets below;
  // the store TTL-expires entries, firing the subscription again when they drop.
  const [typingTick, setTypingTick] = useState(0);
  useEffect(() => {
    if (scope !== "internal") return undefined;
    return subscribeTyping(() => setTypingTick((n) => n + 1));
  }, [scope]);
  // Composer keystrokes broadcast a throttled typing ping to the open chat's members.
  const notifyComposerTyping = useCallback(() => {
    if (scopeRef.current !== "internal") return;
    const tid = String(selectedThreadIdRef.current || "").trim();
    if (tid) sendTyping(tid);
  }, []);
  // Muted chats (device preference): rows show a bell-off glyph and the context menu
  // offers Mute/Unmute; the notification + nav-bubble effects live in their stores.
  const [mutedChatThreadIds, setMutedChatThreadIds] = useState(() => getMutedChatThreadIds());
  useEffect(() => {
    // Muting applies to any personal conversation, private ones included — the preference store is
    // keyed by thread id and does not care which scope owns it.
    if (!isScopedList) return undefined;
    setMutedChatThreadIds(getMutedChatThreadIds());
    return subscribeUserPreferences(() => setMutedChatThreadIds(getMutedChatThreadIds()));
  }, [scope, isScopedList]);
  const handleToggleChatMute = useCallback((threadId) => {
    toggleChatMuted(threadId);
  }, []);
  // Seed the first render from the app-wide overview cache (normal scope) so returning to Messages
  // paints the last-known list instantly instead of blanking for a fresh fetch — the mount effect
  // still kicks a background refresh to reconcile. Null on the very first app load (nothing cached
  // yet) and in internal scope, where seeding falls back to empty and the old load path runs.
  // Lazy useState (mount-once): hidden Activity panes still render, and the snapshot getter is not
  // render-pure (it can drop + re-emit a snapshot owned by another user) — it must not run on
  // every render of every pane.
  const [initialOverview] = useState(() =>
    isScopedList ? null : getThreadsOverviewSnapshot(),
  );
  const seedRows = (rows) =>
    (Array.isArray(rows) ? rows : []).map((t) => normalizeApiThreadRow(t, null));
  // Internal Chats only: the staff-only list is one complete fetch. The normal scope's list rows
  // live in the sparse thread-list window below (`threadListWindow`), not here.
  const [threads, setThreads] = useState([]);
  // Server-computed attention sections (normal scope): ALL "Waiting for you", "Unanswered patient
  // reply" and "Unanswered bot replies" threads regardless of the windowed page or page size,
  // from the overview endpoint. Replaced wholesale on every page-1 refresh; rows share the
  // regular row shape.
  const [attentionWaiting, setAttentionWaiting] = useState(() =>
    seedRows(initialOverview?.attention?.waiting),
  );
  const [attentionPatientReply, setAttentionPatientReply] = useState(() =>
    seedRows(initialOverview?.attention?.patientReply),
  );
  const [staffReactedPatientReplyMessageIds, setStaffReactedPatientReplyMessageIds] = useState(
    () => new Set(),
  );
  const [attentionBotAck, setAttentionBotAck] = useState(() =>
    seedRows(initialOverview?.attention?.botAck),
  );
  // True when the server's attention candidate scan hit its cap, so the three lists above may be
  // missing their OLDEST rows. Surfaced in the list rather than swallowed — a short triage list
  // with no explanation is indistinguishable from an empty queue.
  const [attentionTruncated, setAttentionTruncated] = useState(() =>
    Boolean(initialOverview?.attention?.truncated),
  );
  // The per-user default assistant thread, surfaced regardless of the window/page so it is never
  // dropped from the list. Merged into the sorted list client-side — sorted into its natural
  // position among the others (not pinned to the top) and kept visible through active filters.
  // From the overview endpoint; empty in internal scope. Seeded from the overview cache too.
  const [defaultThreads, setDefaultThreads] = useState(() => seedRows(initialOverview?.pinned));
  // The selected thread's row fetched on demand when the selection lives outside EVERY loaded
  // list — opened from the triage board / Tasks page / a notification, or restored from a
  // previous session, while beyond the windowed page and the attention sections. Used only as
  // the last-resort header row; any list copy of the thread takes precedence.
  const [selectedThreadOffListRow, setSelectedThreadOffListRow] = useState(null);
  // Every thread the OPEN thread's patient is a member of, fetched by patient id (unwindowed) for
  // the merged WhatsApp view. The windowed list only holds recently-active threads, so a patient's
  // quiet second-clinic conversation would otherwise be invisible and the merged stream would
  // silently collapse to the single open thread. Declared up here (not in the merged block below)
  // because `updateThreadRowEverywhere` must be able to patch these rows too.
  const [mergedPatientThreadRows, setMergedPatientThreadRows] = useState([]);
  // Which (patient, tick) `mergedPatientThreadRows` was fetched for — switching between two
  // clinics of the same patient must not refetch, while a thread create/delete event bumps the
  // tick so a brand-new sibling thread is picked up.
  const mergedPatientFetchKeyRef = useRef("");
  const [mergedPatientRefreshTick, setMergedPatientRefreshTick] = useState(0);
  const threadsRefreshTimerRef = useRef(null);
  // Server-side search results (normal scope), kept separate from the virtual window so clearing
  // the search box restores the list instantly with no refetch. One capped fetch per settle —
  // recall is server-capped, so there is no paging. `searchSeqRef` guards against out-of-order
  // responses from rapid typing; `searchTruncated` drives the "first N" affordance.
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const searchSeqRef = useRef(0);
  // Whether the server-wide contact-chat switch is known to be OFF (session latch; see
  // lib/direct-chat-availability). Only meaningful in the direct scope, where the Private pane
  // swaps its empty state for a "not enabled on this server" notice.
  const [directChatDisabled, setDirectChatDisabled] = useState(
    () => getDirectChatAvailability() === "disabled",
  );
  useEffect(
    () => subscribeDirectChatAvailability((status) => setDirectChatDisabled(status === "disabled")),
    [],
  );
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  // Per-thread history: load only the latest HISTORY_PAGE_SIZE on open, page older on scroll, and
  // keep recent threads warm so re-opening is instant. `historyAllOlderLoaded` hides the "Load
  // older" affordance once the start of the conversation is reached.
  const [historyAllOlderLoaded, setHistoryAllOlderLoaded] = useState(false);
  const [historyLoadingOlder, setHistoryLoadingOlder] = useState(false);
  // Merged WhatsApp view (see the merged block far below): ascending history per sibling clinic
  // OTHER than the open one, and whether each has reached the start of its conversation. Declared
  // here with the rest of the history state because the prepend-anchor layout effect and
  // `loadOlderHistory` — both above the merged block — depend on them.
  const [siblingHistories, setSiblingHistories] = useState(() => new Map());
  const siblingHistoriesRef = useRef(siblingHistories);
  siblingHistoriesRef.current = siblingHistories;
  const [siblingAllLoaded, setSiblingAllLoaded] = useState(() => new Map());
  const siblingAllLoadedRef = useRef(siblingAllLoaded);
  siblingAllLoadedRef.current = siblingAllLoaded;
  const historyCacheRef = useRef(new Map());
  const historyLoadingOlderRef = useRef(false);
  const historyAllOlderLoadedRef = useRef(false);
  // Set before a "load older" prepend so the layout effect can pin the viewport and the bottom-
  // auto-scroll effect can skip that one update (otherwise prepending would jump to the bottom).
  const prependAnchorRef = useRef(null);
  // Merged-view state read by `loadOlderHistory`, which is defined long before the merged block
  // computes it. Assigned during render further down.
  const mergedActiveRef = useRef(false);
  const mergedSiblingIdsRef = useRef([]);
  const skipNextAutoScrollRef = useRef(false);
  // True while the send "fly" pop/slide owns the chatContent transform; the stick-to-bottom
  // ResizeObserver defers to it so it doesn't cancel that animation with a competing flipSlide.
  const flyAnimatingRef = useRef(false);
  const [userDisplayNamesById, setUserDisplayNamesById] = useState({});
  const userDisplayNamesByIdRef = useRef({});
  const [userAvatarUrlsById, setUserAvatarUrlsById] = useState({});
  // The composer's draft text lives in LOCAL state inside `MessageComposerContainer` so keystrokes
  // don't re-render this hook / MessagesPage / the whole thread list. We reach it imperatively for
  // the flows that live outside the composer (send read+clear, AI seed/apply) via this handle.
  const composerApiRef = useRef(null);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  // Mirror for handlers that must read the CURRENT picks without a stale closure (edit mode parks
  // them); assigned each render right here.
  const selectedAttachmentsRef = useRef([]);
  selectedAttachmentsRef.current = selectedAttachments;
  // Edit mode: WHICH message the next Send replaces — the text itself lives in the composer (see
  // MessageComposerContainer's beginEdit/finishEdit), so keystrokes never re-render this hook.
  // null = normal send. { id, threadId, kind: "internal"|"app"|"whatsapp", hasAttachments, message,
  // unresolvedMentionIds } — `message` is the pre-edit copy used for no-op detection and rollback.
  const [editingMessage, setEditingMessage] = useState(null);
  const editingMessageRef = useRef(null);
  // Attachments parked while editing (an edit cannot change attachments); restored on exit.
  const stashedAttachmentsRef = useRef(null);
  // exitEditMode is declared far below; the WS listener (declared before it) cancels through this.
  const exitEditModeRef = useRef(() => {});
  // Id → {label} for re-seeding a message's @mentions into the composer on edit; assigned after the
  // memberDirectory memo at the bottom of the hook.
  const memberDirectoryRef = useRef({});
  // Mirror of activeThreadIsWhatsApp (computed further down) for handlers declared before it.
  const activeThreadIsWhatsAppRef = useRef(false);
  const [composerAttachmentPreviews, setComposerAttachmentPreviews] = useState([]);
  const [composerStickerPreviews, setComposerStickerPreviews] = useState([]);
  const [sendInProgress, setSendInProgress] = useState(false);
  const [devSendAsPatient, setDevSendAsPatient] = useState(false);
  const [clearThreadMessagesInProgress, setClearThreadMessagesInProgress] = useState(false);
  const [draftReplyAiModalOpen, setDraftReplyAiModalOpen] = useState(false);
  const [draftReplyAiModalSource, setDraftReplyAiModalSource] = useState("");
  const [draftReplyAiModalOutput, setDraftReplyAiModalOutput] = useState("");
  const [draftReplyAiGenerating, setDraftReplyAiGenerating] = useState(false);
  // "Rewrite with AI" persona/switch metadata plus the persisted tone preferences. Metadata is
  // fetched lazily on first dialog open (module-level session cache); until then the hardcoded
  // fallback keeps the picker fully functional.
  const [draftReplyAiMeta, setDraftReplyAiMeta] = useState(() => draftReplyAiMetaCache || DRAFT_REPLY_AI_FALLBACK_META);
  const [draftReplyAiPersonaPref, setDraftReplyAiPersonaPref] = useState(() => getUserPreferences().draftReplyAiPersona);
  const [draftReplyAiOptionsPref, setDraftReplyAiOptionsPref] = useState(() => getUserPreferences().draftReplyAiOptions);
  /** Guards against duplicate in-flight metadata fetches (reset on failure so a later open retries). */
  const draftReplyAiMetaFetchRef = useRef(false);
  const [flowSessionResetDialogOpen, setFlowSessionResetDialogOpen] = useState(false);
  const [chatConnectionStatus, setChatConnectionStatus] = useState(() => getChatConnectionStatus());
  const historyEndRef = useRef(null);
  const chatScrollAreaRef = useRef(null);
  // Whether the reader is at (or near) the chat bottom, tracked from the viewport's scroll events;
  // the stick-to-bottom effect only snaps within an already-open thread when this is true.
  const chatWasAtBottomRef = useRef(true);
  // Thread the stick-to-bottom effect last ran for: a change means "thread just opened" (always
  // snap); same id means an in-thread update (respect chatWasAtBottomRef).
  const chatSnapThreadIdRef = useRef(null);
  // A jump that has just landed (search hit, notification, pinned message) OWNS the viewport for a
  // short window. Loading a jump target pulls in the whole conversation, and those hundreds of
  // bubbles keep growing after the scroll — images decode, attachments and merged rails lay out —
  // which both moves the target and makes the stick-to-bottom observer fire. Without this, that
  // observer still reads the reader as "parked at the bottom" (chatWasAtBottomRef only updates on
  // async scroll events) and yanks the conversation back to the newest message, so the jump looks
  // like it did nothing.
  const jumpTargetIdRef = useRef("");
  const jumpSettleUntilRef = useRef(0);
  // The message-content wrapper inside the scroll viewport; transformed for the GPU "FLIP" scroll.
  const chatContentRef = useRef(null);
  const fileInputRef = useRef(null);
  // One-shot pop request set on send; consumed by the pop effect once the placeholder paints.
  const pendingFlyRef = useRef(null);
  // clientId -> { timer } for optimistic placeholders still awaiting their WS echo.
  const pendingOptimisticRef = useRef(new Map());
  const optimisticCounterRef = useRef(0);
  const historyItemsRef = useRef([]);
  const selectedThreadIdRef = useRef(null);
  const handleThreadClickRef = useRef(() => { });
  const restoredSelectedThreadRef = useRef(false);
  // The thread id currently selected via the persisted-session restore (null once the user picks a
  // thread themselves). Requests probing this id suppress the global 403 toast: the user never
  // asked to open it, so "You don't have permission to do that." would scold them for nothing.
  const sessionRestoredThreadIdRef = useRef(null);
  // The list can paint from the overview cache (seeded above) before the first network load lands,
  // so the saved-selection restore must wait for fresh threads — restoring against a stale seed
  // could wrongly clear a selection that the fresh page actually contains.
  const firstThreadsLoadDoneRef = useRef(false);
  /** messageId to reveal once history loads (set when opening a thread from a notification). */
  const pendingScrollMessageIdRef = useRef(null);
  const highlightClearTimerRef = useRef(null);
  /** Message briefly ring-highlighted after jumping to it from a notification. */
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  /** threadId -> true when WS reported new messages in another thread (red dot until user opens that thread) */
  const [localUnreadByThreadId, setLocalUnreadByThreadId] = useState({});
  // "New messages" divider anchor for the open internal chat: the first history item the
  // caller hadn't read when opening it. Computed BEFORE read receipts fire, kept while the
  // thread stays open (so the line doesn't vanish mid-read), reset on every thread switch.
  const [newMessagesDividerId, setNewMessagesDividerId] = useState(null);
  const threadsRef = useRef([]);
  const [threadFlagsError, setThreadFlagsError] = useState("");
  const threadFlagsErrorClearRef = useRef(null);
  const pendingFlowSessionThreadIdRef = useRef(null);
  /** threadId -> direct/team/bot ids for WhatsApp wait labels (populated for the open thread by loadAdminThreadMembers). */
  const [threadMemberIdsByThreadId, setThreadMemberIdsByThreadId] = useState(() => ({}));
  const [listClockMs, setListClockMs] = useState(() => Date.now());
  const [threadFilterLockedOnly, setThreadFilterLockedOnly] = useState(
    () => getUserPreferences().messagesThreadFilterLockedOnly,
  );
  // DISABLED (for now): the staff_handles flag is no longer applied anywhere, so
  // its filter pill is hidden too.
  // const [threadFilterStaffHandlesOnly, setThreadFilterStaffHandlesOnly] = useState(
  //   () => getUserPreferences().messagesThreadFilterStaffHandlesOnly,
  // );
  const [threadFilterAwaitingReplyOnly, setThreadFilterAwaitingReplyOnly] = useState(
    () => getUserPreferences().messagesThreadFilterAwaitingReplyOnly,
  );
  const [threadFilterWhatsappOnly, setThreadFilterWhatsappOnly] = useState(
    () => getUserPreferences().messagesThreadFilterWhatsappOnly,
  );
  // DISABLED (for now): thread assignment — assignment lives on tasks only, so the
  // assignee filters are hidden.
  // const [threadFilterAssignedToMe, setThreadFilterAssignedToMe] = useState(
  //   () => getUserPreferences().messagesThreadFilterAssignedToMe,
  // );
  // const [threadFilterAssigneeId, setThreadFilterAssigneeId] = useState(
  //   () => getUserPreferences().messagesThreadFilterAssigneeId,
  // );
  // Free-text thread search. Transient (not persisted across launches) — a stale search box on
  // relaunch would silently hide threads. Matches thread title/name and the last-message preview.
  const [threadSearchQuery, setThreadSearchQuery] = useState("");
  const threadSearchQueryRef = useRef("");
  threadSearchQueryRef.current = threadSearchQuery;
  // "Last message sent" date-range filter as a { from, to } Dates object, or null for no filter
  // (`to` may be momentarily undefined mid-selection). Transient like the search box — absolute
  // dates would go stale on relaunch and silently hide threads.
  const [threadFilterLastMessageRange, setThreadFilterLastMessageRange] = useState(null);
  const [threadListView, setThreadListView] = useState(
    () => getUserPreferences().messagesThreadListView,
  );
  // Messages list lens — "all" (chronological, the calm default), "needs" (the triage piles),
  // "mine" (assigned to me) — and the view mode (thread list vs triage board). Both persisted.
  const [threadLens, setThreadLens] = useState(() => getUserPreferences().messagesThreadLens);
  const [viewMode, setViewMode] = useState(() => getUserPreferences().messagesViewMode);
  // The selected thread's checklist (Tasks panel). Reloaded on selection change and on
  // task WS events; the seq ref drops stale responses from rapid thread switching.
  const [threadTasks, setThreadTasks] = useState([]);
  const [threadTasksLoading, setThreadTasksLoading] = useState(false);
  const threadTasksSeqRef = useRef(0);
  // Which right-hand panel the conversation shows: the participants list or Tasks.
  const [rightPanelMode, setRightPanelMode] = useState(() => {
    try {
      return window.localStorage.getItem(MESSAGES_RIGHT_PANEL_MODE_KEY) === "tasks"
        ? "tasks"
        : "participants";
    } catch {
      return "participants";
    }
  });
  // "Unanswered bot replies" thresholds. Tenant-wide shared settings (global_settings on the
  // server); adopted from every overview response, so an admin's edit propagates to everyone
  // on their next list refresh. Defaults warm-start the first render.
  const [botAckThresholdMinutes, setBotAckThresholdMinutes] = useState(() =>
    Number.isFinite(initialOverview?.settings?.botAck?.thresholdMinutes)
      ? initialOverview.settings.botAck.thresholdMinutes
      : BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
  );
  const [botAckMaxHours, setBotAckMaxHours] = useState(() =>
    Number.isFinite(initialOverview?.settings?.botAck?.maxHours)
      ? initialOverview.settings.botAck.maxHours
      : BOT_ACK_DEFAULT_MAX_HOURS,
  );
  // "Unanswered patient reply" thresholds — the same deal, but a SEPARATE window: these used to
  // ride on the bot-ack pair, so a patient nobody answered aged out of "Needs you" after 48h.
  const [patientReplyThresholdMinutes, setPatientReplyThresholdMinutes] = useState(() =>
    Number.isFinite(initialOverview?.settings?.patientReply?.thresholdMinutes)
      ? initialOverview.settings.patientReply.thresholdMinutes
      : PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
  );
  const [patientReplyMaxHours, setPatientReplyMaxHours] = useState(() =>
    Number.isFinite(initialOverview?.settings?.patientReply?.maxHours)
      ? initialOverview.settings.patientReply.maxHours
      : PATIENT_REPLY_DEFAULT_MAX_HOURS,
  );
  // Configured WhatsApp business numbers ([{botId,label}]) from settings — drives the "Number"
  // thread filter and the new-chat send-from picker. Empty when multi-number is off.
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  // WhatsApp number ("section") filter: the gateway bot id to show, or "" for all numbers.
  // Transient (not persisted) — a stored bot id could go stale if the configured numbers change.
  const [threadFilterWhatsappBotId, setThreadFilterWhatsappBotId] = useState("");
  // "Healo app" channel filter: in-app patient conversations only (metadata.conversationType
  // == "app"). Transient like the number filter — a persisted channel pill could silently
  // hide threads on relaunch.
  const [threadFilterAppOnly, setThreadFilterAppOnly] = useState(false);

  // Merged WhatsApp GROUP rows (one row per group jid instead of one per clinic gateway).
  // Read HERE rather than next to mergedWhatsAppView further down, because
  // threadListWindowFilters below consumes it — a const declared after that memo would be in
  // the temporal dead zone at its point of use, which is a ReferenceError, not a stale value.
  const mergedWaGroupsView = useMergedWaGroupsView() && !isScopedList;

  // ---- Fully-virtual thread list (normal scope) -------------------------------------------
  // The filter pills become fetch params so the server's exact `total` always matches what the
  // list renders. The date range is resolved to LOCAL whole-day instants here — the same
  // [00:00:00, 23:59:59.999] bounds the client predicate uses for the attention lists.
  const threadListWindowFilters = useMemo(() => {
    let lastMessageFrom = "";
    let lastMessageTo = "";
    if (threadFilterLastMessageRange?.from) {
      const from = new Date(threadFilterLastMessageRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(threadFilterLastMessageRange.to ?? threadFilterLastMessageRange.from);
      to.setHours(23, 59, 59, 999);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        lastMessageFrom = from.toISOString();
        lastMessageTo = to.toISOString();
      }
    }
    // The WhatsApp Groups lens swaps the window's population to the server's `scope=groups`
    // partition. While it's active the channel pills (WhatsApp / Healo app) and the
    // color-folders tail param would contradict the scope — the server would return an empty
    // intersection — so they're neutralized here rather than trusted to be untoggled (the
    // pills persist across lens switches by design).
    const waGroupsLens = threadLens === WA_GROUPS_LENS;
    // A clinic chip narrows the list to ONE gateway, so merging siblings from the other
    // branches would contradict it — and worse, the merged row's sources[] would omit the
    // clinics the group actually spans, making the reply-from picker wrong rather than merely
    // redundant. THIS IS THE SINGLE ENFORCEMENT POINT: everything downstream derives from the
    // rows this query returns. (The server re-checks and bypasses merging too.)
    const mergeGroups = waGroupsLens && mergedWaGroupsView && !threadFilterWhatsappBotId;
    return {
      scope: waGroupsLens ? "groups" : "",
      mergeGroups,
      locked: threadFilterLockedOnly,
      awaitingReply: threadFilterAwaitingReplyOnly,
      whatsapp: waGroupsLens ? false : threadFilterWhatsappOnly,
      // "Healo app" channel pill → the server-side conversationType filter (fetchThreads
      // gives this precedence over the legacy whatsapp boolean; the pills are mutually
      // exclusive in the UI anyway).
      conversationType: !waGroupsLens && threadFilterAppOnly ? "app" : "",
      whatsappBotId: threadFilterWhatsappBotId,
      lastMessageFrom,
      lastMessageTo,
      // Groups view: the virtual region is the UNGROUPED tail (the server-complete group
      // sections render above it as leading items).
      groups: !waGroupsLens && threadListView === "groups" ? "exclude" : "",
    };
  }, [
    threadFilterLockedOnly,
    threadFilterAwaitingReplyOnly,
    threadFilterWhatsappOnly,
    threadFilterAppOnly,
    threadFilterWhatsappBotId,
    threadFilterLastMessageRange,
    threadListView,
    threadLens,
    mergedWaGroupsView,
  ]);
  const threadFiltersActive =
    threadFilterLockedOnly ||
    threadFilterAwaitingReplyOnly ||
    threadFilterWhatsappOnly ||
    threadFilterAppOnly ||
    Boolean(threadFilterWhatsappBotId) ||
    Boolean(threadFilterLastMessageRange?.from);
  // Whether the WhatsApp Groups lens is active (drives the window scope, the search scope and
  // several presentation gates in messages-page).
  const waGroupsLensActive = threadLens === WA_GROUPS_LENS;
  // The unfiltered overview response can seed/refresh the window only when the window's query IS
  // the unfiltered chronological list — under any pill (or the groups-view tail) the window
  // refetches with its own params instead. The overview is default-scope, which EXCLUDES group
  // threads, so it must never seed the Groups lens's window either.
  const windowAdoptsOverview =
    !threadFiltersActive && threadListView !== "groups" && !waGroupsLensActive;
  const windowAdoptsOverviewRef = useRef(windowAdoptsOverview);
  windowAdoptsOverviewRef.current = windowAdoptsOverview;
  // True while the mount's overview request — which seeds the window's offsets [0, limit) and its
  // exact total — is still in flight. It suppresses the window's own cold-start chunk-0 fetch, so
  // a cold load issues ONE request for those rows instead of the overview plus an immediate,
  // near-identical GET /api/threads?offset=0&withTotal=1. Starts false when a cached snapshot
  // already seeds the window synchronously below.
  const [threadsSeedPending, setThreadsSeedPending] = useState(
    () => !isScopedList && !initialOverview,
  );
  const threadListWindow = useThreadListWindow({
    enabled: !isScopedList,
    filters: threadListWindowFilters,
    // First-render seed from the app-wide overview cache (instant paint on pane re-mount).
    seed:
      !isScopedList && windowAdoptsOverview && initialOverview
        ? { rows: initialOverview.threads, total: initialOverview.page?.total }
        : null,
    // Only the unfiltered chronological window is seeded by the overview; under a filter pill (or
    // the groups-view tail) the window owns its own params and must fetch immediately.
    seedPending: threadsSeedPending && windowAdoptsOverview,
  });
  const threadListWindowRef = useRef(threadListWindow);
  threadListWindowRef.current = threadListWindow;

  // Merged Groups view: a WS message arriving on a NON-canonical sibling names a thread that is
  // not in the window's index, so a row patch keyed on it would silently no-op and the merged
  // row would sit stale until the next refresh. This maps sibling id -> canonical row id.
  // Rebuilt from the loaded rows whenever fetched rows land (threadListWindow.snapshot is the
  // identity-changing dep the groups-unread effect below already uses).
  const mergedGroupCanonicalRef = useRef(null);
  mergedGroupCanonicalRef.current = useMemo(
    () =>
      mergedWaGroupsView && waGroupsLensActive
        ? buildMergedGroupIndex(threadListWindow.loadedRowsSnapshot())
        : null,
    [mergedWaGroupsView, waGroupsLensActive, threadListWindow],
  );
  // Falls through to the id itself when there is no mapping, so every non-merged path is
  // unchanged.
  const canonicalRowIdFor = useCallback((threadId) => {
    const tid = String(threadId || "").trim();
    return mergedGroupCanonicalRef.current?.get(tid) || tid;
  }, []);

  // WhatsApp Groups unread badge on the lens control — fed by the app-wide scope=groups store
  // (which also refreshes itself on WS group messages), not by this page's loaded rows: the
  // badge must count unread groups the window hasn't fetched.
  const [waGroupsUnreadCount, setWaGroupsUnreadCount] = useState(() =>
    isScopedList ? 0 : getWaGroupsUnreadThreadsCount(),
  );
  useEffect(() => {
    if (isScopedList) return undefined;
    setWaGroupsUnreadCount(getWaGroupsUnreadThreadsCount());
    return subscribeWaGroupsUnread(setWaGroupsUnreadCount);
  }, [isInternalScope]);
  // Teach the store which thread ids are groups as the lens's window loads rows, so its WS
  // debounce classifies known threads fast even before the first summary response.
  useEffect(() => {
    if (!waGroupsLensActive) return;
    noteWaGroupThreadIds(
      threadListWindow.loadedRowsSnapshot().map((t) => getThreadRowId(t)).filter(Boolean),
    );
    // threadListWindow.snapshot changes identity whenever fetched rows land.
  }, [waGroupsLensActive, threadListWindow.snapshot]);

  // Server-complete grouped rows for the Groups view's leading sections (groups=only). null when
  // the view is off; refreshed with the same cadence as the list (WS-debounced refresh).
  const [groupedThreadRows, setGroupedThreadRows] = useState(null);
  const [groupedThreadsTruncated, setGroupedThreadsTruncated] = useState(false);
  const groupedFetchSeqRef = useRef(0);
  // The UNFILTERED inbox total from the overview — the "of N" side of the counter while a pill
  // narrows the window's own total.
  const [threadUnfilteredTotal, setThreadUnfilteredTotal] = useState(() =>
    !isScopedList && Number.isFinite(initialOverview?.page?.total)
      ? initialOverview.page.total
      : null,
  );
  const [collapsedSections, setCollapsedSections] = useState(
    () => new Set(getUserPreferences().messagesThreadListCollapsedSections),
  );
  const [participantsCollapsedSections, setParticipantsCollapsedSections] = useState(() => {
    const stored = getUserPreferences().messagesParticipantsCollapsedSections;
    if (Array.isArray(stored) && stored.length > 0) {
      return new Set(stored);
    }
    // First-run default: keep bots tucked away since they rarely need attention.
    return new Set(["bots"]);
  });
  const [threadGroups, setThreadGroups] = useState(() => getThreadGroupsConfig());
  const [threadGroupsError, setThreadGroupsError] = useState("");
  const threadGroupsErrorClearRef = useRef(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(readMessagesRightPanelOpen);
  const membersFetchSeqRef = useRef(0);
  const [threadMembersGrouped, setThreadMembersGrouped] = useState({ direct: [], team: [], bots: [] });
  /** idle | loading | api | history | error */
  const [memberListSource, setMemberListSource] = useState("idle");
  const [threadMembersError, setThreadMembersError] = useState("");
  const [threadMembersNotice, setThreadMembersNotice] = useState("");
  const [ambiguousMigrationModal, setAmbiguousMigrationModal] = useState({
    open: false,
    threadMemberUserId: null,
    threadMemberSnapshot: null,
    matchUserIds: [],
    candidates: [],
    loading: false,
    selectedUserId: null,
    submitting: false,
  });
  const [mergeResultModal, setMergeResultModal] = useState({
    open: false,
    success: false,
    title: "",
    description: "",
    sourceName: "",
    targetName: "",
  });
  const [threadMemberDetailsOpen, setThreadMemberDetailsOpen] = useState(false);
  const [threadMemberDetailsUser, setThreadMemberDetailsUser] = useState(null);
  const [threadMemberDetailsAvatarUrl, setThreadMemberDetailsAvatarUrl] = useState("");
  const [threadMemberDetailsAllowPhoneDuplicates, setThreadMemberDetailsAllowPhoneDuplicates] =
    useState(false);
  const [refreshingPhoneDuplicates, setRefreshingPhoneDuplicates] = useState(false);
  const [savingCorporateInsurance, setSavingCorporateInsurance] = useState(false);

  // Archived messages state
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState("");
  const [archivedSkip, setArchivedSkip] = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedAllLoaded, setArchivedAllLoaded] = useState(false);
  const [archiveTriggered, setArchiveTriggered] = useState(false);
  const archivedSkipRef = useRef(0);
  const archivedLoadingRef = useRef(false);

  const messageClusters = useMemo(() => clusterHistoryMessages(historyItems), [historyItems]);

  // Merged WhatsApp view (on by default, per-device preference). Never in the internal Chats
  // scope — those threads have no patient/number dimension to merge across.
  const mergedWhatsAppView = useMergedWhatsAppView() && !isScopedList;

  const isCurrentUserId = useCallback((userId) => {
    const id = String(userId || "").trim();
    if (!id) return false;
    const current = String(getAuthUser()?.id || "").trim();
    if (current && id === current) return true;
    return getUserIdsFromJwt(getAuthToken()).includes(id);
  }, []);

  const closeAmbiguousMigrationModal = useCallback(() => {
    setAmbiguousMigrationModal((prev) => ({ ...prev, open: false }));
  }, []);

  const closeMergeResultModal = useCallback(() => {
    setMergeResultModal((prev) => ({ ...prev, open: false }));
  }, []);

  const openAmbiguousMigrationModal = useCallback(({ threadMemberUserId, threadMemberSnapshot, matchUserIds }) => {
    setAmbiguousMigrationModal({
      open: true,
      threadMemberUserId,
      threadMemberSnapshot,
      matchUserIds,
      candidates: [],
      loading: true,
      selectedUserId: matchUserIds[0] ?? null,
      submitting: false,
    });
    void Promise.all(
      matchUserIds.map(async (jid) => {
        const id = String(jid || "").trim();
        try {
          const user = await fetchAdminUser({ userId: id });
          return { userId: id, user, error: null };
        } catch (e) {
          return {
            userId: id,
            user: null,
            error: e instanceof Error ? e.message : "Failed to load user",
          };
        }
      }),
    ).then((candidates) => {
      setAmbiguousMigrationModal((prev) => ({ ...prev, candidates, loading: false }));
    });
  }, []);

  const loadAdminThreadMembers = useCallback(
    async (threadId, { suppressPermissionDeniedEvent = false } = {}) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      const seq = ++membersFetchSeqRef.current;
      setMemberListSource("loading");
      setThreadMembersGrouped({ direct: [], team: [], bots: [] });
      setThreadMembersError("");
      setThreadMembersNotice("");

      // WhatsApp GROUP threads have no admin membership worth fetching: memberIds is just the
      // bridge's self user (roster participants are deliberately NOT thread members — see the
      // cross-repo contract), and the real roster travels in `metadata.whatsappGroup`, which the
      // participants panel renders directly. Settle on the history-derived source instead, so a
      // group whose roster has not synced yet still lists its message senders.
      // (findKnownThreadRow is declared later in this hook but is a stable empty-deps callback,
      // initialized within the same first render — safe to reach at call time.)
      if (isWaGroupThread(findKnownThreadRow(tid))) {
        setMemberListSource("history");
        return;
      }

      try {
        const { users: usersFromApi, userIds: legacyUserIds, botIds } = await fetchAdminThreadMembers({
          threadId: tid,
          suppressPermissionDeniedEvent,
        });
        if (seq !== membersFetchSeqRef.current) return;

        const memberSnapshot = parseThreadMemberIdsFromMembersPayload(usersFromApi, legacyUserIds, botIds);
        setThreadMemberIdsByThreadId((prev) => ({ ...prev, [tid]: memberSnapshot }));

        const usersInput =
          Array.isArray(usersFromApi) && usersFromApi.length > 0
            ? usersFromApi
            : legacyUserIds.map((id) => ({ userId: id, source: "direct" }));

        const seenUserIds = new Set();
        const uniqueMembers = [];
        for (const u of usersInput) {
          const userId = String(u?.userId || "").trim();
          if (!userId || seenUserIds.has(userId)) continue;
          seenUserIds.add(userId);
          uniqueMembers.push({ userId, source: normalizeMemberSource(u?.source) });
        }

        const primaryMembership = uniqueMembers.filter((m) => m.source === "direct" || m.source === "both");
        const teamMembership = uniqueMembers.filter((m) => m.source === "team");

        // TWO requests for the whole member list, not two PER member. The per-member form issued a
        // chat-profile and an admin-user GET each — on a ~30-member thread that is 60 requests plus
        // 60 CORS preflights (distinct urls, so the preflight cache never helped), which then queued
        // against the browser's connection limit and laddered out to seconds.
        const memberUserIds = uniqueMembers.map((m) => m.userId);
        const [profilesById, adminUsersById] = await Promise.all([
          fetchUserChatProfiles({ userIds: memberUserIds }).catch(() => ({})),
          fetchAdminUsers({ userIds: memberUserIds }).catch(() => ({})),
        ]);
        if (seq !== membersFetchSeqRef.current) return;

        const buildTeamUserRow = ({ userId, source }) => {
          let label = userId.replace(/_/g, " ");
          const adminUser = adminUsersById[userId] || null;
          let avatarUrl = "";
          const p = profilesById[userId] || null;
          if (p) {
            label = (p.displayName || userId).replace(/_/g, " ");
            avatarUrl = String(p.avatar || "").trim();
          }
          const self = isCurrentUserId(userId);
          return {
            key: `user:${userId}`,
            kind: "user",
            id: userId,
            label,
            subtitle: membershipSubtitle(source, self),
            phoneLine: primaryPhoneLineOrEmpty(adminUser),
            avatarUrl,
            isBot: false,
            isSelf: self,
            memberSource: source,
            adminUserSnapshot: adminUser,
          };
        };

        /** Direct (+ both): chat profile + admin user (migration / duplicate flags), from the batches above. */
        const buildDirectSectionUserRow = ({ userId, source }) => {
          let label = userId.replace(/_/g, " ");
          let avatarUrl = "";
          let isBridgeStub = false;
          const admin = adminUsersById[userId] || null;
          const adminUser = admin;
          {
            const profile = profilesById[userId] || null;
            const fromAdmin = String(formatAdminUserDisplayName(admin) || "").trim();
            const fromChat = profile?.displayName != null ? String(profile.displayName).trim() : "";
            // An unmatched/unsynced WhatsApp number is relayed under a nameless bridge external-stub
            // account, so its resolved "name" is just the relay login id ("ext-bot-<botId>-<phone>"),
            // never a person — don't surface that as the patient name. Prefer a real profile name;
            // otherwise fall back to the patient's phone (the same identity the thread header shows)
            // and flag the row so the panel can upgrade it to the WhatsApp name when one is known.
            const realName =
              fromAdmin && !isWhatsAppBridgeStubName(fromAdmin)
                ? fromAdmin
                : fromChat && !isWhatsAppBridgeStubName(fromChat)
                  ? fromChat
                  : "";
            if (realName) {
              label = realName.replace(/_/g, " ");
            } else {
              isBridgeStub =
                (Boolean(fromAdmin) && isWhatsAppBridgeStubName(fromAdmin)) ||
                (Boolean(fromChat) && isWhatsAppBridgeStubName(fromChat));
              const phone = primaryPhoneLineOrEmpty(admin);
              if (phone) label = phone;
            }
            avatarUrl = String(profile?.avatar || "").trim();
          }
          const self = isCurrentUserId(userId);
          const ambiguousDuplicate = adminUser ? adminUserShowsAmbiguousPanel(adminUser) : false;
          return {
            key: `user:${userId}`,
            kind: "user",
            id: userId,
            label,
            subtitle: membershipSubtitle(source, self),
            phoneLine: primaryPhoneLineOrEmpty(adminUser),
            avatarUrl,
            isBot: false,
            isSelf: self,
            memberSource: source,
            ambiguousDuplicate,
            isBridgeStub,
            adminUserSnapshot: adminUser,
          };
        };

        // Synchronous now — both builders read from the batches fetched above.
        const directRows = primaryMembership.map(buildDirectSectionUserRow);
        const teamRows = teamMembership.map(buildTeamUserRow);

        directRows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        teamRows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

        const botRows = botIds
          .map((bid) => ({
            key: `bot:${bid}`,
            kind: "bot",
            id: bid,
            label: formatBotDisplayLabel(bid),
            subtitle: "Bot",
            isBot: true,
            isSelf: false,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

        if (seq !== membersFetchSeqRef.current) return;
        setThreadMembersGrouped({ direct: directRows, team: teamRows, bots: botRows });
        setMemberListSource("api");
      } catch (e) {
        if (seq !== membersFetchSeqRef.current) return;
        if (e instanceof ApiError && e.status === 403) {
          setMemberListSource("history");
          setThreadMembersNotice("Showing people from messages; full membership list requires employee access.");
        } else {
          setMemberListSource("error");
          setThreadMembersError(e instanceof Error ? e.message : "Could not load thread members");
        }
      }
    },
    [isCurrentUserId],
  );

  const historyDerivedMemberRows = useMemo(
    () => buildThreadMemberRowsFromHistory(historyItems, userDisplayNamesById, isCurrentUserId),
    [historyItems, userDisplayNamesById, isCurrentUserId],
  );

  // The patient's WhatsApp display name from the open thread's history (e.g. "Jeraldine"). For an
  // unmatched/unsynced WhatsApp number the member API only resolves to a bridge external-stub login
  // id, so its inbound messages carry the only real name we have. Most recent inbound wins.
  const whatsAppPatientNameFromHistory = useMemo(() => {
    for (let i = historyItems.length - 1; i >= 0; i -= 1) {
      const name = getWhatsAppPatientPushName(historyItems[i]);
      if (name) return name;
    }
    return "";
  }, [historyItems]);

  // Upgrade bridge-stub patient rows (which the builder labelled with the phone) to that WhatsApp
  // name when we have one; the row still carries the phone on its own line. Everything else passes
  // through untouched, and identity is preserved when there's nothing to upgrade.
  const threadMembersGroupedResolved = useMemo(() => {
    if (!whatsAppPatientNameFromHistory) return threadMembersGrouped;
    const direct = (threadMembersGrouped.direct || []).map((row) =>
      row.isBridgeStub ? { ...row, label: whatsAppPatientNameFromHistory } : row,
    );
    return { ...threadMembersGrouped, direct };
  }, [threadMembersGrouped, whatsAppPatientNameFromHistory]);

  const devPatientImpersonationUserId = useMemo(() => {
    if (!import.meta.env.DEV) {
      return "";
    }
    const current = String(getAuthUser()?.id || "").trim();
    return resolveDevPatientImpersonationUserId(threadMembersGrouped.direct, current);
  }, [threadMembersGrouped.direct]);

  const devPatientImpersonationLabel = useMemo(() => {
    if (!devPatientImpersonationUserId) {
      return "";
    }
    const row = threadMembersGrouped.direct.find(
      (r) => String(r?.id || "").trim() === devPatientImpersonationUserId,
    );
    const name = row ? String(row.label || "").trim() : "";
    return name || devPatientImpersonationUserId;
  }, [threadMembersGrouped.direct, devPatientImpersonationUserId]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    if (!devPatientImpersonationUserId && devSendAsPatient) {
      setDevSendAsPatient(false);
    }
  }, [devPatientImpersonationUserId, devSendAsPatient]);

  const membersPanelLoading =
    memberListSource === "loading" || (memberListSource === "history" && historyLoading);

  useEffect(() => {
    try {
      window.localStorage.setItem(MESSAGES_RIGHT_PANEL_OPEN_KEY, rightPanelOpen ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [rightPanelOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem(MESSAGES_RIGHT_PANEL_MODE_KEY, rightPanelMode);
    } catch {
      /* ignore */
    }
  }, [rightPanelMode]);

  useEffect(() => {
    setUserPreferences({
      messagesThreadFilterLockedOnly: threadFilterLockedOnly,
      // DISABLED (for now): staff_handles + assignee filters are hidden.
      // messagesThreadFilterStaffHandlesOnly: threadFilterStaffHandlesOnly,
      messagesThreadFilterAwaitingReplyOnly: threadFilterAwaitingReplyOnly,
      messagesThreadFilterWhatsappOnly: threadFilterWhatsappOnly,
      // messagesThreadFilterAssignedToMe: threadFilterAssignedToMe,
      // messagesThreadFilterAssigneeId: threadFilterAssigneeId,
      messagesThreadListView: threadListView,
      messagesThreadLens: threadLens,
      messagesViewMode: viewMode,
      messagesThreadListCollapsedSections: Array.from(collapsedSections),
      messagesParticipantsCollapsedSections: Array.from(participantsCollapsedSections),
    });
  }, [
    threadFilterLockedOnly,
    threadFilterAwaitingReplyOnly,
    threadFilterWhatsappOnly,
    threadListView,
    threadLens,
    viewMode,
    collapsedSections,
    participantsCollapsedSections,
  ]);

  const toggleSectionCollapsed = useCallback((sectionKey) => {
    const key = String(sectionKey || "").trim();
    if (!key) return;
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const collapseAllSections = useCallback(() => {
    setCollapsedSections(() => {
      const next = new Set(["waiting", "all"]);
      for (const g of getThreadGroupsConfig()) {
        if (g?.id) next.add(`group:${g.id}`);
      }
      return next;
    });
  }, []);

  const expandAllSections = useCallback(() => {
    setCollapsedSections((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  // Reset every thread filter (toggles, assignee, search, and last-message range) back to
  // "show everything".
  const clearThreadFilters = useCallback(() => {
    setThreadFilterLockedOnly(false);
    // DISABLED (for now): staff_handles + assignee filters are hidden.
    // setThreadFilterStaffHandlesOnly(false);
    setThreadFilterAwaitingReplyOnly(false);
    setThreadFilterWhatsappOnly(false);
    setThreadFilterAppOnly(false);
    // setThreadFilterAssignedToMe(false);
    // setThreadFilterAssigneeId("");
    setThreadFilterWhatsappBotId("");
    setThreadSearchQuery("");
    setThreadFilterLastMessageRange(null);
  }, []);

  // The channel pills (WhatsApp / Healo app) are mutually exclusive — a thread belongs to
  // exactly one channel, so enabling one clears the other instead of silently ANDing to an
  // empty list. Exported in place of the raw setters.
  const toggleThreadFilterWhatsappOnly = useCallback((next) => {
    setThreadFilterWhatsappOnly(next);
    if (next) setThreadFilterAppOnly(false);
  }, []);
  const toggleThreadFilterAppOnly = useCallback((next) => {
    setThreadFilterAppOnly(next);
    if (next) setThreadFilterWhatsappOnly(false);
  }, []);

  const toggleParticipantsSection = useCallback((sectionKey) => {
    const key = String(sectionKey || "").trim();
    if (!key) return;
    setParticipantsCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    return subscribeThreadGroups((next) => setThreadGroups(next));
  }, []);

  // Load the configured WhatsApp numbers once (static tenant config). Best-effort: on failure the
  // "Number" filter and new-chat picker simply stay hidden (single-number behavior).
  useEffect(() => {
    let cancelled = false;
    fetchMessagesSettings()
      .then((s) => {
        if (!cancelled) {
          setWhatsappNumbers(Array.isArray(s?.whatsappNumbers) ? s.whatsappNumbers : []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (threadGroupsErrorClearRef.current) {
        clearTimeout(threadGroupsErrorClearRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedThreadId) {
      membersFetchSeqRef.current += 1;
      setMemberListSource("idle");
      setThreadMembersGrouped({ direct: [], team: [], bots: [] });
      setThreadMembersError("");
      setThreadMembersNotice("");
    }
  }, [selectedThreadId]);

  // Safety net for the participants panel: an OPEN thread must always resolve its member list.
  // loadAdminThreadMembers flips the source to "loading" synchronously, so a selected thread still
  // sitting at "idle" means the members fetch was never kicked off for it — either the thread was
  // opened by a path that skipped it, or a transient selection reset (the effect above) raced the
  // in-flight fetch and reverted the source without clearing the loaded history. When that happens
  // the panel would otherwise stay stuck on the message-derived "From messages" fallback (bot + a
  // sender) instead of the patient until a full app reload. Re-issuing the load here recovers it on
  // the next commit. It fires ONLY in the idle+open combination, so the normal click/restore path
  // (which already sets "loading" in the same batch) never double-fetches.
  useEffect(() => {
    const tid = String(selectedThreadId || "").trim();
    if (tid && memberListSource === "idle") {
      void loadAdminThreadMembers(tid);
    }
  }, [selectedThreadId, memberListSource, loadAdminThreadMembers]);

  useEffect(() => {
    return () => {
      if (threadFlagsErrorClearRef.current) {
        clearTimeout(threadFlagsErrorClearRef.current);
      }
    };
  }, []);

  const scheduleClearThreadFlagsError = useCallback(() => {
    if (threadFlagsErrorClearRef.current) {
      clearTimeout(threadFlagsErrorClearRef.current);
    }
    threadFlagsErrorClearRef.current = setTimeout(() => {
      setThreadFlagsError("");
      threadFlagsErrorClearRef.current = null;
    }, 8000);
  }, []);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  useEffect(() => {
    userDisplayNamesByIdRef.current = userDisplayNamesById;
  }, [userDisplayNamesById]);

  useEffect(() => {
    historyItemsRef.current = historyItems;
  }, [historyItems]);

  useEffect(() => {
    historyAllOlderLoadedRef.current = historyAllOlderLoaded;
  }, [historyAllOlderLoaded]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  // Row lookups for callbacks must see every list a row can be rendered from — the windowed
  // page, the server-fed attention sections and the search results — otherwise acting on a
  // section-only row (e.g. group change building on "current metadata") reads nothing.
  const attentionWaitingRef = useRef([]);
  const attentionPatientReplyRef = useRef([]);
  const attentionBotAckRef = useRef([]);
  const searchResultsRef = useRef([]);
  useEffect(() => {
    attentionWaitingRef.current = attentionWaiting;
  }, [attentionWaiting]);
  useEffect(() => {
    attentionPatientReplyRef.current = attentionPatientReply;
  }, [attentionPatientReply]);
  useEffect(() => {
    attentionBotAckRef.current = attentionBotAck;
  }, [attentionBotAck]);
  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);
  const findKnownThreadRow = useCallback(
    (tid) =>
      threadListWindowRef.current.getRowByThreadId(tid) ||
      threadsRef.current.find((t) => getThreadRowId(t) === tid) ||
      attentionWaitingRef.current.find((t) => getThreadRowId(t) === tid) ||
      attentionPatientReplyRef.current.find((t) => getThreadRowId(t) === tid) ||
      attentionBotAckRef.current.find((t) => getThreadRowId(t) === tid) ||
      searchResultsRef.current.find((t) => getThreadRowId(t) === tid),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];

    async function run() {
      const media = [];
      const stickers = [];

      for (let i = 0; i < selectedAttachments.length; i += 1) {
        const file = selectedAttachments[i];
        const key = `${file.name}-${file.size}-${file.lastModified}-${i}`;
        const sm = localStickerPresentationMode(file);
        if (sm) {
          try {
            if (sm === "lottie-json") {
              const text = await file.text();
              const data = extractLottieAnimationData(JSON.parse(text));
              if (!data) {
                throw new Error("not lottie json");
              }
              stickers.push({ key, presentationMode: sm, fileName: file.name, animationData: data, index: i });
            } else if (sm === "dotlottie") {
              const url = URL.createObjectURL(file);
              createdUrls.push(url);
              stickers.push({ key, presentationMode: sm, fileName: file.name, url, index: i });
            } else if (sm === "webp" || sm === "video") {
              const url = URL.createObjectURL(file);
              createdUrls.push(url);
              stickers.push({ key, presentationMode: sm, fileName: file.name, url, index: i });
            } else {
              const buf = await file.arrayBuffer();
              if (cancelled) {
                return;
              }
              let handled = false;
              const conv = await convertWhatsappWasStickerPayload(buf);
              if (conv) {
                if (conv.mimeType === "application/json") {
                  const text = new TextDecoder("utf-8", { fatal: false }).decode(conv.buffer);
                  const data = extractLottieAnimationData(JSON.parse(text));
                  if (data) {
                    stickers.push({
                      key,
                      presentationMode: "lottie-json",
                      fileName: file.name,
                      animationData: data,
                      index: i,
                    });
                    handled = true;
                  }
                } else if (conv.mimeType === "application/zip") {
                  const url = URL.createObjectURL(new Blob([conv.buffer], { type: "application/zip" }));
                  createdUrls.push(url);
                  stickers.push({ key, presentationMode: "dotlottie", fileName: file.name, url, index: i });
                  handled = true;
                } else if (conv.mimeType.startsWith("video/")) {
                  const url = URL.createObjectURL(new Blob([conv.buffer], { type: conv.mimeType }));
                  createdUrls.push(url);
                  stickers.push({ key, presentationMode: "video", fileName: file.name, url, index: i });
                  handled = true;
                } else if (conv.mimeType.startsWith("image/")) {
                  const url = URL.createObjectURL(new Blob([conv.buffer], { type: conv.mimeType }));
                  createdUrls.push(url);
                  stickers.push({ key, presentationMode: "webp", fileName: file.name, url, index: i });
                  handled = true;
                }
              }
              if (!handled) {
                const { blob, mode } = await normalizeStickerBlobForDisplay(new Blob([buf], { type: file.type }));
                const url = URL.createObjectURL(blob);
                createdUrls.push(url);
                stickers.push({
                  key,
                  presentationMode: mode === "video" ? "video" : "webp",
                  fileName: file.name,
                  url,
                  index: i,
                });
              }
            }
          } catch (_error) {
            stickers.push({ key, presentationMode: sm, fileName: file.name, error: true, index: i });
          }
          continue;
        }

        const variant = localFileMediaPreviewVariant(file);
        if (variant) {
          const url = URL.createObjectURL(file);
          createdUrls.push(url);
          media.push({ key, variant, name: file.name, url, index: i });
        }
      }

      if (!cancelled) {
        setComposerStickerPreviews(stickers);
        setComposerAttachmentPreviews(media);
      }
    }

    void run();
    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [selectedAttachments]);

  const composerOtherAttachments = useMemo(
    () =>
      selectedAttachments
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => !localFileMediaPreviewVariant(file) && !localStickerPresentationMode(file)),
    [selectedAttachments],
  );

  const refreshThreadData = useCallback(async () => {
    if (!getAuthToken()) {
      setThreads([]);
      threadListWindowRef.current.reset();
      setGroupedThreadRows(null);
      setAttentionWaiting([]);
      setAttentionBotAck([]);
      setDefaultThreads([]);
      return [];
    }
    // The server said "contact chat is not enabled" once this session — the Private pane stays
    // empty (the list renders its feature-off notice) and stops re-asking on every WS-driven
    // refresh. Frozen workspaces keep a once-visited pane alive forever, so without this latch
    // the pane re-403'd (console noise + the global permission toast) for the whole session.
    if (scopeRef.current === "direct" && isDirectChatKnownDisabled()) {
      setThreads([]);
      return [];
    }
    try {
      // Both simple scopes load as one complete fetch; only the patient scope is windowed.
      const scopedList =
        scopeRef.current === "internal" || scopeRef.current === "direct";
      const internal = scopedList;
      // Identity of the user this request was issued FOR. A profile switch mid-flight unmounts
      // this page instance (its setStates become no-ops) but the module-level store writes
      // below (adoptThreadsOverviewSnapshot, thread-groups hydrate) would still land the OLD
      // user's data in the NEW user's session — drop the response instead.
      const requestUserId = String(getAuthUser()?.id || "").trim();
      // Normal scope uses the overview endpoint: the first list rows PLUS the complete attention
      // sections, the exact total and the tenant-wide shared settings in one request
      // (windowDays: 0 so the rows equal the virtual list's offsets [0, limit)). Internal Chats
      // keep the plain single-fetch list.
      // Routed through the shared store rather than fetching directly: the sidebar badge starts
      // the identical overview on app boot, so on a cold load both used to pay for the same
      // multi-hundred-kilobyte response. `rethrow` keeps this function's own error handling —
      // the store swallows failures for its background interval.
      const response = internal
        ? await fetchThreads({
            scope: scopeRef.current,
            limit: INTERNAL_THREADS_LIMIT,
            // Direct scope: a 403 means the server-wide contact-chat switch is off (there is no
            // module gate on these endpoints) — a feature probe's denial must not toast.
            suppressPermissionDeniedEvent: scopeRef.current === "direct",
          })
        : await refreshThreadsOverview({ rethrow: true });
      if (scopeRef.current === "direct") {
        markDirectChatEnabled();
      }
      if (String(getAuthUser()?.id || "").trim() !== requestUserId) {
        return [];
      }
      const apiThreads = Array.isArray(response?.threads) ? response.threads : [];
      if (internal) {
        // The internal list is complete — replace wholesale, reusing prior rows for sender
        // continuity.
        setThreads((prev) => {
          const prevById = new Map(prev.map((t) => [getThreadRowId(t), t]));
          return apiThreads.map((t) => normalizeApiThreadRow(t, prevById.get(getThreadRowId(t))));
        });
        // Teach the app-wide chats store which thread ids are internal, so notification
        // clicks route to Chats even before the store's own summary fetch resolves.
        // Feed the loaded ids into the routing store so a notification click resolves the owning
        // pane synchronously instead of paying a thread fetch. Each scope has its own set: a
        // private chat routed as an internal one would open in the wrong pane.
        const loadedIds = apiThreads.map((t) => getThreadRowId(t)).filter(Boolean);
        if (scopeRef.current === "direct") {
          noteDirectThreadIds(loadedIds);
        } else {
          noteInternalThreadIds(loadedIds);
        }
      }
      if (!internal) {
        // Reconcile the virtual window: order/membership may have shifted. When the window shows
        // the plain chronological list, the overview rows are its fresh offsets [0, limit) and
        // seed the new generation directly; under filter pills (or the groups-view tail) the
        // window refetches with its own params instead.
        //
        // The overview strips the default assistant thread out of `threads` (it rides in
        // `pinned`), while the offset endpoint keeps it at its natural position — so the seed
        // must merge it back IN ORDER or the seeded offsets would be shifted by one against
        // every later range fetch. A pinned row that would sort at (or past) the seed's tail
        // is dropped instead: its true offset lies beyond the seeded range.
        if (windowAdoptsOverviewRef.current) {
          const pinnedRows = Array.isArray(response.pinned) ? response.pinned : [];
          let seedRowsInOrder = apiThreads;
          if (pinnedRows.length) {
            const ids = new Set(apiThreads.map((t) => getThreadRowId(t)));
            const extras = pinnedRows.filter((t) => getThreadRowId(t) && !ids.has(getThreadRowId(t)));
            if (extras.length) {
              const extraIds = new Set(extras.map((t) => getThreadRowId(t)));
              const merged = [...apiThreads, ...extras].sort((a, b) => {
                const d = threadLastMessageMs(b) - threadLastMessageMs(a);
                if (d) return d;
                // Mirror the server tie-break {_id: -1} (hex strings compare like ObjectIDs).
                return String(getThreadRowId(b)).localeCompare(String(getThreadRowId(a)));
              });
              while (merged.length && extraIds.has(getThreadRowId(merged[merged.length - 1]))) {
                merged.pop();
              }
              seedRowsInOrder = merged;
            }
          }
          threadListWindowRef.current.refresh({
            rows: seedRowsInOrder,
            total: Number.isFinite(response.page?.total) ? response.page.total : undefined,
          });
        } else {
          threadListWindowRef.current.refresh();
        }
        if (Number.isFinite(response.page?.total)) {
          setThreadUnfilteredTotal(response.page.total);
        }
        // Feed the just-fetched overview to the nav-badge store so the sidebar "Needs you" count
        // tracks this page's data. Acknowledge in particular fires no WS event, so without this the
        // badge would linger on a stale (higher) count until the store's own 60s refresh.
        adoptThreadsOverviewSnapshot(response);
        // Attention sections are replaced wholesale (the server list is authoritative and
        // complete); reuse the previous rows for sender continuity like the page merge does.
        const normalizeAttentionRows = (rows, prevRows) => {
          const prevById = new Map(prevRows.map((t) => [getThreadRowId(t), t]));
          return (Array.isArray(rows) ? rows : []).map((t) =>
            normalizeApiThreadRow(t, prevById.get(getThreadRowId(t))),
          );
        };
        setAttentionWaiting((prev) => normalizeAttentionRows(response.attention?.waiting, prev));
        setAttentionPatientReply((prev) =>
          normalizeAttentionRows(response.attention?.patientReply, prev),
        );
        setAttentionBotAck((prev) => normalizeAttentionRows(response.attention?.botAck, prev));
        // The server hit its attention candidate cap, so the sections above may be short. Kept in
        // state so the list can say so — a silently truncated triage list reads as "all clear".
        setAttentionTruncated(Boolean(response.attention?.truncated));
        // Default thread: replaced wholesale like the attention lists, reusing prior rows for
        // sender continuity.
        setDefaultThreads((prev) => normalizeAttentionRows(response.pinned, prev));
        // Shared settings ride along with every refresh: hydrate the groups store (no-op when
        // unchanged) and adopt both attention windows so admin edits propagate to everyone.
        if (response.settings?.threadGroups) {
          hydrateThreadGroupsFromServer(response.settings.threadGroups);
        }
        const serverBotAck = response.settings?.botAck;
        if (Number.isFinite(serverBotAck?.thresholdMinutes)) {
          setBotAckThresholdMinutes(serverBotAck.thresholdMinutes);
        }
        if (Number.isFinite(serverBotAck?.maxHours)) {
          setBotAckMaxHours(serverBotAck.maxHours);
        }
        // Absent on an older backend — the defaults already loaded stay, rather than silently
        // falling back to bot-ack's much shorter ceiling.
        const serverPatientReply = response.settings?.patientReply;
        if (Number.isFinite(serverPatientReply?.thresholdMinutes)) {
          setPatientReplyThresholdMinutes(serverPatientReply.thresholdMinutes);
        }
        if (Number.isFinite(serverPatientReply?.maxHours)) {
          setPatientReplyMaxHours(serverPatientReply.maxHours);
        }
      }
      return apiThreads;
    } catch (error) {
      // Direct scope: a 403 latches "contact chat is off" for the session (see the short-circuit
      // above); other scopes/failures fall through to the keep-current-lists behavior.
      if (scopeRef.current === "direct") {
        markDirectChatDisabledFromError(error);
      }
      // Keep the current lists (the seeded cache or the last good fetch) on a failed refresh
      // rather than blanking — a transient network error should never wipe what's on screen. The
      // next WS/interval refresh retries. (Logout clears the lists via the no-auth guard above.)
      return [];
    }
  }, []);

  // Long-standing internal name for "refresh the thread lists" — kept as an alias so the many
  // reconcile-after-mutation call sites stay untouched.
  const loadThreads = refreshThreadData;
  const loadThreadsRef = useRef(refreshThreadData);
  loadThreadsRef.current = refreshThreadData;

  // Groups view: the group sections are SERVER-COMPLETE (groups=only, capped) so they never
  // silently miss an old grouped thread the sparse list hasn't loaded. Fetched when the view
  // turns on, re-fetched on every scheduled list refresh (`groupedRefreshTick`) and whenever the
  // filter pills change — the pills narrow the sections with the same server params as the tail.
  const [groupedRefreshTick, setGroupedRefreshTick] = useState(0);
  const threadListWindowFiltersRef = useRef(threadListWindowFilters);
  threadListWindowFiltersRef.current = threadListWindowFilters;
  const threadPillsKey = JSON.stringify({ ...threadListWindowFilters, groups: "" });
  useEffect(() => {
    // The Groups lens has no folder sections (folders belong to the default scope) — fetching
    // them there would burn a request for rows the page suppresses anyway.
    if (isScopedList || threadListView !== "groups" || waGroupsLensActive) {
      setGroupedThreadRows(null);
      setGroupedThreadsTruncated(false);
      return undefined;
    }
    if (!getAuthToken()) return undefined;
    const seq = ++groupedFetchSeqRef.current;
    let cancelled = false;
    fetchThreads({
      ...threadListWindowFiltersRef.current,
      groups: "only",
      limit: THREAD_FETCH_MAX_LIMIT,
      withTotal: true,
    })
      .then((response) => {
        if (cancelled || groupedFetchSeqRef.current !== seq) return;
        const rows = (Array.isArray(response?.threads) ? response.threads : []).map((t) =>
          normalizeApiThreadRow(t, null),
        );
        setGroupedThreadRows(rows);
        // Offset-mode responses carry no `truncated` — derive it from the exact total.
        const total = response?.page?.total;
        setGroupedThreadsTruncated(
          Number.isFinite(total) ? total > rows.length : rows.length >= THREAD_FETCH_MAX_LIMIT,
        );
      })
      .catch(() => {
        // Keep whatever grouped rows are shown; the next refresh tick retries.
      });
    return () => {
      cancelled = true;
    };
  }, [isInternalScope, threadListView, waGroupsLensActive, groupedRefreshTick, threadPillsKey]);

  /**
   * Apply an updater to a thread row WHEREVER it appears — the page list, both server-fed
   * attention lists, the default-thread row and the search results — so optimistic updates (ack,
   * flags, group) never leave a stale copy in another list. The updater returns the same reference
   * to skip a row.
   */
  const updateThreadRowEverywhere = useCallback((threadId, updater) => {
    const tid = String(threadId || "").trim();
    if (!tid) return;
    const apply = (prev) => {
      let changed = false;
      const next = prev.map((t) => {
        if (getThreadRowId(t) !== tid) return t;
        const updated = updater(t);
        if (updated !== t) changed = true;
        return updated;
      });
      return changed ? next : prev;
    };
    setThreads(apply);
    // The sparse window holds the normal scope's list rows; grouped rows back the Groups view.
    threadListWindowRef.current.patchRow(tid, updater);
    setGroupedThreadRows((prev) => (Array.isArray(prev) ? apply(prev) : prev));
    setAttentionWaiting(apply);
    setAttentionPatientReply(apply);
    setAttentionBotAck(apply);
    setDefaultThreads(apply);
    setSearchResults(apply);
    // Merged-view sibling rows fetched by patient id live in no list. Patching them here is what
    // keeps `mergedSiblingRevision` bumping for a clinic that exists ONLY as a fetched row —
    // without it, a new inbound on an off-list clinic would never refresh that clinic's page.
    setMergedPatientThreadRows(apply);
    // The off-list fallback row backs the conversation header when the selection is outside
    // every list, so optimistic claim/status/flag patches must reach it too.
    setSelectedThreadOffListRow((prev) =>
      prev && getThreadRowId(prev) === tid ? updater(prev) : prev,
    );
  }, []);

  /**
   * Patch ONE message wherever a copy of it is held: the open history, a merged sibling clinic's
   * page, and the warm history cache — so re-opening the thread never resurrects the old text or
   * an unsent bubble (mergeHistoryNewest keeps cached rows the fresh page no longer returns).
   * `updater(m)` returns the replacement, or null to remove the message. Matches by id only; a
   * list without a match is returned by identity so React bails out.
   */
  const patchMessageEverywhere = useCallback((threadId, messageId, updater) => {
    const tid = String(threadId || "").trim();
    const mid = String(messageId || "").trim();
    if (!tid || !mid) return;
    const apply = (list) => {
      if (!Array.isArray(list)) return list;
      let changed = false;
      const out = [];
      for (const m of list) {
        if (String(m?.id || "").trim() !== mid) {
          out.push(m);
          continue;
        }
        changed = true;
        const next = updater(m);
        if (next != null) out.push(next);
      }
      return changed ? out : list;
    };
    if (String(selectedThreadIdRef.current || "").trim() === tid) setHistoryItems(apply);
    if (siblingHistoriesRef.current.has(tid)) {
      setSiblingHistories((prev) => {
        const cur = prev.get(tid) || [];
        const next = apply(cur);
        if (next === cur) return prev;
        const map = new Map(prev);
        map.set(tid, next);
        return map;
      });
    }
    const cached = historyCacheRef.current.get(tid);
    if (cached) {
      const next = apply(cached.items);
      if (next !== cached.items) historyCacheRef.current.set(tid, { ...cached, items: next });
    }
  }, []);

  /**
   * Rollback twin of patchMessageEverywhere for a REMOVED message (a refused unsend): re-insert it
   * by createdAt wherever that thread's history is held, skipping copies the server has meanwhile
   * re-delivered.
   */
  const restoreMessageEverywhere = useCallback((threadId, message) => {
    const tid = String(threadId || "").trim();
    if (!tid || !message) return;
    if (String(selectedThreadIdRef.current || "").trim() === tid) {
      setHistoryItems((prev) => insertMessageSortedByCreatedAt(prev, message));
    }
    if (siblingHistoriesRef.current.has(tid)) {
      setSiblingHistories((prev) => {
        const cur = prev.get(tid) || [];
        const next = insertMessageSortedByCreatedAt(cur, message);
        if (next === cur) return prev;
        const map = new Map(prev);
        map.set(tid, next);
        return map;
      });
    }
    const cached = historyCacheRef.current.get(tid);
    if (cached) {
      const next = insertMessageSortedByCreatedAt(cached.items, message);
      if (next !== cached.items) historyCacheRef.current.set(tid, { ...cached, items: next });
    }
  }, []);

  /**
   * The copy of a message currently held for `threadId` (open history first, then a merged
   * sibling page, then the warm cache), or null. Reads refs, so it reflects the last COMMITTED
   * state — the right thing to consult after an awaited request has failed.
   */
  const findHeldMessage = useCallback((threadId, messageId) => {
    const tid = String(threadId || "").trim();
    const mid = String(messageId || "").trim();
    if (!tid || !mid) return null;
    const pick = (list) =>
      Array.isArray(list) ? list.find((m) => String(m?.id || "").trim() === mid) : undefined;
    const own = String(selectedThreadIdRef.current || "").trim() === tid ? pick(historyItemsRef.current) : undefined;
    return own || pick(siblingHistoriesRef.current.get(tid)) || pick(historyCacheRef.current.get(tid)?.items) || null;
  }, []);

  // Coalesce bursts of realtime events into at most one refresh per ~300ms (a new message
  // updates the affected row in place immediately; this reconciles new/removed threads, order,
  // the exact total and — in Groups view — the server-complete group sections).
  const scheduleThreadsRefresh = useCallback(() => {
    if (threadsRefreshTimerRef.current) return;
    threadsRefreshTimerRef.current = setTimeout(() => {
      threadsRefreshTimerRef.current = null;
      loadThreadsRef.current();
      setGroupedRefreshTick((n) => n + 1);
    }, 300);
  }, []);
  const scheduleThreadsRefreshRef = useRef(scheduleThreadsRefresh);
  scheduleThreadsRefreshRef.current = scheduleThreadsRefresh;
  useEffect(() => () => {
    if (threadsRefreshTimerRef.current) clearTimeout(threadsRefreshTimerRef.current);
  }, []);

  const handleFlowSessionResetDialogOpenChange = useCallback((open) => {
    setFlowSessionResetDialogOpen(open);
    if (!open) {
      pendingFlowSessionThreadIdRef.current = null;
    }
  }, []);

  const confirmFlowSessionReset = useCallback(async () => {
    const tid = String(pendingFlowSessionThreadIdRef.current || "").trim();
    pendingFlowSessionThreadIdRef.current = null;
    setFlowSessionResetDialogOpen(false);
    if (!tid) return;
    try {
      await postAdminClearThreadMessages({ threadId: tid, resetSessionOnly: true });
      await loadThreads();
      toast.success("Session cleared. Chat history is unchanged.");
    } catch (sessionError) {
      const message =
        sessionError instanceof Error ? sessionError.message : "Could not reset thread session.";
      toast.error(message);
      loadThreadsRef.current();
    }
  }, [loadThreads]);

  const handleThreadFlagCheckedChange = useCallback(
    async (threadId, shortName, nextChecked) => {
      const tid = String(threadId || "").trim();
      const flag = String(shortName || "").trim();
      if (!tid || !flag) {
        return;
      }
      try {
        setThreadFlagsError("");
        const res = nextChecked
          ? await addThreadFlags({ threadId: tid, flags: [flag] })
          : await removeThreadFlags({ threadId: tid, flags: [flag] });
        updateThreadRowEverywhere(tid, (t) => ({
          ...t,
          metadata: { ...(t.metadata || {}), flags: res.flags },
        }));
        // DISABLED (for now): staff_handles is retired — only unlocking prompts the
        // flow-session reset.
        if (!nextChecked && flag === "locked" /* || flag === "staff_handles" */) {
          pendingFlowSessionThreadIdRef.current = tid;
          setFlowSessionResetDialogOpen(true);
        }
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update thread flags";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [loadThreads, scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  );

  /**
   * "Bot answering": clear BOTH AI-handling flags in ONE call. A single flags:remove is one
   * read-merge-write on the server — firing two parallel removals instead can race and
   * resurrect one of the flags. Clearing the pause prompts the flow-session reset, same as
   * clearing either flag individually.
   *
   * staff_handles is retired (DISABLED for now) but stays in the removal list on
   * purpose: it is pure legacy-data cleanup for threads flagged before the retirement.
   */
  const handleAiHandlingClear = useCallback(
    async (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      try {
        setThreadFlagsError("");
        const res = await removeThreadFlags({ threadId: tid, flags: ["locked", "staff_handles"] });
        updateThreadRowEverywhere(tid, (t) => ({
          ...t,
          metadata: { ...(t.metadata || {}), flags: res.flags },
        }));
        pendingFlowSessionThreadIdRef.current = tid;
        setFlowSessionResetDialogOpen(true);
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update thread flags";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  );

  /**
   * Acknowledge a thread's last (bot) message so it drops out of "Unanswered bot replies".
   * Optimistically stamps lastMessageAcknowledgedAt locally, but only while the acked message
   * is still the thread's last one — a newer message must keep the thread visible so it can
   * reappear. A loadThreads() follows to reconcile with the authoritative server state.
   */
  const handleAcknowledgeMessage = useCallback(
    async (threadId, messageId) => {
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      if (!tid || !mid) {
        return;
      }
      try {
        setThreadFlagsError("");
        await acknowledgeMessage({ threadId: tid, messageId: mid });
        const acknowledgedAt = new Date().toISOString();
        updateThreadRowEverywhere(tid, (t) =>
          String(t?.lastMessageId || "") === mid
            ? { ...t, lastMessageAcknowledgedAt: acknowledgedAt }
            : t,
        );
        loadThreadsRef.current();
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not acknowledge message";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  );

  /**
   * Toggle the shared "business" reaction on a message. Clicking the emoji that is already the
   * business reaction clears it; any other emoji replaces it. Updates optimistically (mirroring
   * the WS replace-by-id shape) and lets the backend's re-broadcast reconcile.
   */
  const handleToggleBusinessReaction = useCallback(
    async (threadId, messageId, emoji) => {
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      const nextEmoji = String(emoji || "").trim();
      if (!tid || !mid || !nextEmoji) {
        return;
      }
      const me = String(getAuthUser()?.id || "").trim();

      const current = historyItemsRef.current.find((m) => String(m?.id || "") === mid);
      if (!current) {
        return;
      }
      const prevReactions = current.reactions;
      const currentBiz = businessReaction(current);
      const removing = currentBiz?.emoji === nextEmoji;
      const otherReactions = normalizeReactions(current).filter((r) => r.party !== "business");
      const nextReactions = removing
        ? otherReactions
        : [
            ...otherReactions,
            {
              party: "business",
              emoji: nextEmoji,
              source: "app",
              byUserId: me,
              at: new Date().toISOString(),
            },
          ];

      // Optimistic in-place update of just this message's reactions.
      setHistoryItems((prev) =>
        prev.map((m) => {
          if (String(m?.id || "") !== mid) {
            return m;
          }
          return { ...m, reactions: nextReactions };
        }),
      );
      updateThreadRowEverywhere(tid, (t) =>
        String(t?.lastMessageId || "") === mid ? { ...t, lastMessageReactions: nextReactions } : t,
      );
      setStaffReactedPatientReplyMessageIds((prev) => {
        const next = new Set(prev);
        if (removing) {
          next.delete(mid);
        } else {
          next.add(mid);
        }
        return next;
      });

      try {
        setThreadFlagsError("");
        if (removing) {
          await removeMessageReaction({ threadId: tid, messageId: mid });
        } else {
          await setMessageReaction({ threadId: tid, messageId: mid, emoji: nextEmoji });
        }
        // Backend re-broadcasts the updated message (same id); the WS listener reconciles.
      } catch (error) {
        // Revert the optimistic change for this message.
        setHistoryItems((prev) =>
          prev.map((m) => (String(m?.id || "") === mid ? { ...m, reactions: prevReactions } : m)),
        );
        updateThreadRowEverywhere(tid, (t) =>
          String(t?.lastMessageId || "") === mid ? { ...t, lastMessageReactions: prevReactions } : t,
        );
        setStaffReactedPatientReplyMessageIds((prev) => {
          const next = new Set(prev);
          if (removing) {
            next.add(mid);
          } else {
            next.delete(mid);
          }
          return next;
        });
        const message = error instanceof Error ? error.message : "Could not update reaction";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  );

  /**
   * Retract one of our WhatsApp messages from the patient's phone ("delete for everyone").
   *
   * Optimistically strikes the bubble through and marks it pending — the same shape the server
   * broadcasts a beat later — so the actor sees the effect immediately. WhatsApp's verdict is not
   * part of the response: it lands as a message update that either settles the deletion or flags
   * that the patient can still see it. Only an outright refusal to *try* (message not on WhatsApp
   * yet, connection down) reverts the bubble and surfaces the reason.
   */
  const handleRevokeMessage = useCallback(
    async (threadId, messageId) => {
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      if (!tid || !mid) return;
      const prevHistory = historyItemsRef.current;
      setHistoryItems((prev) =>
        prev.map((m) =>
          String(m?.id || "") === mid
            ? {
                ...m,
                metadata: {
                  ...(m?.metadata || {}),
                  whatsapp: {
                    ...(m?.metadata?.whatsapp || {}),
                    deleted: true,
                    deletePending: true,
                    deleteFailed: false,
                    deleteError: "",
                  },
                },
              }
            : m,
        ),
      );
      try {
        setThreadFlagsError("");
        await revokeWhatsAppMessage({ threadId: tid, messageId: mid });
        // The verdict arrives as a broadcast message update; the WS listener reconciles it.
      } catch (error) {
        setHistoryItems(prevHistory);
        const message = error instanceof Error ? error.message : "Could not delete this message";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError],
  );

  /**
   * DEV tool: soft-delete one message in the open thread. Optimistically removes the bubble;
   * the backend sets a deleted flag on the document (never removes it from the DB) and
   * broadcasts the removal so other clients drop it too. On failure the bubble is restored.
   */
  const handleDeleteMessage = useCallback(
    async (threadId, messageId) => {
      if (!import.meta.env.DEV) return;
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      if (!tid || !mid) return;
      const prevHistory = historyItemsRef.current;
      setHistoryItems((prev) => prev.filter((m) => String(m?.id || "") !== mid));
      try {
        setThreadFlagsError("");
        await deleteThreadMessage({ threadId: tid, messageId: mid });
        // The deleted message may have been the thread row's last/preview message — reload the
        // list so the row reconciles from the server's live enrichment.
        loadThreadsRef.current();
      } catch (error) {
        setHistoryItems(prevHistory);
        const message = error instanceof Error ? error.message : "Could not delete message";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError],
  );

  /**
   * DEV tool: "clear to this point" — soft-delete every message AFTER the given one; the
   * anchor message survives as the thread's new last message. Optimistically truncates the
   * open history (restored on failure); documents stay in the DB flagged deleted, so the bot
   * no longer sees them in its history either.
   */
  const handleClearMessagesAfter = useCallback(
    async (threadId, messageId) => {
      if (!import.meta.env.DEV) return;
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      if (!tid || !mid) return;
      const prevHistory = historyItemsRef.current;
      setHistoryItems((prev) => {
        const idx = prev.findIndex((m) => String(m?.id || "") === mid);
        return idx >= 0 ? prev.slice(0, idx + 1) : prev;
      });
      try {
        setThreadFlagsError("");
        await clearThreadMessagesAfter({ threadId: tid, messageId: mid });
        // The thread row's last/preview message changed — reconcile from the server.
        loadThreadsRef.current();
      } catch (error) {
        setHistoryItems(prevHistory);
        const message = error instanceof Error ? error.message : "Could not clear messages";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError],
  );

  // DISABLED (for now): sending a message no longer auto-applies `staff_handles`
  // nor assigns the thread to the sender — assignment lives on tasks only.
  //
  // /** Auto-apply `staff_handles` when sending a message in a WhatsApp thread (before the send). */
  // const ensureStaffHandlesOnWhatsappSend = useCallback(
  //   async (threadId) => {
  //     const tid = String(threadId || "").trim();
  //     if (!tid) return;
  //     const thread = findKnownThreadRow(tid);
  //     if (String(thread?.metadata?.conversationType || "").toLowerCase() !== "whatsapp") return;
  //     if (getThreadFlagStrings(thread).includes("staff_handles")) return;
  //     try {
  //       setThreadFlagsError("");
  //       const res = await addThreadFlags({ threadId: tid, flags: ["staff_handles"] });
  //       updateThreadRowEverywhere(tid, (t) => ({
  //         ...t,
  //         metadata: { ...(t.metadata || {}), flags: res.flags },
  //       }));
  //     } catch (error) {
  //       loadThreadsRef.current();
  //       const message = error instanceof Error ? error.message : "Could not set staff handles flag";
  //       setThreadFlagsError(message);
  //       scheduleClearThreadFlagsError();
  //     }
  //   },
  //   [findKnownThreadRow, scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  // );
  //
  // /**
  //  * Manual take-over on send: when a staff member sends a message, assign the
  //  * thread to them so it is clear who controls it. Skips when the thread is
  //  * already manually assigned to the current user. Best-effort: a failure
  //  * reloads the thread list rather than blocking the send.
  //  */
  // const assignSelfOnSend = useCallback(async (threadId) => {
  //   // Internal Chats are staff-to-staff, not patient threads; never auto-assign them on send.
  //   if (scopeRef.current === "internal") return;
  //   const tid = String(threadId || "").trim();
  //   if (!tid) return;
  //   const me = String(getAuthUser()?.id || "").trim();
  //   if (!me) return;
  //   const thread = findKnownThreadRow(tid);
  //   const current = getThreadAssignee(thread);
  //   if (current && current.type === "manual" && current.id === me) return;
  //   try {
  //     const res = await assignThread({ threadId: tid });
  //     const next = res?.thread || null;
  //     updateThreadRowEverywhere(tid, (t) => ({
  //       ...t,
  //       assignedToUserId: next?.assignedToUserId || me,
  //       assignmentType: next?.assignmentType || "manual",
  //       assignedAt: next?.assignedAt || null,
  //       assignedToUser: next?.assignedToUser || null,
  //     }));
  //   } catch {
  //     loadThreadsRef.current();
  //   }
  // }, [findKnownThreadRow, updateThreadRowEverywhere]);

  // Create (or, for a 1:1, reuse) an internal staff chat, then select it. Backs the Chats
  // compose flow; the backend dedups direct chats and requires a name for groups.
  const createInternalChat = useCallback(
    async ({ name, userIds, kind } = {}) => {
      const result = await createInternalThread({ name, userIds, kind });
      const newThreadId = String(result?.threadId || "").trim();
      if (newThreadId) {
        setSelectedThreadId(newThreadId);
        await loadThreads();
      }
      return result;
    },
    [loadThreads],
  );

  // Start (or reopen) a WhatsApp conversation with a patient who hasn't messaged first: find-or-
  // create the thread via the default gateway, refresh the list so the new row appears, then open
  // it (which loads history and focuses the composer).
  const startPatientMessage = useCallback(
    async ({ patientUserId, phone, displayName, whatsappBotId, channel } = {}) => {
      // "app" targets the patient's Healo-app conversation (no gateway, no phone); anything
      // else keeps the WhatsApp flow. Post-create navigation is channel-agnostic.
      const result = String(channel || "").toLowerCase() === "app"
        ? await ensureAppThreadForPatient({ patientUserId })
        : await ensurePatientThread({ patientUserId, phone, displayName, whatsappBotId });
      const newThreadId = String(result?.threadId || "").trim();
      if (newThreadId) {
        await loadThreads();
        handleThreadClickRef.current(newThreadId);
      }
      return result;
    },
    [loadThreads],
  );

  // Leave an internal GROUP chat (Chats only). Mirrors handleDeleteChat's client flow:
  // optimistic removal + clear selection + server reconcile. The backend appends the
  // "left the group" system row and notifies the remaining members.
  const [leaveGroupInProgress, setLeaveGroupInProgress] = useState(false);
  const handleLeaveGroup = useCallback(
    async (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      setLeaveGroupInProgress(true);
      try {
        await leaveInternalThread({ threadId: tid });
        if (String(selectedThreadIdRef.current || "").trim() === tid) {
          setSelectedThreadId(null);
          persistSelectedThreadId(null, scope);
          setHistoryItems([]);
          setHistoryError("");
        }
        setThreads((prev) => prev.filter((t) => getThreadRowId(t) !== tid));
        markThreadReadLocally(tid);
        await loadThreads();
      } catch (error) {
        toast.error(String(error?.message || "Could not leave the group."));
        loadThreadsRef.current();
      } finally {
        setLeaveGroupInProgress(false);
      }
    },
    [loadThreads, scope],
  );

  // Internal Chats read-state: opening a thread (or the context menu action) marks it read
  // server-side; "Mark as unread" flags it. Both update the row + the sidebar bubble
  // optimistically — the summary refresh reconciles.
  const markThreadReadEverywhere = useCallback(
    (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      updateThreadRowEverywhere(tid, (t) => ({ ...t, unreadCount: 0 }));
      markThreadReadLocally(tid);
      setLocalUnreadByThreadId((prev) => {
        if (!prev[tid]) return prev;
        const next = { ...prev };
        delete next[tid];
        return next;
      });
      markThreadRead({ threadId: tid }).catch(() => {
        /* advisory; the next summary refresh corrects */
      });
    },
    [updateThreadRowEverywhere],
  );
  const handleMarkThreadRead = useCallback(
    (threadId) => markThreadReadEverywhere(threadId),
    [markThreadReadEverywhere],
  );
  const handleMarkThreadUnread = useCallback(
    (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      // Marking the OPEN thread unread also closes it — otherwise the open pane's read
      // receipts would immediately re-read it.
      if (String(selectedThreadIdRef.current || "").trim() === tid) {
        setSelectedThreadId(null);
        persistSelectedThreadId(null, scope);
        setHistoryItems([]);
        setHistoryError("");
      }
      updateThreadRowEverywhere(tid, (t) => ({ ...t, unreadCount: Math.max(1, Number(t?.unreadCount) || 0) }));
      markThreadUnreadLocally(tid);
      markThreadUnread({ threadId: tid }).catch(() => {
        /* advisory; the next summary refresh corrects */
      });
    },
    [scope, updateThreadRowEverywhere],
  );

  // Soft-delete a chat (Chats only). Optimistically drops it from the list, clears the detail
  // pane if it was open, then reconciles with the server.
  const [deleteChatInProgress, setDeleteChatInProgress] = useState(false);
  const handleDeleteChat = useCallback(
    async (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      setDeleteChatInProgress(true);
      try {
        await deleteThread({ threadId: tid });
        if (String(selectedThreadIdRef.current || "").trim() === tid) {
          setSelectedThreadId(null);
          persistSelectedThreadId(null, scope);
          setHistoryItems([]);
          setHistoryError("");
        }
        setThreads((prev) => prev.filter((t) => getThreadRowId(t) !== tid));
        threadListWindowRef.current.removeThread(tid);
        await loadThreads();
      } catch {
        // On failure, reload to reflect the true server state rather than leave a phantom row.
        loadThreadsRef.current();
      } finally {
        setDeleteChatInProgress(false);
      }
    },
    [loadThreads, scope],
  );

  // DEV-ONLY admin action: delete a thread AND all its messages via
  // DELETE /api/admin/threads/{threadId} (no membership requirement, unlike handleDeleteChat's
  // endpoint). Two-step: requestDeleteThread arms the confirm dialog, confirmDeleteThread fires.
  const [deleteThreadTarget, setDeleteThreadTarget] = useState(null); // { threadId, title } | null
  const [deleteThreadInProgress, setDeleteThreadInProgress] = useState(false);
  const requestDeleteThread = useCallback((threadId, title = "") => {
    const tid = String(threadId || "").trim();
    if (!tid) return;
    setDeleteThreadTarget({ threadId: tid, title: String(title || "").trim() });
  }, []);
  const handleDeleteThreadDialogOpenChange = useCallback((open) => {
    if (!open) setDeleteThreadTarget(null);
  }, []);
  const confirmDeleteThread = useCallback(async () => {
    const tid = String(deleteThreadTarget?.threadId || "").trim();
    if (!tid || deleteThreadInProgress) return;
    setDeleteThreadInProgress(true);
    try {
      await deleteAdminThread({ threadId: tid });
      setDeleteThreadTarget(null);
      if (String(selectedThreadIdRef.current || "").trim() === tid) {
        setSelectedThreadId(null);
        persistSelectedThreadId(null, scope);
        setHistoryItems([]);
        setHistoryError("");
      }
      setThreads((prev) => prev.filter((t) => getThreadRowId(t) !== tid));
      threadListWindowRef.current.removeThread(tid);
      await loadThreads();
    } catch {
      // Keep the dialog open so the failure is visible (retry or cancel); reload to reflect the
      // true server state rather than leave a phantom row.
      loadThreadsRef.current();
    } finally {
      setDeleteThreadInProgress(false);
    }
  }, [deleteThreadTarget, deleteThreadInProgress, loadThreads, scope]);

  const scheduleClearThreadGroupsError = useCallback(() => {
    if (threadGroupsErrorClearRef.current) {
      clearTimeout(threadGroupsErrorClearRef.current);
    }
    threadGroupsErrorClearRef.current = setTimeout(() => {
      setThreadGroupsError("");
      threadGroupsErrorClearRef.current = null;
    }, 8000);
  }, []);

  const handleThreadGroupChange = useCallback(
    async (threadId, nextGroupId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      const targetGroupId =
        typeof nextGroupId === "string" && nextGroupId.trim() ? nextGroupId.trim() : null;

      const current = findKnownThreadRow(tid);
      const currentMetadata =
        current?.metadata && typeof current.metadata === "object" ? current.metadata : {};
      const nextMetadata = { ...currentMetadata };
      if (targetGroupId) {
        nextMetadata.group = targetGroupId;
      } else {
        delete nextMetadata.group;
      }

      // Optimistic update so the chip / partition reflects the choice immediately.
      updateThreadRowEverywhere(tid, (t) => ({ ...t, metadata: nextMetadata }));

      try {
        setThreadGroupsError("");
        // Send ONLY the key being changed — the server merges per key and treats an explicit null
        // as a delete. Round-tripping the whole blob (as this used to) meant a group change wrote
        // back metadata read seconds earlier, undoing anything the WhatsApp self-heal or a flag /
        // status change had merged in since.
        const res = await updateAdminThread({
          threadId: tid,
          metadata: { group: targetGroupId },
        });
        const serverMetadata =
          res?.metadata && typeof res.metadata === "object" ? res.metadata : nextMetadata;
        updateThreadRowEverywhere(tid, (t) => ({ ...t, metadata: serverMetadata }));
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update thread group";
        setThreadGroupsError(message);
        scheduleClearThreadGroupsError();
      }
    },
    [findKnownThreadRow, scheduleClearThreadGroupsError, updateThreadRowEverywhere],
  );

  // DISABLED (for now): only consumed by the disabled thread-assignment handlers below.
  //
  // /**
  //  * Strip the workflow-status override (and its companions) from a metadata object when it is
  //  * one of `clearStatuses` — mirrors the server-side cleanup on assign/unassign so the derived
  //  * status wins immediately. Returns the same reference when nothing changes.
  //  */
  // const metadataWithoutStatusOverride = useCallback((metadata, clearStatuses) => {
  //   const current = String(metadata?.status || "").trim();
  //   if (!current || !clearStatuses.includes(current)) return metadata;
  //   const next = { ...(metadata || {}) };
  //   delete next.status;
  //   delete next.statusChangedAt;
  //   delete next.statusChangedBy;
  //   delete next.statusMessageId;
  //   delete next.pendingReason;
  //   delete next.pendingUntil;
  //   return next;
  // }, []);

  // DISABLED (for now): thread assignment (explicit assign, unassign, claim,
  // release) — assignment lives on tasks only (see handleTaskClaim /
  // handleTaskAssign). Kept for easy restore.
  //
  // /** Explicit manual (re)assignment of a thread to a chosen team member. */
  // const handleAssignThread = useCallback(async (threadId, userId) => {
  //   const tid = String(threadId || "").trim();
  //   const uid = String(userId || "").trim();
  //   if (!tid || !uid) return;
  //   try {
  //     const res = await assignThread({ threadId: tid, userId: uid });
  //     const next = res?.thread || null;
  //     updateThreadRowEverywhere(tid, (t) => ({
  //       ...t,
  //       assignedToUserId: next?.assignedToUserId || uid,
  //       assignmentType: next?.assignmentType || "manual",
  //       assignedAt: next?.assignedAt || null,
  //       assignedToUser: next?.assignedToUser || null,
  //       metadata: next?.metadata || metadataWithoutStatusOverride(t.metadata, ["open", "pending"]),
  //     }));
  //   } catch {
  //     loadThreadsRef.current();
  //   }
  // }, [metadataWithoutStatusOverride, updateThreadRowEverywhere]);
  //
  // /** Clear a thread's assignee. */
  // const handleUnassignThread = useCallback(async (threadId) => {
  //   const tid = String(threadId || "").trim();
  //   if (!tid) return;
  //   try {
  //     await unassignThread({ threadId: tid });
  //     updateThreadRowEverywhere(tid, (t) => ({
  //       ...t,
  //       assignedToUserId: "",
  //       assignmentType: "",
  //       assignedAt: null,
  //       assignedToUser: null,
  //       metadata: metadataWithoutStatusOverride(t.metadata, ["assigned"]),
  //     }));
  //   } catch {
  //     loadThreadsRef.current();
  //   }
  // }, [metadataWithoutStatusOverride, updateThreadRowEverywhere]);
  //
  // /**
  //  * Claim: grab an open thread from the shared queue — assignee becomes the current user and
  //  * the thread derives "assigned" (the server also clears an open/pending override). Optimistic;
  //  * the owner avatar renders from the id (isCurrentUserId) until the expanded user reconciles.
  //  */
  // const handleClaimThread = useCallback(async (threadId) => {
  //   const tid = String(threadId || "").trim();
  //   if (!tid) return;
  //   const me = String(getAuthUser()?.id || "").trim();
  //   if (me) {
  //     updateThreadRowEverywhere(tid, (t) => ({
  //       ...t,
  //       assignedToUserId: me,
  //       assignmentType: "manual",
  //       assignedAt: new Date().toISOString(),
  //       assignedToUser: null,
  //       metadata: metadataWithoutStatusOverride(t.metadata, ["open", "pending"]),
  //     }));
  //   }
  //   try {
  //     setThreadFlagsError("");
  //     const res = await assignThread({ threadId: tid });
  //     const next = res?.thread || null;
  //     if (next) {
  //       updateThreadRowEverywhere(tid, (t) => ({
  //         ...t,
  //         assignedToUserId: next.assignedToUserId || me,
  //         assignmentType: next.assignmentType || "manual",
  //         assignedAt: next.assignedAt || null,
  //         assignedToUser: next.assignedToUser || null,
  //         metadata: next.metadata || t.metadata,
  //       }));
  //     }
  //   } catch (error) {
  //     loadThreadsRef.current();
  //     const message = error instanceof Error ? error.message : "Could not claim thread";
  //     setThreadFlagsError(message);
  //     scheduleClearThreadFlagsError();
  //   }
  // }, [metadataWithoutStatusOverride, scheduleClearThreadFlagsError, updateThreadRowEverywhere]);
  //
  // /** Release: hand a claimed thread back to the shared queue (derives back to open). */
  // const handleReleaseThread = useCallback(
  //   (threadId) => handleUnassignThread(threadId),
  //   [handleUnassignThread],
  // );

  /**
   * Set or clear the workflow-status override. Optimistically mirrors the server's metadata
   * patch — including the statusMessageId anchor for done/pending, which drives the
   * auto-reopen-on-patient-reply derivation. Done also RELEASES the thread (server does the
   * same), so a re-opened conversation returns to the shared queue, not to whoever closed it.
   */
  const handleStatusChange = useCallback(
    async (threadId, status, { pendingReason, pendingUntil } = {}) => {
      const tid = String(threadId || "").trim();
      const next = String(status || "").trim();
      if (!tid) return;
      const untilISO =
        pendingUntil instanceof Date ? pendingUntil.toISOString() : String(pendingUntil || "").trim();
      updateThreadRowEverywhere(tid, (t) => {
        const metadata = { ...(t.metadata || {}) };
        delete metadata.status;
        delete metadata.statusChangedAt;
        delete metadata.statusChangedBy;
        delete metadata.statusMessageId;
        delete metadata.pendingReason;
        delete metadata.pendingUntil;
        if (next) {
          metadata.status = next;
          metadata.statusChangedAt = new Date().toISOString();
          if (next === "done" || next === "pending") {
            const mid = String(t?.lastMessageId || "").trim();
            if (mid) metadata.statusMessageId = mid;
          }
          if (next === "pending") {
            if (pendingReason) metadata.pendingReason = String(pendingReason).trim();
            if (untilISO) metadata.pendingUntil = untilISO;
          }
        }
        const row = { ...t, metadata };
        if (next === "done") {
          row.assignedToUserId = "";
          row.assignmentType = "";
          row.assignedAt = null;
          row.assignedToUser = null;
        }
        return row;
      });
      try {
        setThreadFlagsError("");
        await setThreadStatus({
          threadId: tid,
          status: next,
          pendingReason,
          pendingUntil: untilISO || undefined,
        });
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update thread status";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, updateThreadRowEverywhere],
  );

  /**
   * Snooze: status → pending with an `until`. The thread leaves the active queues and derives
   * back to open when the deadline passes — or sooner, when the patient replies.
   */
  const handleSnoozeThread = useCallback(
    (threadId, until) =>
      handleStatusChange(threadId, "pending", { pendingReason: "snooze", pendingUntil: until }),
    [handleStatusChange],
  );

  /**
   * Toggle a patient tag. Tags live on the PATIENT record (patientMeta.tags, deep-merged
   * server-side), so the optimistic update touches every loaded thread row sharing that
   * patient — they all display the same tag set.
   */
  const handlePatientTagToggle = useCallback(
    async (threadId, tagKey, nextChecked) => {
      const tid = String(threadId || "").trim();
      const key = String(tagKey || "").trim();
      if (!tid || !key) return;
      const thread = findKnownThreadRow(tid);
      const patientUserId = String(thread?.patientUserId || "").trim();
      if (!patientUserId) {
        setThreadFlagsError("This conversation has no patient to tag.");
        scheduleClearThreadFlagsError();
        return;
      }
      const current = Array.isArray(thread?.patientTags)
        ? thread.patientTags.map((t) => String(t || "").trim()).filter(Boolean)
        : [];
      const nextTags = nextChecked
        ? Array.from(new Set([...current, key]))
        : current.filter((t) => t !== key);

      const apply = (prev) =>
        prev.map((t) =>
          String(t?.patientUserId || "").trim() === patientUserId ? { ...t, patientTags: nextTags } : t,
        );
      setThreads(apply);
      threadListWindowRef.current.patchRowsWhere(
        (t) => String(t?.patientUserId || "").trim() === patientUserId,
        (t) => ({ ...t, patientTags: nextTags }),
      );
      setGroupedThreadRows((prev) => (Array.isArray(prev) ? apply(prev) : prev));
      setAttentionWaiting(apply);
      setAttentionBotAck(apply);
      setDefaultThreads(apply);
      setSearchResults(apply);

      try {
        setThreadFlagsError("");
        await putAdminUser({ userId: patientUserId, patientMeta: { tags: nextTags } });
      } catch (error) {
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update patient tags";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [findKnownThreadRow, scheduleClearThreadFlagsError],
  );

  /** Reload the selected thread's checklist (selection change + task WS events). */
  const loadThreadTasks = useCallback(async (threadId) => {
    const tid = String(threadId || "").trim();
    if (!tid) return;
    const seq = ++threadTasksSeqRef.current;
    setThreadTasksLoading(true);
    try {
      const res = await fetchThreadTasks({ threadId: tid });
      if (threadTasksSeqRef.current !== seq) return;
      if (String(selectedThreadIdRef.current || "").trim() !== tid) return;
      setThreadTasks(res.tasks);
    } catch {
      if (threadTasksSeqRef.current === seq) setThreadTasks([]);
    } finally {
      if (threadTasksSeqRef.current === seq) setThreadTasksLoading(false);
    }
  }, []);
  const loadThreadTasksRef = useRef(loadThreadTasks);
  loadThreadTasksRef.current = loadThreadTasks;

  // Load the checklist whenever the selected (normal-scope) thread changes.
  useEffect(() => {
    if (isScopedList) {
      setThreadTasks([]);
      return;
    }
    const tid = String(selectedThreadId || "").trim();
    if (!tid) {
      threadTasksSeqRef.current += 1;
      setThreadTasks([]);
      setThreadTasksLoading(false);
      return;
    }
    loadThreadTasksRef.current(tid);
  }, [selectedThreadId, scope]);

  /** Bump the thread-row task badge ("open/total") optimistically alongside checklist edits. */
  const bumpThreadTaskCounts = useCallback(
    (threadId, deltaOpen, deltaTotal) => {
      updateThreadRowEverywhere(threadId, (t) => ({
        ...t,
        tasksOpen: Math.max(0, (Number(t.tasksOpen) || 0) + deltaOpen),
        tasksTotal: Math.max(0, (Number(t.tasksTotal) || 0) + deltaTotal),
      }));
    },
    [updateThreadRowEverywhere],
  );

  /** Add a staff task to the selected thread's checklist. */
  const handleTaskCreate = useCallback(
    async (title, { dueAt } = {}) => {
      const tid = String(selectedThreadIdRef.current || "").trim();
      const trimmedTitle = String(title || "").trim();
      if (!tid || !trimmedTitle) return;
      try {
        setThreadFlagsError("");
        const res = await createThreadTask({ threadId: tid, title: trimmedTitle, dueAt });
        if (res.task && String(selectedThreadIdRef.current || "").trim() === tid) {
          setThreadTasks((prev) => [...prev, res.task]);
        }
        bumpThreadTaskCounts(tid, 1, 1);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not add task";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [bumpThreadTaskCounts, scheduleClearThreadFlagsError],
  );

  /** Toggle a checklist task done/todo (optimistic, reconciled from the response). */
  const handleTaskToggle = useCallback(
    async (taskId, nextDone) => {
      const id = String(taskId || "").trim();
      if (!id) return;
      const current = threadTasks.find((t) => t.id === id);
      if (!current) return;
      const nextStatus = nextDone ? "done" : "todo";
      if (current.status === nextStatus) return;
      const tid = current.threadId;
      setThreadTasks((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, status: nextStatus, doneAt: nextDone ? new Date().toISOString() : null }
            : t,
        ),
      );
      bumpThreadTaskCounts(tid, nextDone ? -1 : 1, 0);
      try {
        setThreadFlagsError("");
        const res = await updateTask({ taskId: id, status: nextStatus });
        if (res.task) {
          setThreadTasks((prev) => prev.map((t) => (t.id === id ? res.task : t)));
        }
      } catch (error) {
        loadThreadTasksRef.current(tid);
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not update task";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [bumpThreadTaskCounts, scheduleClearThreadFlagsError, threadTasks],
  );

  /** Claim a checklist task (assignee = me). A lost race (409) reloads the checklist. */
  const handleTaskClaim = useCallback(
    async (taskId) => {
      const id = String(taskId || "").trim();
      if (!id) return;
      const me = String(getAuthUser()?.id || "").trim();
      if (!me) return;
      const current = threadTasks.find((t) => t.id === id);
      setThreadTasks((prev) => prev.map((t) => (t.id === id ? { ...t, assigneeId: me } : t)));
      try {
        setThreadFlagsError("");
        const res = await updateTask({ taskId: id, assigneeId: me });
        if (res.task) {
          setThreadTasks((prev) => prev.map((t) => (t.id === id ? res.task : t)));
        }
      } catch (error) {
        if (current?.threadId) loadThreadTasksRef.current(current.threadId);
        const message =
          error instanceof ApiError && error.status === 409
            ? "Someone else just claimed this task."
            : error instanceof Error
              ? error.message
              : "Could not claim task";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, threadTasks],
  );

  /**
   * Assign a checklist task to a chosen teammate (or "" to clear). Unlike claim, this is a
   * deliberate reassignment — it may overwrite the current assignee (reassign: true skips
   * the claim race-guard). `displayName` paints the optimistic owner avatar immediately.
   */
  const handleTaskAssign = useCallback(
    async (taskId, userId, displayName = "") => {
      const id = String(taskId || "").trim();
      if (!id) return;
      const uid = String(userId || "").trim();
      const current = threadTasks.find((t) => t.id === id);
      setThreadTasks((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                assigneeId: uid,
                assignee: uid && displayName ? { id: uid, displayName } : null,
              }
            : t,
        ),
      );
      try {
        setThreadFlagsError("");
        const res = await updateTask({ taskId: id, assigneeId: uid, reassign: true });
        if (res.task) {
          setThreadTasks((prev) => prev.map((t) => (t.id === id ? res.task : t)));
        }
      } catch (error) {
        if (current?.threadId) loadThreadTasksRef.current(current.threadId);
        const message = error instanceof Error ? error.message : "Could not assign task";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [scheduleClearThreadFlagsError, threadTasks],
  );

  /** Delete a checklist task. */
  const handleTaskDelete = useCallback(
    async (taskId) => {
      const id = String(taskId || "").trim();
      if (!id) return;
      const current = threadTasks.find((t) => t.id === id);
      if (!current) return;
      setThreadTasks((prev) => prev.filter((t) => t.id !== id));
      bumpThreadTaskCounts(current.threadId, current.status === "todo" ? -1 : 0, -1);
      try {
        setThreadFlagsError("");
        await deleteTask({ taskId: id });
      } catch (error) {
        loadThreadTasksRef.current(current.threadId);
        loadThreadsRef.current();
        const message = error instanceof Error ? error.message : "Could not delete task";
        setThreadFlagsError(message);
        scheduleClearThreadFlagsError();
      }
    },
    [bumpThreadTaskCounts, scheduleClearThreadFlagsError, threadTasks],
  );

  useEffect(() => {
    return subscribeUserThreadEvents((detail) => {
      scheduleThreadsRefreshRef.current();
      if (String(detail.action || "").startsWith("task")) {
        // Task events refresh the open thread's checklist; the scheduled list refresh above
        // already reconciles every row's task badge.
        const tid = String(detail.threadId || "").trim();
        if (
          tid &&
          !scopedListRef.current &&
          tid === String(selectedThreadIdRef.current || "").trim()
        ) {
          loadThreadTasksRef.current(tid);
        }
        return;
      }
      // A thread was created/removed/repointed (task events already returned above): the merged
      // view's per-patient lookup is a snapshot, so invalidate it and refetch. Only when one is
      // actually cached — otherwise every thread event on the account would tick this for nothing.
      if (mergedPatientFetchKeyRef.current) setMergedPatientRefreshTick((n) => n + 1);
      if (detail.action === "deleted") {
        const tid = String(detail.threadId || "").trim();
        if (tid) historyCacheRef.current.delete(tid);
        const active = String(selectedThreadIdRef.current || "").trim();
        if (tid && active === tid) {
          setSelectedThreadId(null);
          persistSelectedThreadId(null, scope);
          setHistoryItems([]);
          setHistoryError("");
          setHistoryLoading(false);
          membersFetchSeqRef.current += 1;
          setMemberListSource("idle");
          setThreadMembersGrouped({ direct: [], team: [], bots: [] });
          setThreadMembersError("");
          setThreadMembersNotice("");
          setUserDisplayNamesById({});
          setLocalUnreadByThreadId((prev) => {
            if (!prev[tid]) return prev;
            const next = { ...prev };
            delete next[tid];
            return next;
          });
        }
      }
    });
  }, []);

  /** After merge / profile updates, messages keep stale sender ids + labels until history is refetched. */
  const reloadActiveThreadHistorySilently = useCallback(async (threadId) => {
    const tid = String(threadId || "").trim();
    if (!tid) return;
    try {
      const response = await fetchThreadHistory({ threadId: tid, maxMessageCount: HISTORY_PAGE_SIZE, skip: 0 });
      if (selectedThreadIdRef.current !== tid) return;
      const fresh = (Array.isArray(response?.history) ? response.history : []).slice().reverse();
      const merged = mergeHistoryNewest(historyItemsRef.current, fresh);
      const nextHistoryItems = merged.items;
      setHistoryItems(nextHistoryItems);
      if (merged.gap) setHistoryAllOlderLoaded(fresh.length < HISTORY_PAGE_SIZE);

      const humanUserIds = new Set();
      const botSenderIds = new Set();
      for (const item of nextHistoryItems) {
        if (isStandaloneSystemLineMessage(item)) continue;
        const senderId = String(item?.sender?.userId || "").trim();
        if (!senderId) continue;
        if (isSenderBotMessage(item)) {
          botSenderIds.add(senderId);
        } else {
          humanUserIds.add(senderId);
        }
      }

      if (humanUserIds.size === 0 && botSenderIds.size === 0) {
        setUserDisplayNamesById({});
        setUserAvatarUrlsById({});
        return;
      }

      // ONE request for every distinct sender. Fanning out per id cost ~30 requests plus ~30 CORS
      // preflights on a busy thread (the preflight cache is per-url, so it could not help), which
      // then queued against the browser's connection limit.
      const humanEntries = await fetchUserDisplayProfileEntries([...humanUserIds]);
      const botEntries = [...botSenderIds].map((id) => [id, formatBotDisplayLabel(id)]);
      setUserDisplayNamesById(
        Object.fromEntries([
          ...humanEntries.map(([id, profile]) => [id, String(profile?.label || "").trim() || id]),
          ...botEntries,
        ]),
      );
      setUserAvatarUrlsById(
        Object.fromEntries(humanEntries.map(([id, profile]) => [id, String(profile?.avatar || "").trim()])),
      );
    } catch {
      /* ignore — full thread open still works */
    }
  }, []);

  // Latest-value ref so long-lived subscriptions (e.g. the receipt-delta listener) can trigger a
  // silent reload without re-subscribing whenever the callback identity changes.
  const reloadActiveThreadHistorySilentlyRef = useRef(reloadActiveThreadHistorySilently);
  reloadActiveThreadHistorySilentlyRef.current = reloadActiveThreadHistorySilently;

  useEffect(() => {
    return subscribeUserRefresh(() => {
      scheduleThreadsRefreshRef.current();
      const activeThreadId = String(selectedThreadIdRef.current || "").trim();
      if (activeThreadId) {
        void loadAdminThreadMembers(activeThreadId);
        void reloadActiveThreadHistorySilently(activeThreadId);
      }
    });
  }, [loadAdminThreadMembers, reloadActiveThreadHistorySilently]);

  useEffect(() => {
    firstThreadsLoadDoneRef.current = false;
    loadThreads().finally(() => {
      firstThreadsLoadDoneRef.current = true;
      // Release the window's cold-start fetch whether or not the overview succeeded: on failure
      // the window's own chunk-0 request becomes the retry path.
      setThreadsSeedPending(false);
    });
    return subscribeAuthStateChange(() => {
      // Workspace owner guard: a profile switch fires this synchronously while the OUTGOING
      // user's pane is still mounted and subscribed — only the pane owned by the now-current
      // user may reset its caches and refetch, or the outgoing workspace would load the
      // incoming user's threads right before it freezes. Also inert when no user is current
      // (401/logout), preserving the frozen state for the thaw; '' owner (not inside a
      // workspace pane) keeps the legacy always-reset behavior.
      const owner = ownerUserId;
      if (owner && String(getAuthUser()?.id || "") !== owner) return;
      restoredSelectedThreadRef.current = false;
      // A different session for the SAME owner (e.g. PIN re-entry after expiry) invalidates
      // every cached/loaded thread and the pagination frontier — start the list and history
      // caches fresh, and re-arm the saved-selection restore to wait for the first load.
      // (The module-level AI-rewrite meta cache is cleared by its own module-level auth
      // subscriber — this owner-guarded listener can't be trusted with cross-user caches.)
      firstThreadsLoadDoneRef.current = false;
      historyCacheRef.current.clear();
      // reset() clears the total, which would re-arm the window's cold-start fetch — re-arm the
      // suppression too so the incoming user's load is again a single request.
      setThreadsSeedPending(!scopedListRef.current);
      threadListWindowRef.current.reset();
      loadThreads().finally(() => {
        firstThreadsLoadDoneRef.current = true;
        setThreadsSeedPending(false);
      });
    });
  }, []);

  useEffect(() => {
    if (selectedThreadId) {
      persistSelectedThreadId(selectedThreadId, scope);
    }
  }, [selectedThreadId]);

  useEffect(() => {
    if (restoredSelectedThreadRef.current) {
      return;
    }
    // A buffered notification jump owns the selection: it drains in a later effect on this same
    // mount, so restoring here first would open the saved thread only for it to be replaced a tick
    // later — two history fetches and a visible flash.
    if (hasPendingOpenThreadMessage(openTargetScope)) {
      return;
    }
    // The persisted id is keyed by the signed-in user, so without an auth user the read is not
    // "no saved selection", it is "cannot tell yet" — bail WITHOUT latching and let a later pass
    // (the deps below re-run once the list loads) decide.
    if (!String(getAuthUser()?.id || "").trim()) {
      return;
    }
    const savedId = readPersistedSelectedThreadId(scope);
    restoredSelectedThreadRef.current = true;
    if (!savedId) {
      return;
    }
    // Deliberately NOT gated on the thread list having loaded. The saved id comes from
    // localStorage and needs no network, so opening here starts the conversation's history fetch
    // immediately rather than queueing it behind the thread-list request — which on a large tenant
    // is seconds of dead time before the reader sees anything.
    //
    // Waiting was never load-bearing anyway: the virtual list only holds the loaded ranges, so
    // "not in a loaded list" says nothing about the thread's existence, which is why the previous
    // version restored regardless of what the list contained. The off-list fetch-on-miss effect
    // resolves the header, and clears the selection on a definitive 404.
    //
    // restoredFromSession: a saved id the server definitively rejects (403/404 — deleted, or no
    // longer/never a member) clears itself silently instead of opening a dead pane with a red
    // "user is not a member of this thread" error the reader never asked for.
    handleThreadClickRef.current(savedId, { restoredFromSession: true });
    // The list-load deps remain so a mount that raced auth (no user id yet above) still restores
    // on the next pass, and so a profile switch — which resets the ref and bumps `generation` —
    // re-restores for the incoming user.
  }, [threads, threadListWindow.total, threadListWindow.generation, isInternalScope, scope, openTargetScope]);

  useEffect(() => {
    return subscribeChatConnectionStatus(setChatConnectionStatus);
  }, []);

  useEffect(() => {
    return subscribeChatMessages((detail) => {
      if (detail.kind === "error") {
        console.warn("[Chat realtime]", detail.message);
        return;
      }
      if (detail.kind !== "message") {
        return;
      }
      const msg = detail.message;
      const activeId = String(selectedThreadIdRef.current || "").trim();
      const msgThreadId = String(msg?.threadId || "").trim();
      const uid = getCurrentUserIdForReadReceipts();

      // An in-place update (a reaction change, an edit, a revoke/edit verdict) re-emits an
      // existing message. Refresh the bubble wherever a copy is held — open history, merged
      // sibling page, warm cache — but never bump the thread row's recency, flag unread, or
      // reload threads. A soft-deleted / unsent message arrives the same way with
      // `deleted: true`: drop the bubble instead of replacing it, and reload the list in case it
      // was a row's last/preview message.
      if (detail.update === true) {
        const isDeleted = msg?.deleted === true;
        const updatedMessageId = String(msg?.id || "").trim();
        if (updatedMessageId && msgThreadId) {
          patchMessageEverywhere(msgThreadId, updatedMessageId, (prevItem) => {
            if (isDeleted) return null;
            // Carry clientId across, the same way the new-message path does: the bubble's React
            // key is `clientId || id`, so dropping it on an in-place refresh remounts the node
            // and re-fires its arrival animation. Matters most for own messages, which keep a
            // clientId from their optimistic placeholder and are exactly the ones that receive
            // read/delivery receipt updates seconds after send.
            const keptClientId = prevItem?.clientId;
            return keptClientId ? { ...msg, clientId: keptClientId } : msg;
          });
        } else if (msgThreadId && msgThreadId === activeId) {
          // Id-less update frame (defensive): fall back to the legacy dedup matching in the open
          // thread only.
          setHistoryItems((prev) => {
            const idx = findMessageIndexInHistory(prev, msg);
            if (idx < 0) {
              return prev;
            }
            const next = [...prev];
            if (isDeleted) {
              next.splice(idx, 1);
            } else {
              const keptClientId = prev[idx]?.clientId;
              next[idx] = keptClientId ? { ...msg, clientId: keptClientId } : msg;
            }
            return next;
          });
        }
        // A reaction change on a row's LAST message re-classifies the row live in every list —
        // e.g. a teammate's business reaction drops the thread out of "Unanswered patient
        // reply" here too, without waiting for the next overview refresh — and an EDIT of the
        // row's last message refreshes its preview text the same way.
        if (!isDeleted && msgThreadId && updatedMessageId) {
          updateThreadRowEverywhere(msgThreadId, (t) => patchThreadRowForMessageUpdate(t, msg));
        }
        if (isDeleted) {
          // The message being edited just vanished (unsent from another device / by a DEV tool):
          // there is nothing left to save into — drop the edit and give the parked draft back.
          if (updatedMessageId && editingMessageRef.current?.id === updatedMessageId) {
            exitEditModeRef.current({ restoreDraft: true });
          }
          scheduleThreadsRefreshRef.current();
        }
        return;
      }

      if (msgThreadId) {
        // Under the merged Groups view the LIST row for a sibling is its canonical row; every
        // other use of msgThreadId below (history matching, receipts, mark-read) must stay on
        // the real thread, so the translation is deliberately confined to the two row-facing
        // call sites.
        const rowThreadId = canonicalRowIdFor(msgThreadId);
        // Update the row wherever it is known — the page list AND the server-fed attention
        // lists — so a new message keeps section rows live between overview refreshes.
        updateThreadRowEverywhere(rowThreadId, (t) => {
          // Mirror the backend's newestNonSystemMessage: metadata-flagged informational
          // messages (isSystem/autoReply — lock notes, passive call logs) bump the row's
          // recency and preview but must not become its lastMessageSender/lastMessageId,
          // or they would transiently re-classify the thread's attention section.
          const isSystemInformational =
            msg?.metadata?.isSystem === true || msg?.metadata?.autoReply === true;
          const uid = String(msg?.sender?.userId || "").trim();
          const nextSender =
            !isSystemInformational && uid.length > 0
              ? {
                userId: uid,
                kind: String(msg?.sender?.kind || "user").toLowerCase() === "bot" ? "bot" : "user",
              }
              : null;
          const nextMessageId = isSystemInformational ? "" : String(msg?.id || "").trim();
          return {
            ...t,
            lastMessageAt: msg.createdAt || t.lastMessageAt,
            // Group system lines carry a structured body the generic parser would render as a
            // bare capitalized event ("Participant add") — format the human line instead, the
            // same one the server-enriched preview shows on the next refresh.
            lastMessagePreview:
              (String(msg?.contentType || "").trim().toLowerCase() === "group.system"
                ? formatWaGroupSystemLine(msg)
                : parseMessageBodyText(msg?.body)) || t.lastMessagePreview,
            ...(nextSender ? { lastMessageSender: nextSender } : {}),
            // A new message becomes the thread's last one: track its id, drop any prior
            // acknowledgement so a fresh bot reply re-surfaces under "Unanswered bot replies",
            // and refresh the WhatsApp staff-answer (`fromMe`) flag so a staff reply drops the
            // thread out of "Unanswered patient reply" instantly (not only on the next refetch).
            ...(nextMessageId
              ? {
                  lastMessageId: nextMessageId,
                  lastMessageAcknowledgedAt: null,
                  lastMessageFromMe: isWhatsAppStaffAnswerMessage(msg),
                }
              : {}),
          };
        });
        // A message for an as-yet-unknown thread is intentionally NOT injected optimistically:
        // the user.messages.{uid} stream carries both Messages and Chats threads, and a row built
        // from a message can't tell which scope it belongs to — injecting would leak a Chats
        // thread into Messages (or vice versa) and persist via keptOptimistic. The scope-filtered
        // loadThreads() below reconciles the list authoritatively instead.
      }

      if (msgThreadId && msgThreadId !== activeId && uid && shouldFlagThreadUnreadFromWsMessage(msg, uid)) {
        setLocalUnreadByThreadId((prev) => ({
          ...prev,
          // Keyed by the row the badge renders on, which is the canonical sibling when merged.
          [canonicalRowIdFor(msgThreadId)]: true,
        }));
      }

      if (!msgThreadId || msgThreadId !== activeId) {
        scheduleThreadsRefreshRef.current();
        return;
      }
      const prevHistory = historyItemsRef.current;
      const messageAlreadyPresent = findMessageIndexInHistory(prevHistory, msg) >= 0;

      // An own echo settles a pending optimistic placeholder (which has no server id, so the normal
      // dedup can't match it). Clear that placeholder's failure backstop here; the in-place swap
      // happens in the updater below, preserving clientId so the bubble's React key — and its DOM
      // node mid-fly — stays stable.
      if (!messageAlreadyPresent) {
        const optIdx = findOptimisticMatchIndex(prevHistory, msg);
        const reconciledClientId = optIdx >= 0 ? prevHistory[optIdx].clientId || null : null;
        if (reconciledClientId) {
          const pending = pendingOptimisticRef.current.get(reconciledClientId);
          if (pending?.timer) clearTimeout(pending.timer);
          pendingOptimisticRef.current.delete(reconciledClientId);
        }
      }

      setHistoryItems((prev) => {
        let idx = findMessageIndexInHistory(prev, msg);
        let merged = msg;
        if (idx < 0) {
          const optIdx = findOptimisticMatchIndex(prev, msg);
          if (optIdx >= 0) {
            idx = optIdx;
            merged = { ...msg, clientId: prev[optIdx].clientId };
          }
        }
        const next = idx >= 0 ? [...prev] : [...prev, merged];
        if (idx >= 0) {
          next[idx] = merged;
        }
        next.sort((a, b) => {
          const ta = new Date(a.createdAt || 0).getTime();
          const tb = new Date(b.createdAt || 0).getTime();
          return ta - tb;
        });
        return next;
      });

      // A message landing in the OPEN conversation of a visible window is read on
      // arrival: receipt it right away and (Chats) move the caller's read watermark, so
      // the row pip / nav bubble never count something already on screen. A hidden
      // window skips — unread must survive until the reader actually returns.
      if (
        !messageAlreadyPresent &&
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        const readerId = getCurrentUserIdForReadReceipts();
        if (readerId && messageShouldMarkRead(msg, readerId)) {
          // Pass the thread so the server can skip resolving it from the message id.
          sendReadReceipt(String(msg?.id || "").trim(), msgThreadId);
          if (scopeRef.current === "internal") {
            markThreadReadLocally(msgThreadId);
            markThreadRead({ threadId: msgThreadId }).catch(() => {});
          } else if (isWaGroupMessage(msg)) {
            // Group message read on arrival: optimistically keep the Groups lens badge clear
            // for the on-screen thread and move the server watermark — the WS receipt above
            // only writes per-message readBy, never thread_reads, so without this the next
            // summary refresh would flip the open thread back to unread (best effort).
            markWaGroupThreadReadLocally(msgThreadId);
            markThreadRead({ threadId: msgThreadId }).catch(() => {});
          }
        }
      }

      const senderId = String(msg?.sender?.userId || "").trim();
      if (
        !messageAlreadyPresent &&
        senderId &&
        !isStandaloneSystemLineMessage(msg) &&
        userDisplayNamesByIdRef.current[senderId] === undefined
      ) {
        if (isSenderBotMessage(msg)) {
          setUserDisplayNamesById((prev) => {
            if (prev[senderId] !== undefined) return prev;
            return { ...prev, [senderId]: formatBotDisplayLabel(senderId) };
          });
        } else {
          fetchUserDisplayProfileEntry(senderId).then(([id, profile]) => {
            const nextLabel = String(profile?.label || "").trim() || id;
            const nextAvatar = String(profile?.avatar || "").trim();
            setUserDisplayNamesById((prev) => {
              if (prev[id] !== undefined) return prev;
              return { ...prev, [id]: nextLabel };
            });
            setUserAvatarUrlsById((prev) => {
              if (prev[id] !== undefined) return prev;
              return { ...prev, [id]: nextAvatar };
            });
          });
        }
      }

      scheduleThreadsRefreshRef.current();
    });
  }, []);

  useEffect(() => {
    const syncNotifyContext = () => {
      setChatNotificationMessagesRouteState({
        isActive: true,
        selectedThreadId,
      });
    };
    syncNotifyContext();
    return () => {
      setChatNotificationMessagesRouteState({ isActive: false, selectedThreadId: null });
    };
  }, [selectedThreadId]);

  const scrollToBottom = useCallback(() => {
    const el = chatScrollAreaRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  /**
   * Snap the chat viewport to the very bottom, hiding a small near-bottom increment behind a GPU
   * FLIP slide so the newest content lands in view smoothly. Shared by the on-new-message effect
   * and the settle observer so late-growing content (an image finishing its load) still ends up
   * fully at the bottom. `utils.remove` inside flipSlide cancels any in-flight slide, so calling
   * this twice for the same growth is safe (the second call sees delta ~0 and no-ops).
   */
  const stickChatToBottom = useCallback(() => {
    const el = chatScrollAreaRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const delta = maxScroll - el.scrollTop;
    el.scrollTop = el.scrollHeight;
    chatWasAtBottomRef.current = true;
    if (!prefersReducedMotion() && delta > 1 && delta <= el.clientHeight) {
      flipSlide(chatContentRef.current, delta, 320, "outCubic");
    }
  }, []);

  /** Jump to a message by id and briefly ring-highlight it. Returns false if it is not in the DOM. */
  const scrollToMessage = useCallback((messageId) => {
    const id = String(messageId || "").trim();
    const viewport = chatScrollAreaRef.current;
    if (!id || !viewport) {
      return false;
    }
    const node = viewport.querySelector(`[data-msg-id="${CSS.escape(id)}"]`);
    if (!node) {
      return false;
    }
    node.scrollIntoView({ block: "center", behavior: "auto" });
    // Claim the viewport. Marking the reader as no longer bottom-parked here is deliberate: the
    // scroll event that would do it is async, and until it lands the stick-to-bottom observer
    // would treat the still-growing history as "new content below a bottom-parked reader" and
    // scroll away from the message we just revealed.
    chatWasAtBottomRef.current = false;
    jumpTargetIdRef.current = id;
    jumpSettleUntilRef.current = Date.now() + JUMP_SETTLE_MS;
    setHighlightedMessageId(id);
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
    }
    highlightClearTimerRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
      highlightClearTimerRef.current = null;
    }, 2500);
    return true;
  }, []);

  // Track the reader's at-bottom state from the chat viewport's scroll events (programmatic
  // snaps fire scroll too, keeping it consistent). Re-seeded on attach — but an Activity
  // thaw needs care: the hidden pane kept RENDERING incoming messages while its effects
  // (including the auto-scroll) were unmounted, so on re-show the preserved offset can sit
  // above content that grew underneath. A reader who was at the bottom before the pane hid
  // is put back at the (new) bottom BEFORE re-seeding — measuring first would misread them
  // as "scrolled up" and disable stick-to-bottom for every later message. And never measure
  // a display:none viewport (clientHeight 0 reads as a huge gap and poisons the flag).
  useEffect(() => {
    const el = chatScrollAreaRef.current;
    if (!el) return undefined;
    const measure = () => {
      chatWasAtBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight <= chatNearBottomPx(el);
    };
    if (el.clientHeight > 0) {
      if (chatWasAtBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
      measure();
    }
    // Any real scroll gesture ends a jump's ownership of the viewport immediately — the reader has
    // taken over, and re-anchoring under them would feel like the chat refusing to move.
    const releaseJump = () => {
      jumpSettleUntilRef.current = 0;
      jumpTargetIdRef.current = "";
    };
    el.addEventListener("scroll", measure, { passive: true });
    el.addEventListener("wheel", releaseJump, { passive: true });
    el.addEventListener("touchstart", releaseJump, { passive: true });
    return () => {
      el.removeEventListener("scroll", measure);
      el.removeEventListener("wheel", releaseJump);
      el.removeEventListener("touchstart", releaseJump);
    };
  }, [selectedThreadId]);

  // Window-return restore: if this window was hidden while messages arrived, the reader
  // who was parked at the bottom lands back at the (new) bottom the moment the window is
  // visible again — belt-and-braces over the synchronous hidden-path scroll above.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!chatWasAtBottomRef.current) return;
      const el = chatScrollAreaRef.current;
      if (el && el.clientHeight > 0) {
        el.scrollTop = el.scrollHeight;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Pin the viewport when older history is prepended so the read position doesn't jump. Runs
  // before paint (layout effect) to avoid a flash; the bottom-auto-scroll effect below skips the
  // same update via skipNextAutoScrollRef.
  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current;
    if (!anchor) return;
    prependAnchorRef.current = null;
    const el = chatScrollAreaRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight - anchor.prevScrollHeight + anchor.prevScrollTop;
    }
    // `siblingHistories` too: in the merged view a "Load older" can prepend messages from another
    // clinic only, which grows the stream without touching `historyItems`.
  }, [historyItems, siblingHistories]);

  useEffect(() => {
    if (historyLoading || !selectedThreadId) {
      return;
    }
    // A load-older prepend just ran: the layout effect anchored the viewport — don't yank to bottom.
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }
    // A different thread just opened: always land at the bottom. Within the same thread (new
    // messages, silent reloads, Activity re-shows) only stick for a reader already at the bottom.
    const threadChanged = chatSnapThreadIdRef.current !== selectedThreadId;
    chatSnapThreadIdRef.current = selectedThreadId;
    const applyScroll = () => {
      // A pending jump takes priority once its bubble has rendered; otherwise (and for ordinary
      // new messages) stick to the bottom.
      const pendingId = pendingScrollMessageIdRef.current;
      if (pendingId) {
        // Crucially this RETURNS even when the bubble is not there yet. A jump into unloaded
        // history renders at least once before its target exists (the fetch is still in flight),
        // and falling through to the bottom-snap on that pass parks the reader at the newest
        // message — which is exactly what "it didn't scroll to the message" looks like.
        if (scrollToMessage(pendingId)) pendingScrollMessageIdRef.current = null;
        return;
      }
      if (!threadChanged && !chatWasAtBottomRef.current) return;
      // Snap to the bottom instantly (no main-thread scroll animation), hiding a small near-bottom
      // increment behind a GPU transform slide so the chat itself feels smooth, not jerky.
      stickChatToBottom();
    };
    // Animation frames never fire in a HIDDEN window (backgrounded/occluded Electron
    // window, background browser tab) — deferring through rAF there parks the scroll
    // forever and the receiver's chat sits above the newest messages. Scrolling a hidden
    // window's DOM works fine without frames, so apply synchronously in that case; the
    // window is then already at the bottom whenever it becomes visible again.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      applyScroll();
    } else {
      requestAnimationFrame(applyScroll);
    }
    // `siblingHistories` for the merged view: another clinic's messages change the stream without
    // touching `historyItems` — this effect must still run, both to consume skipNextAutoScrollRef
    // after a sibling-only prepend and to stick a bottom-parked reader to a new merged message.
  }, [historyItems, siblingHistories, historyLoading, selectedThreadId, scrollToMessage, stickChatToBottom]);

  // Content that finishes laying out AFTER the snap above — most commonly an inbound image, which
  // reserves ~no height until it loads — would push the newest message back off the bottom, so the
  // reader never actually sees it. A ResizeObserver keeps the viewport pinned while the reader is at
  // the bottom, so late-growing content still lands fully in view. Transform-only work (the FLIP /
  // send-pop slides) doesn't change the border box, so it never triggers this; a genuine height
  // change does. Re-attached per thread since the viewport/content nodes are stable within a thread.
  useEffect(() => {
    const content = chatContentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      // A jump that just landed owns the viewport: the growth being reported here is the rest of
      // its conversation laying out, which drifts the target off screen. Re-anchor on the target
      // instead of reading the growth as "new content arrived below the reader".
      if (Date.now() < jumpSettleUntilRef.current) {
        const viewport = chatScrollAreaRef.current;
        const targetId = jumpTargetIdRef.current;
        if (viewport && targetId) {
          const node = viewport.querySelector(`[data-msg-id="${CSS.escape(targetId)}"]`);
          // scrollIntoView changes no box size, so this cannot feed the observer back into itself.
          if (node) node.scrollIntoView({ block: "center", behavior: "auto" });
        }
        return;
      }
      // Never yank a reader who scrolled up into history; never fight the send-fly animation (it
      // owns the transform and does its own snap). Only correct a genuine shortfall from the bottom.
      if (!chatWasAtBottomRef.current || flyAnimatingRef.current) return;
      const el = chatScrollAreaRef.current;
      if (!el) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 1) {
        stickChatToBottom();
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [selectedThreadId, stickChatToBottom]);

  // WhatsApp-style send: the sent bubble POPS in at its spot (scale up from the send corner; its
  // drop-shadow scales with the transform, so it "lifts off" the background — detached look), while
  // the existing content slides up smoothly via the GPU FLIP. A layout effect so the bubble starts
  // scaled-down before first paint (no flash at full size); reduced-motion skips the pop.
  useLayoutEffect(() => {
    const fly = pendingFlyRef.current;
    if (!fly) return;
    pendingFlyRef.current = null;
    if (prefersReducedMotion()) return;
    const { clientId } = fly;
    const viewport = chatScrollAreaRef.current;
    if (!viewport) return;
    // Snap to the bottom INSTANTLY (no main-thread scroll animation — that's the jank/delay); the
    // content's upward slide is recreated below as a GPU transform (flipSlide), so the snap is unseen.
    const prevScrollTop = viewport.scrollTop;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;
    viewport.scrollTop = maxScroll;
    const delta = maxScroll - prevScrollTop;
    const part = viewport.querySelector(`[data-optimistic-id="${CSS.escape(clientId)}"]`);
    if (!part) return;
    const article = part.parentElement;
    const lonePart = article ? article.children.length === 1 : false;
    // Pop the whole bubble box for a lone message (its shadow scales with it); a merged segment just
    // pops its own text.
    const target = (lonePart && part.closest("article")) || part;

    skipNextAutoScrollRef.current = true;
    // Hold off the settle observer while this animation owns the transform, so a resize mid-pop
    // (e.g. the echo reconciling) can't cancel the slide with a competing flipSlide.
    flyAnimatingRef.current = true;
    flipSlide(chatContentRef.current, delta, OPTIMISTIC_POP_DURATION_MS, "outCubic");

    // Pre-set the scaled-down start before paint so it never flashes at full size.
    target.style.transformOrigin = "bottom right";
    target.style.willChange = "transform, opacity";
    target.style.transform = "scale(0.75)";
    target.style.opacity = "0";
    animate(target, {
      scale: [0.75, 1],
      opacity: [0, 1],
      duration: OPTIMISTIC_POP_DURATION_MS,
      ease: "outBack",
      onComplete: () => {
        target.style.transform = "";
        target.style.opacity = "";
        target.style.transformOrigin = "";
        target.style.willChange = "";
        flyAnimatingRef.current = false;
      },
    });
  }, [historyItems]);

  // Switching threads while a FLIP slide is mid-flight would otherwise leave the new thread's
  // content offset — reset the transform on thread change. Also clear the fly guard so a pop
  // interrupted by the switch can't leave the settle observer muted in the new thread.
  useEffect(() => {
    flyAnimatingRef.current = false;
    const content = chatContentRef.current;
    if (content) {
      utils.remove(content);
      content.style.transform = "";
      content.style.willChange = "";
    }
  }, [selectedThreadId]);

  useEffect(() => () => {
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
    }
    pendingOptimisticRef.current.forEach((entry) => {
      if (entry?.timer) clearTimeout(entry.timer);
    });
    pendingOptimisticRef.current.clear();
  }, []);

  // Thaw sweep + refresh, on every show (empty deps: an Activity re-show re-runs all effects;
  // the very first mount no-ops on the empty selection). Hiding the pane ran the cleanup above
  // (fail timers cleared, pending map emptied) and tore down the WS subscription, so an echo
  // that landed while hidden was missed and a placeholder could be stranded as "sending".
  // Order matters: (1) mark placeholders with no live pending entry failed, (2) silently reload
  // the newest page + the open thread's checklist, (3) once the reload merged, drop the swept
  // placeholders whose real echo came back (same sender + body — findOptimisticMatchIndex's
  // bridge), so a send-then-instant-switch resolves without duplicating or sticking.
  useEffect(() => {
    const tid = String(selectedThreadIdRef.current || "").trim();
    if (!tid) return;
    const strandedIds = new Set();
    for (const m of historyItemsRef.current) {
      if (m?.metadata?.optimistic !== true || m?.metadata?.optimisticFailed === true) continue;
      const clientId = String(m?.clientId || "");
      if (clientId && !pendingOptimisticRef.current.has(clientId)) strandedIds.add(clientId);
    }
    if (strandedIds.size) {
      setHistoryItems((prev) =>
        prev.map((m) =>
          m?.metadata?.optimistic === true && strandedIds.has(String(m?.clientId || ""))
            ? { ...m, metadata: { ...m.metadata, optimisticFailed: true } }
            : m,
        ),
      );
    }
    void reloadActiveThreadHistorySilently(tid).then(() => {
      if (!strandedIds.size) return;
      if (String(selectedThreadIdRef.current || "").trim() !== tid) return;
      setHistoryItems((prev) => {
        // The echo of a swept send must be a settled message NO OLDER than the placeholder
        // (minus a minute of client/server clock-skew slack) — an ancient identical reply
        // ("Ok", "Thanks") in loaded history is not its echo, and dropping a failed-marked
        // send against one would silently lose the message.
        const settled = (placeholder) => {
          const sentAtMs = new Date(placeholder?.createdAt || 0).getTime() - 60000;
          return prev.some(
            (m) =>
              m !== placeholder &&
              m?.metadata?.optimistic !== true &&
              new Date(m?.createdAt || 0).getTime() >= sentAtMs &&
              String(m?.sender?.userId || "").trim() ===
                String(placeholder?.sender?.userId || "").trim() &&
              parseMessageBodyText(m?.body) === parseMessageBodyText(placeholder?.body),
          );
        };
        // Only ever drop rows that are STILL optimistic placeholders: a live echo that arrived
        // between the sweep and this merge may have inherited the swept clientId (the in-place
        // reconciliation preserves it) — that row is the real message and must stay.
        const next = prev.filter(
          (m) =>
            !(
              m?.metadata?.optimistic === true &&
              strandedIds.has(String(m?.clientId || "")) &&
              settled(m)
            ),
        );
        return next.length === prev.length ? prev : next;
      });
    });
    if (!scopedListRef.current) loadThreadTasksRef.current(tid);
  }, []);

  /** Open a thread and reveal a message in response to a clicked notification. */
  const openToTarget = useCallback((target) => {
    const threadId = String(target?.threadId || "").trim();
    if (!threadId) {
      return;
    }
    const messageId = String(target?.messageId || "").trim();
    // Beat the one-time persisted-thread restore that also runs on mount.
    restoredSelectedThreadRef.current = true;
    pendingScrollMessageIdRef.current = messageId || null;
    if (String(selectedThreadIdRef.current || "").trim() !== threadId) {
      // Loading history will trigger the scroll-after-load effect above.
      handleThreadClickRef.current(threadId);
      return;
    }
    // Thread already open (history present): reveal directly — the effect won't re-fire.
    if (messageId) {
      requestAnimationFrame(() => {
        if (scrollToMessage(messageId)) {
          pendingScrollMessageIdRef.current = null;
        }
      });
    }
  }, [scrollToMessage]);

  // Drain a request that fired before this page mounted (e.g. clicked from another page).
  // Targets carry a scope ('normal' = patient Messages, 'internal' = Chats): each pane
  // consumes ONLY its own scope's targets — the pending target for the other pane stays
  // buffered so that pane drains it on its next mount/show.
  useEffect(() => {
    const target = consumePendingOpenThreadMessage(openTargetScope);
    if (target) {
      openToTarget(target);
    }
  }, [openToTarget, openTargetScope]);

  // Handle requests while already mounted; consume the stored pending so a later
  // remount of this page does not replay a stale jump. Foreign-scope targets are left
  // untouched for the owning pane.
  useEffect(() => {
    return subscribeOpenThreadMessage((target) => {
      if ((target?.scope || "normal") !== openTargetScope) return;
      consumePendingOpenThreadMessage(openTargetScope);
      openToTarget(target);
    });
  }, [openToTarget, openTargetScope]);

  useEffect(() => {
    // const viewport = chatScrollAreaRef.current;
    // if (!viewport || !selectedThreadId) return;
    // const content = viewport.firstElementChild;
    // if (!content) return;
    // const ro = new ResizeObserver(scrollToBottom);
    // ro.observe(content);
    // return () => ro.disconnect();
  }, [selectedThreadId, scrollToBottom]);

  // True when a server-side search is active (normal scope, non-empty box). While searching, the
  // displayed list is the server's search results; otherwise it's the windowed/paginated threads.
  const searching = !isScopedList && threadSearchQuery.trim() !== "";

  const sortedThreads = useMemo(() => {
    const source = searching ? searchResults : threads;
    // Outside search (normal scope only), fold in the always-loaded default assistant thread
    // (deduped) so the window never drops it; it then sorts into its natural position among the
    // others, same as any thread. Never in internal Chats, which has no default thread.
    let base = source;
    if (!searching && !isScopedList && defaultThreads.length) {
      const ids = new Set((source || []).map((t) => getThreadRowId(t)));
      const extra = defaultThreads.filter((t) => !ids.has(getThreadRowId(t)));
      if (extra.length) base = [...(source || []), ...extra];
    }
    if (!base?.length) return [];
    return [...base].sort(byLastMessageDesc);
  }, [searching, searchResults, threads, defaultThreads, scope]);

  // Chats rows currently showing a live "typing…" state (someone other than me typed in
  // the last few seconds). Re-derived on every typing-store tick; null outside Chats.
  const typingThreadIds = useMemo(() => {
    if (!isInternalScope) return null;
    void typingTick; // dependency: re-derive when the store changes/expires
    const meId = String(getAuthUser()?.id || "").trim();
    const out = new Set();
    for (const t of threads) {
      const tid = getThreadRowId(t);
      if (!tid) continue;
      if (getTypingUserIds(tid).some((id) => id && id !== meId)) out.add(tid);
    }
    return out;
  }, [isInternalScope, typingTick, threads]);

  // Who is typing in the OPEN chat (ids; the page resolves display names).
  const headerTypingUserIds = useMemo(() => {
    if (!isInternalScope || !selectedThreadId) return EMPTY_ARRAY;
    void typingTick;
    const meId = String(getAuthUser()?.id || "").trim();
    const ids = getTypingUserIds(selectedThreadId).filter((id) => id && id !== meId);
    return ids.length ? ids : EMPTY_ARRAY;
  }, [isInternalScope, selectedThreadId, typingTick]);

  // Predicate for the Messages filter bar (locked, staff-handles, assigned, WhatsApp,
  // awaiting-reply, date range), shared by the page list and the server-fed attention lists so
  // active filters narrow the sections exactly like they narrow "All threads". Null when no
  // filter is active (callers skip filtering entirely).
  const activeThreadFilterPredicate = useMemo(() => {
    // DISABLED (for now): staff_handles + assignee filters are hidden, so they no
    // longer participate here.
    // The Groups lens hides the channel pills and neutralizes them in the window fetch
    // (see threadListWindowFilters) — every group row is `whatsapp_group`, so a persisted
    // pill would contradict the scope and wipe the lens's rows (search results included).
    // Mirror that neutralization here rather than trusting the pills to be untoggled.
    const waGroupsLens = threadLens === WA_GROUPS_LENS;
    const whatsappOnly = waGroupsLens ? false : threadFilterWhatsappOnly;
    const appOnly = waGroupsLens ? false : threadFilterAppOnly;
    const noFilter =
      !threadFilterLockedOnly &&
      !threadFilterAwaitingReplyOnly &&
      !whatsappOnly &&
      !appOnly &&
      !threadFilterWhatsappBotId &&
      !threadFilterLastMessageRange?.from;
    if (noFilter) return null;
    return (thread) => {
      const tid = getThreadRowId(thread);
      const flags = new Set(getThreadFlagStrings(thread));
      const membership = threadMemberIdsByThreadId[tid];
      if (threadFilterLockedOnly && !flags.has("locked")) return false;
      // if (threadFilterStaffHandlesOnly && !flags.has("staff_handles")) return false;
      if (
        whatsappOnly &&
        String(thread?.metadata?.conversationType || "").toLowerCase() !== "whatsapp"
      ) {
        return false;
      }
      if (
        appOnly &&
        String(thread?.metadata?.conversationType || "").toLowerCase() !== "app"
      ) {
        return false;
      }
      if (threadFilterAwaitingReplyOnly) {
        if (getWhatsAppInboxWaitingMinutes(thread, membership ?? null, listClockMs) == null) {
          return false;
        }
      }
      // if (threadFilterAssignedToMe && !isAssignedToUser(thread, me)) return false;
      // if (threadFilterAssigneeId && getThreadAssignee(thread)?.id !== threadFilterAssigneeId) {
      //   return false;
      // }
      if (threadFilterWhatsappBotId && String(thread?.whatsappBotId || "") !== threadFilterWhatsappBotId) {
        return false;
      }
      if (
        threadFilterLastMessageRange?.from &&
        !threadLastMessageInRange(thread, threadFilterLastMessageRange)
      ) {
        return false;
      }
      return true;
    };
  }, [
    threadLens,
    threadFilterLockedOnly,
    threadFilterAwaitingReplyOnly,
    threadFilterWhatsappOnly,
    threadFilterAppOnly,
    threadFilterWhatsappBotId,
    threadFilterLastMessageRange,
    threadMemberIdsByThreadId,
    listClockMs,
  ]);

  // Ids of the default assistant thread(s), exempt from the filter bar so the thread stays visible
  // whatever filter is active (it is still sorted into place by `sortedThreads`).
  const defaultThreadIdSet = useMemo(
    () => new Set(defaultThreads.map((t) => getThreadRowId(t))),
    [defaultThreads],
  );

  const filteredSortedThreads = useMemo(() => {
    // Chats has no filter bar; Messages' persisted filters must never apply to internal chats.
    // Free-text search is server-side, so there is no client text filter here — `sortedThreads`
    // already holds the search results when searching.
    if (isScopedList) return sortedThreads;
    if (!activeThreadFilterPredicate) return sortedThreads;
    return sortedThreads.filter(
      (t) => defaultThreadIdSet.has(getThreadRowId(t)) || activeThreadFilterPredicate(t),
    );
  }, [scope, sortedThreads, activeThreadFilterPredicate, defaultThreadIdSet]);

  // Attention lists with the active filters applied, ready for the section renderer. Empty in
  // internal scope (Chats has no attention sections).
  const filteredAttentionWaiting = useMemo(() => {
    if (isScopedList) return EMPTY_ATTENTION_LIST;
    if (!activeThreadFilterPredicate) return attentionWaiting;
    return attentionWaiting.filter(activeThreadFilterPredicate);
  }, [scope, attentionWaiting, activeThreadFilterPredicate]);

  const filteredAttentionPatientReply = useMemo(() => {
    if (isScopedList) return EMPTY_ATTENTION_LIST;
    const source = staffReactedPatientReplyMessageIds.size
      ? attentionPatientReply.filter(
          (t) => !staffReactedPatientReplyMessageIds.has(String(t?.lastMessageId || "").trim()),
        )
      : attentionPatientReply;
    if (!activeThreadFilterPredicate) return source;
    return source.filter(activeThreadFilterPredicate);
  }, [scope, attentionPatientReply, activeThreadFilterPredicate, staffReactedPatientReplyMessageIds]);

  const filteredAttentionBotAck = useMemo(() => {
    if (isScopedList) return EMPTY_ATTENTION_LIST;
    if (!activeThreadFilterPredicate) return attentionBotAck;
    return attentionBotAck.filter(activeThreadFilterPredicate);
  }, [scope, attentionBotAck, activeThreadFilterPredicate]);

  // "Needs you" lens badge: the complete triage piles — every waiting thread plus the
  // patient-reply and bot-ack rows past the threshold (deduped against waiting). Complete because
  // all three lists come from the overview endpoint regardless of the page/window.
  const needsCount = useMemo(() => {
    if (isScopedList) return 0;
    const waitingIds = new Set(filteredAttentionWaiting.map((t) => getThreadRowId(t)));
    let count = filteredAttentionWaiting.length;
    for (const t of filteredAttentionPatientReply) {
      if (waitingIds.has(getThreadRowId(t))) continue;
      if (
        getPatientReplyWaitingMinutes(
          t,
          patientReplyThresholdMinutes,
          patientReplyMaxHours,
          listClockMs,
        ) != null
      ) {
        count += 1;
      }
    }
    for (const t of filteredAttentionBotAck) {
      if (waitingIds.has(getThreadRowId(t))) continue;
      if (getBotAckWaitingMinutes(t, botAckThresholdMinutes, botAckMaxHours, listClockMs) != null) {
        count += 1;
      }
    }
    return count;
  }, [
    scope,
    filteredAttentionWaiting,
    filteredAttentionPatientReply,
    filteredAttentionBotAck,
    botAckThresholdMinutes,
    botAckMaxHours,
    patientReplyThresholdMinutes,
    patientReplyMaxHours,
    listClockMs,
  ]);

  // DISABLED (for now): the "Mine" lens is hidden with thread assignment —
  // assignment lives on tasks only.
  // // "Mine" lens badge: threads assigned to me among the LOADED rows (the windowed page +
  // // attention lists) — a small mono number, not a global count.
  // const mineCount = useMemo(() => {
  //   if (scope === "internal") return 0;
  //   const me = String(getAuthUser()?.id || "").trim();
  //   if (!me) return 0;
  //   const ids = new Set();
  //   for (const t of sortedThreads) {
  //     if (isAssignedToUser(t, me)) ids.add(getThreadRowId(t));
  //   }
  //   for (const t of filteredAttentionWaiting) {
  //     if (isAssignedToUser(t, me)) ids.add(getThreadRowId(t));
  //   }
  //   return ids.size;
  // }, [scope, sortedThreads, filteredAttentionWaiting]);

  // The list clock only drives minute-granularity freshness (waiting-minute chips, effective
  // status, "pending until" labels). A 30s tick is plenty for that and re-renders the whole list
  // 6x less often than the old 5s tick did while idle.
  useEffect(() => {
    setListClockMs(Date.now());
    const id = setInterval(() => setListClockMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Debounced server-side thread search (normal scope). Each settle issues one request guarded by
  // searchSeqRef so a slow earlier response can't clobber a newer one; clearing the box cancels any
  // in-flight request and drops back to the windowed list (kept intact in `threads`).
  useEffect(() => {
    if (isScopedList) return undefined;
    const q = threadSearchQuery.trim();
    if (!q) {
      searchSeqRef.current += 1;
      setSearchResults([]);
      setSearchLoading(false);
      setSearchTruncated(false);
      return undefined;
    }
    setSearchLoading(true);
    const seq = ++searchSeqRef.current;
    const handle = setTimeout(async () => {
      try {
        // One capped fetch — server recall is capped anyway, so there is no paging. `withTotal`
        // opts into the new response shape (total == returned, truncated when capped). On the
        // Groups lens the search must stay inside the groups scope — the default scope
        // excludes group threads, so an unscoped search could never find them (and would leak
        // 1:1 rows into the Groups tab).
        const response = await fetchThreads({
          search: q,
          scope: threadLens === WA_GROUPS_LENS ? "groups" : "",
          // Merge search hits too, so a group found by search reads as one row like it does in
          // the list. The server runs the selector as a separate pass, so a row surfaced by
          // matching one clinic still carries its complete sibling set.
          mergeGroups: threadLens === WA_GROUPS_LENS && mergedWaGroupsView && !threadFilterWhatsappBotId,
          limit: THREAD_SEARCH_LIMIT,
          withTotal: true,
        });
        if (searchSeqRef.current !== seq) return;
        const rows = (Array.isArray(response?.threads) ? response.threads : []).map((t) =>
          normalizeApiThreadRow(t, null),
        );
        setSearchResults(rows);
        // Trust the server's cap+1 probe alone — a result of exactly the limit is NOT truncated.
        setSearchTruncated(Boolean(response.page?.truncated));
      } catch {
        if (searchSeqRef.current !== seq) return;
        setSearchResults([]);
        setSearchTruncated(false);
      } finally {
        if (searchSeqRef.current === seq) setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
    // threadLens: switching lenses mid-search re-issues the query in the new scope.
  }, [threadSearchQuery, scope, threadLens, mergedWaGroupsView, threadFilterWhatsappBotId]);

  // NOTE: the per-thread WhatsApp membership prefetch (one GET …/members per listed thread) is
  // gone — section membership is decided by the server-side overview endpoint, and the wait
  // chips/labels work from row fields alone. loadAdminThreadMembers still records the OPEN
  // thread's membership for its richer wait tooltip.

  const getActiveThreadPhone = useCallback(() => {
    const directRows = threadMembersGrouped?.direct ?? [];
    if (directRows.length === 0) return "";
    const adminUser = directRows[0]?.adminUserSnapshot;
    if (!adminUser) return "";
    return primaryPhoneLineOrEmpty(adminUser);
  }, [threadMembersGrouped]);

  const loadNextArchivedPage = useCallback(async () => {
    if (archivedLoadingRef.current) return;
    const phone = getActiveThreadPhone();
    if (!phone) {
      setArchivedError("No phone number for active thread");
      return;
    }
    archivedLoadingRef.current = true;
    setArchivedLoading(true);
    setArchivedError("");
    try {
      const result = await fetchArchivedMessagesByPhone({
        phone,
        limit: 5,
        skip: archivedSkipRef.current,
      });
      const newMessages = Array.isArray(result.messages) ? result.messages : [];
      setArchivedMessages((prev) => [...newMessages.reverse(), ...prev]);
      const nextSkip = archivedSkipRef.current + newMessages.length;
      archivedSkipRef.current = nextSkip;
      setArchivedSkip(nextSkip);
      setArchivedTotal(result.count);
      if (nextSkip < result.count || newMessages.length === 0) {
        setArchivedAllLoaded(true);
      }
    } catch (e) {
      setArchivedError(e instanceof Error ? e.message : "Loading archive failed");
    } finally {
      archivedLoadingRef.current = false;
      setArchivedLoading(false);
    }
  }, [getActiveThreadPhone]);

  const triggerArchiveLoad = useCallback(() => {
    // archive:view gates the by-phone fetch server-side, so without it this can only 403.
    // Checked imperatively (not via a prop) so it also covers a revoke while the thread is
    // open and any future caller — mirroring work-plan-mc-store's poll guard.
    if (!hasPermission("archive", "view")) return;
    if (archiveTriggered) {
      // Already triggered — just scroll to top
      const el = chatScrollAreaRef.current;
      if (el) el.scrollTop = 0;
      return;
    }
    setArchiveTriggered(true);
    // Scroll to top so user sees the archived section appearing
    const el = chatScrollAreaRef.current;
    if (el) el.scrollTop = 0;
    void loadNextArchivedPage();
  }, [archiveTriggered, loadNextArchivedPage]);

  /** Reset archive state when switching threads */
  const resetArchivedState = useCallback(() => {
    setArchivedMessages([]);
    setArchivedLoading(false);
    setArchivedError("");
    setArchivedSkip(0);
    setArchivedTotal(0);
    setArchivedAllLoaded(false);
    setArchiveTriggered(false);
    archivedSkipRef.current = 0;
    archivedLoadingRef.current = false;
  }, []);

  // Resolve display names + avatars for the senders in `items`. mode "replace" scopes the maps to
  // exactly this set (opening a thread); "merge" adds new senders without dropping existing ones
  // (load-older and silent refresh).
  const resolveSenderProfiles = useCallback(async (items, mode) => {
    const humanUserIds = new Set();
    const botSenderIds = new Set();
    for (const item of items) {
      if (isStandaloneSystemLineMessage(item)) continue;
      const senderId = String(item?.sender?.userId || "").trim();
      if (!senderId) continue;
      if (isSenderBotMessage(item)) botSenderIds.add(senderId);
      else humanUserIds.add(senderId);
    }
    if (humanUserIds.size === 0 && botSenderIds.size === 0) {
      if (mode === "replace") {
        setUserDisplayNamesById({});
        setUserAvatarUrlsById({});
      }
      return;
    }
    // ONE request for every distinct sender — see the note on the other resolver above.
    const humanEntries = await fetchUserDisplayProfileEntries([...humanUserIds]);
    const botEntries = [...botSenderIds].map((id) => [id, formatBotDisplayLabel(id)]);
    const names = Object.fromEntries([
      ...humanEntries.map(([id, profile]) => [id, String(profile?.label || "").trim() || id]),
      ...botEntries,
    ]);
    const avatars = Object.fromEntries(
      humanEntries.map(([id, profile]) => [id, String(profile?.avatar || "").trim()]),
    );
    if (mode === "replace") {
      setUserDisplayNamesById(names);
      setUserAvatarUrlsById(avatars);
    } else {
      setUserDisplayNamesById((prev) => ({ ...names, ...prev }));
      setUserAvatarUrlsById((prev) => ({ ...avatars, ...prev }));
    }
  }, []);

  // Mark unread messages in `items` as read (best effort) and refresh the thread list so its
  // unread badge clears. Internal scope also moves the caller's thread_reads watermark —
  // this covers messages that arrive while the thread is open and visible.
  const sendHistoryReadReceipts = useCallback(async (items) => {
    const uid = getCurrentUserIdForReadReceipts();
    if (!uid || getChatConnectionStatus() !== "connected") return;
    const ids = items
      .filter((m) => messageShouldMarkRead(m, uid))
      .map((m) => String(m.id || "").trim())
      .filter(Boolean);
    if (!ids.length) return;
    await sendReadReceipts(ids, String(selectedThreadIdRef.current || "").trim());
    if (scopeRef.current === "internal") {
      const tid = String(selectedThreadIdRef.current || "").trim();
      if (tid) {
        markThreadReadLocally(tid);
        markThreadRead({ threadId: tid }).catch(() => {});
      }
    }
    loadThreadsRef.current();
  }, []);

  // Delivery acks for what this client picked up ITSELF. Messages arriving over a live socket are
  // stamped delivered server-side, but nothing is replayed to a reconnecting socket, so anything
  // that landed while we were offline is first seen here. Keyed on historyItems so it covers every
  // load path (initial, older-page, silent reload, newest-refresh) from one place; the queue's
  // dedupe makes the repeat sweeps free.
  useEffect(() => {
    const uid = getCurrentUserIdForReadReceipts();
    if (!uid) return;
    const tid = String(selectedThreadId || "").trim();
    for (const message of historyItems || []) {
      if (shouldAckDelivered(message, uid)) {
        queueDeliveredAck(String(message.id || "").trim(), tid);
      }
    }
  }, [historyItems, selectedThreadId]);

  // Live receipt deltas: merge the named users into the arrays the loaded messages already hold.
  // Deltas, not snapshots — so this appends and never removes, and a `resync` marker (emitted when
  // the server's per-thread buffer overflowed) refetches instead of guessing.
  useEffect(() => {
    return subscribeChatReceipts((detail) => {
      const tid = String(detail?.threadId || "").trim();
      if (!tid || tid !== String(selectedThreadIdRef.current || "").trim()) {
        return;
      }
      if (detail.resync) {
        void reloadActiveThreadHistorySilentlyRef.current?.(tid);
        return;
      }
      if (!detail.entries?.length) return;

      setHistoryItems((prev) => {
        const byId = new Map(detail.entries.map((entry) => [entry.messageId, entry]));
        let changed = false;
        const next = prev.map((message) => {
          const entry = byId.get(String(message?.id || "").trim());
          if (!entry) return message;

          const merged = { ...message };
          if (entry.deliveredBy.length) {
            merged.deliveredTo = mergeReceiptUserIds(message.deliveredTo, entry.deliveredBy, "deliveredAt", detail.at);
          }
          if (entry.readBy.length) {
            merged.readBy = mergeReceiptUserIds(message.readBy, entry.readBy, "readAt", detail.at);
          }
          if (entry.channelStatus) {
            merged.channelStatus = entry.channelStatus;
          }
          changed = true;
          return merged;
        });
        return changed ? next : prev;
      });
    });
  }, []);

  // Messages that arrived while this window was HIDDEN stay unread on purpose (badge
  // survives) — but the moment the reader comes back to a window with the chat already
  // open, what's on screen counts as read. Sweep the loaded history on visibility return;
  // sendHistoryReadReceipts no-ops when nothing needs marking.
  useEffect(() => {
    if (!isScopedList || typeof document === "undefined") return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!String(selectedThreadIdRef.current || "").trim()) return;
      void sendHistoryReadReceipts(historyItemsRef.current || []);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [scope, sendHistoryReadReceipts]);

  // Same sweep for an Activity-pane THAW (switching back to this page/profile): the
  // hidden pane rendered incoming messages with effects unmounted, so this mount-time
  // effect IS the "pane became visible" signal. First mount no-ops (empty history).
  useEffect(() => {
    if (!isScopedList || typeof document === "undefined") return undefined;
    if (document.visibilityState !== "visible") return undefined;
    if (!String(selectedThreadIdRef.current || "").trim()) return undefined;
    void sendHistoryReadReceipts(historyItemsRef.current || []);
    return undefined;
  }, [scope, sendHistoryReadReceipts]);

  // Cache `items` for `threadId` (LRU, capped at HISTORY_CACHE_MAX) so re-opening is instant.
  const writeHistoryCache = useCallback((threadId, items, allLoaded) => {
    const tid = String(threadId || "").trim();
    if (!tid) return;
    const cache = historyCacheRef.current;
    cache.delete(tid);
    cache.set(tid, { items, allLoaded: Boolean(allLoaded) });
    while (cache.size > HISTORY_CACHE_MAX) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
  }, []);

  // Silent refresh after a cache hit: pull the newest page and reconcile, catching messages that
  // arrived while this thread was in the background.
  const refreshActiveThreadNewest = useCallback(
    async (threadId) => {
      const tid = String(threadId || "").trim();
      if (!tid) return;
      try {
        const response = await fetchThreadHistory({
          threadId: tid,
          maxMessageCount: HISTORY_PAGE_SIZE,
          skip: 0,
        });
        if (selectedThreadIdRef.current !== tid) return;
        const fresh = (Array.isArray(response?.history) ? response.history : []).slice().reverse();
        const merged = mergeHistoryNewest(historyItemsRef.current, fresh);
        setHistoryItems(merged.items);
        const allLoaded = merged.gap ? fresh.length < HISTORY_PAGE_SIZE : historyAllOlderLoadedRef.current;
        if (merged.gap) setHistoryAllOlderLoaded(allLoaded);
        writeHistoryCache(tid, merged.items, allLoaded);
        await resolveSenderProfiles(fresh, "merge");
        await sendHistoryReadReceipts(fresh);
      } catch {
        /* ignore — the cached view is still usable */
      }
    },
    [resolveSenderProfiles, sendHistoryReadReceipts, writeHistoryCache],
  );

  // Load the next older page (scroll-up). Skips by the count already loaded (self-correcting for
  // new messages appended at the bottom), de-dupes by id, and anchors the viewport so the read
  // position doesn't jump.
  //
  // In the merged WhatsApp view every clinic in the stream pages back together — otherwise only
  // the open thread would walk backwards and the other clinics would stay stuck at their newest
  // page, which reads as "this clinic's past conversation is missing". All pages are fetched in
  // parallel and applied in ONE batch: applying them as they resolve would shift the viewport
  // once per clinic.
  const loadOlderHistory = useCallback(async () => {
    const tid = String(selectedThreadIdRef.current || "").trim();
    if (!tid) return;
    if (historyLoadingOlderRef.current) return;
    const openDone = historyAllOlderLoadedRef.current;
    const pendingSiblingIds = mergedActiveRef.current
      ? mergedSiblingIdsRef.current.filter((id) => !siblingAllLoadedRef.current.get(id))
      : [];
    if (openDone && pendingSiblingIds.length === 0) return;
    historyLoadingOlderRef.current = true;
    setHistoryLoadingOlder(true);
    try {
      // A failed page resolves to null so one clinic's error can't abort the others.
      const olderPage = (threadId, skip) =>
        fetchThreadHistory({ threadId, maxMessageCount: HISTORY_PAGE_SIZE, skip })
          .then((response) => (Array.isArray(response?.history) ? response.history : []))
          .catch(() => null);

      const [openPage, siblingPages] = await Promise.all([
        openDone ? Promise.resolve(null) : olderPage(tid, historyItemsRef.current.length),
        Promise.all(
          pendingSiblingIds.map((id) =>
            olderPage(id, (siblingHistoriesRef.current.get(id) || []).length).then((page) => ({ id, page })),
          ),
        ),
      ]);
      if (selectedThreadIdRef.current !== tid) return;

      // Resolve every stream's additions BEFORE touching state, so the anchor is measured once
      // against the pre-prepend layout.
      const newerFirstToAdditions = (page, current) => {
        const olderAsc = page.slice().reverse();
        const existingIds = new Set(current.map((m) => String(m?.id || "")));
        return olderAsc.filter((m) => !existingIds.has(String(m?.id || "")));
      };
      const openAdditions = openPage ? newerFirstToAdditions(openPage, historyItemsRef.current) : [];
      const openReachedStart = openPage ? openPage.length < HISTORY_PAGE_SIZE : openDone;
      const siblingUpdates = siblingPages
        .filter(({ page }) => page !== null)
        .map(({ id, page }) => {
          const current = siblingHistoriesRef.current.get(id) || [];
          const additions = newerFirstToAdditions(page, current);
          return {
            id,
            additions,
            items: [...additions, ...current],
            reachedStart: page.length < HISTORY_PAGE_SIZE,
          };
        });

      const hasAdditions =
        openAdditions.length > 0 || siblingUpdates.some((u) => u.additions.length > 0);
      if (hasAdditions) {
        const el = chatScrollAreaRef.current;
        if (el) prependAnchorRef.current = { prevScrollHeight: el.scrollHeight, prevScrollTop: el.scrollTop };
        skipNextAutoScrollRef.current = true;
      }

      if (openAdditions.length) {
        const nextItems = [...openAdditions, ...historyItemsRef.current];
        setHistoryItems(nextItems);
        writeHistoryCache(tid, nextItems, openReachedStart);
        void resolveSenderProfiles(openAdditions, "merge");
      }
      if (openReachedStart) setHistoryAllOlderLoaded(true);

      if (siblingUpdates.length) {
        setSiblingHistories((prev) => {
          const next = new Map(prev);
          for (const u of siblingUpdates) {
            if (u.additions.length) next.set(u.id, u.items);
          }
          return next;
        });
        setSiblingAllLoaded((prev) => {
          const next = new Map(prev);
          for (const u of siblingUpdates) next.set(u.id, u.reachedStart);
          return next;
        });
        for (const u of siblingUpdates) {
          if (!u.additions.length) continue;
          writeHistoryCache(u.id, u.items, u.reachedStart);
          void resolveSenderProfiles(u.additions, "merge");
        }
      }
    } catch {
      /* keep the button; the user can retry */
    } finally {
      historyLoadingOlderRef.current = false;
      setHistoryLoadingOlder(false);
    }
  }, [resolveSenderProfiles, writeHistoryCache]);

  // Compute where the "New messages" divider belongs: the first history item the caller
  // hasn't read (own messages never count). Uses the per-message readBy receipts, so a
  // fully-read thread yields null and no divider renders.
  const computeFirstUnreadMessageId = (items) => {
    const uid = getCurrentUserIdForReadReceipts();
    if (!uid) return null;
    const first = (Array.isArray(items) ? items : []).find((m) => messageShouldMarkRead(m, uid));
    const id = first ? String(first.id || "").trim() : "";
    return id || null;
  };

  const handleThreadClick = (threadId, { restoredFromSession = false } = {}) => {
    resetArchivedState();
    const tid = String(threadId || "").trim();
    // Any user-initiated open supersedes the restore — from then on 403s toast normally.
    sessionRestoredThreadIdRef.current = restoredFromSession ? tid : null;
    setSelectedThreadId(threadId);
    setNewMessagesDividerId(null);
    // Drop an off-list header row fetched for a DIFFERENT thread so a later return to it
    // refetches fresh data (re-clicking the same thread keeps the warm row).
    setSelectedThreadOffListRow((prev) =>
      prev && getThreadRowId(prev) === tid ? prev : null,
    );
    setHistoryError("");
    setLocalUnreadByThreadId((prev) => {
      if (!tid || !prev[tid]) return prev;
      const next = { ...prev };
      delete next[tid];
      return next;
    });
    // Internal Chats: opening a thread is reading it — zero the row + sidebar bubble
    // optimistically and move the server-side watermark (best effort).
    if (scopeRef.current === "internal" && tid) {
      updateThreadRowEverywhere(tid, (t) => ({ ...t, unreadCount: 0 }));
      markThreadReadLocally(tid);
      markThreadRead({ threadId: tid }).catch(() => {});
    } else if (tid) {
      // WhatsApp group unread (the Groups lens badge) clears the same way. Safe for every
      // Messages thread: the store only ever holds scope=groups ids, so a 1:1 open no-ops.
      const row = findKnownThreadRow(tid);
      const isGroup = isWaGroupThread(row);
      // A merged row stands for N sibling threads, one per clinic gateway, all holding the same
      // conversation. The reader has seen it, so every copy is read — otherwise the other
      // clinics' unread badges would resurface the moment someone un-merges or picks a chip.
      // mergedGroupThreadIds returns exactly [tid] when unmerged, so the old behaviour is
      // byte-identical.
      const siblingIds = isGroup ? mergedGroupThreadIds(row) : [tid];
      for (const id of siblingIds) markWaGroupThreadReadLocally(id);
      // Group unread is watermark-based server-side (thread_reads), and the history read
      // receipts below never move the watermark outside internal scope — so opening a
      // group must move it here, mirroring the internal branch above (best effort).
      if (isGroup) {
        // Only the siblings that actually carry unread: seven gateways would otherwise mean
        // seven POSTs on every single row open.
        const unreadIds = mergedGroupThreadIds(row, { unreadOnly: true });
        for (const id of unreadIds.length ? unreadIds : [tid]) {
          markThreadRead({ threadId: id }).catch(() => {});
        }
        if (siblingIds.length > 1) {
          // Zero the merged row's own pip immediately; the summary refresh reconciles.
          updateThreadRowEverywhere(tid, (t) => ({ ...t, unreadCount: 0 }));
        }
      }
    }

    void loadAdminThreadMembers(threadId, { suppressPermissionDeniedEvent: restoredFromSession });

    // A pending notification jump may target a message older than the first page; load the full
    // history (and bypass the cache) so the jump can always land.
    const wantsFull = String(pendingScrollMessageIdRef.current || "").trim() !== "";
    const cached = !wantsFull && tid ? historyCacheRef.current.get(tid) : null;
    if (cached && cached.items.length) {
      // Instant re-open from cache, then silently reconcile the newest page in the background.
      writeHistoryCache(tid, cached.items, cached.allLoaded); // LRU touch
      if (scopeRef.current === "internal") {
        // Visual marker only — opening still lands at the bottom. Hijacking the scroll to
        // the divider marked the reader as "scrolled up", which stopped the stick-to-bottom
        // on every later message (the never-yank-a-scrolled-up-reader rule).
        setNewMessagesDividerId(computeFirstUnreadMessageId(cached.items));
      }
      setHistoryItems(cached.items);
      setHistoryAllOlderLoaded(Boolean(cached.allLoaded));
      setHistoryLoading(false);
      void resolveSenderProfiles(cached.items, "replace");
      void refreshActiveThreadNewest(tid);
      return;
    }

    setHistoryLoading(true);
    setHistoryAllOlderLoaded(false);
    fetchThreadHistory({
      threadId: tid,
      maxMessageCount: wantsFull ? 0 : HISTORY_PAGE_SIZE,
      skip: 0,
      suppressPermissionDeniedEvent: restoredFromSession,
    })
      .then(async (response) => {
        // The restored selection proved accessible — treat it like any other open from here on.
        if (restoredFromSession && sessionRestoredThreadIdRef.current === tid) {
          sessionRestoredThreadIdRef.current = null;
        }
        const page = Array.isArray(response?.history) ? response.history : [];
        // Bounded pages come newest-first (reverse to ascending); a full load is already ascending.
        const ascending = wantsFull ? page : page.slice().reverse();
        const allLoaded = wantsFull ? true : page.length < HISTORY_PAGE_SIZE;
        if (scopeRef.current === "internal") {
          // Before the receipts below mark everything read: anchor the "New messages"
          // divider. Visual marker only — see the cached branch above for why it must
          // not steal the scroll position.
          setNewMessagesDividerId(computeFirstUnreadMessageId(ascending));
        }
        setHistoryItems(ascending);
        setHistoryAllOlderLoaded(allLoaded);
        writeHistoryCache(tid, ascending, allLoaded);
        await resolveSenderProfiles(ascending, "replace");
        await sendHistoryReadReceipts(ascending);
      })
      .catch((error) => {
        // A selection restored from a previous session can be dead by now: the thread was
        // deleted/repointed (404) or the reader lost membership (403 "user is not a member of
        // this thread" — e.g. an id saved before the per-scope storage keys). Nothing the
        // reader did caused it and nothing they can do fixes it, so drop the selection (and
        // the persisted id) and let the pane fall back to its empty state. A user-initiated
        // open keeps showing the error, as do transient failures (network/5xx) — those may
        // resolve on a re-click.
        if (
          restoredFromSession &&
          (error?.status === 403 || error?.status === 404) &&
          String(selectedThreadIdRef.current || "").trim() === tid
        ) {
          sessionRestoredThreadIdRef.current = null;
          setSelectedThreadId(null);
          persistSelectedThreadId(null, scopeRef.current);
          setHistoryItems([]);
          setHistoryError("");
          setUserDisplayNamesById({});
          setUserAvatarUrlsById({});
          pendingScrollMessageIdRef.current = null;
          return;
        }
        const message = error instanceof Error ? error.message : "failed to load thread history";
        setHistoryItems([]);
        setHistoryError(message);
        setUserDisplayNamesById({});
        setUserAvatarUrlsById({});
        // The thread didn't load — drop any pending notification jump so it can't
        // leak into the next thread we open.
        pendingScrollMessageIdRef.current = null;
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  };

  handleThreadClickRef.current = handleThreadClick;
  // Stable identity for the thread list's `onSelectThread` prop so React.memo on SegmentedThreadList
  // and the rows isn't defeated by a fresh function each render; always dispatches to the latest
  // handleThreadClick via the ref updated above.
  const handleThreadClickStable = useCallback(
    (threadId) => handleThreadClickRef.current(threadId),
    [],
  );
  const handleAttachmentPick = (event) => {
    // Attachments are frozen while editing — an edit cannot change them (the button is disabled
    // too; this covers a picker that was already open).
    if (editingMessageRef.current) {
      event.target.value = "";
      return;
    }
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedAttachments((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const handleRemoveAttachment = (targetIndex) => {
    setSelectedAttachments((prev) => prev.filter((_, index) => index !== targetIndex));
  };

  // Flip a still-pending placeholder into a "couldn't send" state if its echo never lands.
  const markOptimisticFailed = useCallback((clientId) => {
    pendingOptimisticRef.current.delete(clientId);
    setHistoryItems((prev) =>
      prev.map((m) =>
        m?.clientId === clientId && m?.metadata?.optimistic
          ? { ...m, metadata: { ...m.metadata, optimisticFailed: true } }
          : m,
      ),
    );
  }, []);

  // Insert an optimistic own-message placeholder and request the fly animation. The real echo
  // later reconciles in place (see the chat-message subscription), keyed by clientId.
  const enqueueOptimisticMessage = useCallback(
    (threadId, myId, text) => {
      const clientId = `optimistic-${optimisticCounterRef.current++}`;
      const optimistic = {
        clientId,
        threadId,
        body: { text },
        sender: { userId: myId, kind: "user" },
        createdAt: new Date().toISOString(),
        attachments: [],
        metadata: { optimistic: true },
      };
      setHistoryItems((prev) => [...prev, optimistic]);
      pendingFlyRef.current = { clientId };
      const timer = setTimeout(() => markOptimisticFailed(clientId), OPTIMISTIC_FAIL_TIMEOUT_MS);
      pendingOptimisticRef.current.set(clientId, { timer });
    },
    [markOptimisticFailed],
  );

  // ---- Edit message / Delete for everyone (internal unsend) ----------------------------------

  /**
   * Leave edit mode. `restoreDraft` brings back the reply that was being written before the edit
   * started (and its parked attachments) — both Save and Cancel do, so starting an edit never
   * costs the user a half-typed message.
   */
  const exitEditMode = useCallback(({ restoreDraft = true } = {}) => {
    if (!editingMessageRef.current) return;
    editingMessageRef.current = null;
    setEditingMessage(null);
    composerApiRef.current?.finishEdit?.({ restoreDraft });
    const stash = stashedAttachmentsRef.current;
    stashedAttachmentsRef.current = null;
    if (Array.isArray(stash) && stash.length) setSelectedAttachments(stash);
  }, []);
  exitEditModeRef.current = exitEditMode;
  const handleCancelEdit = useCallback(() => exitEditMode({ restoreDraft: true }), [exitEditMode]);

  /**
   * Enter edit mode for one of OUR messages: seed the composer with its text, re-seed its
   * @mentions (so getMentionUserIds() still resolves them), and park the current draft +
   * attachments. `overrides` re-enters edit mode after a failed save with the text the user had
   * typed. Mention ids whose label cannot be resolved from the member directory are kept aside
   * and unioned back at save, so an edit never silently drops a mention it could not render.
   */
  const handleBeginEditMessage = useCallback((message, overrides = null) => {
    const mid = String(message?.id || "").trim();
    const tid = String(message?.threadId || selectedThreadIdRef.current || "").trim();
    if (!mid || !tid) return;
    // The composer only mounts under the manage permission (and never for announce-only WhatsApp
    // groups); without it there is nothing to edit in.
    if (!composerApiRef.current) return;
    // "internal" = staff chat, "whatsapp" = patient WhatsApp (gateway edit + async verdict),
    // "app" = any other patient conversation (Healo app channel, legacy rows) — edited in place
    // like internal. A message that already went through the gateway is WhatsApp whatever the
    // thread row says (same rule the server applies).
    const kind =
      scopeRef.current === "internal"
        ? "internal"
        : activeThreadIsWhatsAppRef.current ||
            message?.metadata?.whatsapp ||
            String(message?.externalId || "").trim()
          ? "whatsapp"
          : "app";
    // NOT getMessageText: it substitutes "No content" for an empty body.
    const text =
      typeof overrides?.text === "string"
        ? overrides.text
        : String(parseMessageBodyText(message?.body) || "");
    const rawMentionIds = Array.isArray(overrides?.mentionIds)
      ? overrides.mentionIds
      : Array.isArray(message?.metadata?.mentions)
        ? message.metadata.mentions
        : [];
    const mentionIds =
      kind === "internal"
        ? [...new Set(rawMentionIds.map((v) => String(v || "").trim()).filter(Boolean))]
        : [];
    const mentions = [];
    const unresolvedMentionIds = [];
    for (const id of mentionIds) {
      const label = String(memberDirectoryRef.current?.[id]?.label || "").trim();
      if (label) mentions.push({ id, label });
      else unresolvedMentionIds.push(id);
    }
    // Only the FIRST beginEdit parks the picks — switching target (or re-entering after a failed
    // save) keeps the original stash.
    if (!editingMessageRef.current) stashedAttachmentsRef.current = selectedAttachmentsRef.current;
    setSelectedAttachments([]);
    const next = {
      id: mid,
      threadId: tid,
      kind,
      hasAttachments: parseAttachments(message).length > 0,
      message,
      unresolvedMentionIds,
    };
    editingMessageRef.current = next;
    setEditingMessage(next);
    composerApiRef.current?.beginEdit?.({ text, mentions });
  }, []);

  /**
   * Save the edit (handleSendMessage routes here while editing). Optimistic: the bubble shows the
   * new text at once and edit mode closes (Slack-style); the server's truth arrives as an update
   * frame with the same id. A refused save rolls the bubble back — only while it still carries OUR
   * optimistic stamp, so a server frame that already landed is never clobbered — and re-enters edit
   * mode with the attempted text so nothing typed is lost.
   */
  const handleSubmitEdit = async () => {
    const editing = editingMessageRef.current;
    if (!editing || sendInProgress) return;
    const text = (composerApiRef.current?.getDraft() || "").trim();
    // Empty text is only a caption removal on a locally edited message that still has attachments.
    if (!text && !(editing.kind !== "whatsapp" && editing.hasAttachments)) {
      setThreadFlagsError("Message text cannot be empty");
      scheduleClearThreadFlagsError();
      return;
    }
    const original = editing.message;
    const prevText = String(parseMessageBodyText(original?.body) || "").trim();
    // Internal: the ids the draft still @mentions, plus the ones we could not re-seed (kept as-is).
    // WhatsApp: undefined — the server leaves metadata.mentions untouched.
    let mentionIds;
    if (editing.kind === "internal") {
      const picked = composerApiRef.current?.getMentionUserIds?.() || [];
      mentionIds = [...new Set([...picked, ...(editing.unresolvedMentionIds || [])])];
    }
    const prevMentions = Array.isArray(original?.metadata?.mentions)
      ? original.metadata.mentions.map((v) => String(v || "").trim()).filter(Boolean)
      : [];
    const sameMentions =
      editing.kind !== "internal" ||
      (mentionIds.length === prevMentions.length && mentionIds.every((id) => prevMentions.includes(id)));
    if (text === prevText && sameMentions) {
      // Nothing changed — no request, no "edited" marker.
      exitEditMode({ restoreDraft: true });
      return;
    }
    // Re-checked at save time: the menu gate ran when the chip rendered. The server is the
    // authority and answers with this same copy.
    if (editing.kind === "whatsapp" && !isWithinWhatsAppEditWindow(original)) {
      setThreadFlagsError("WhatsApp only allows editing a message within 15 minutes of sending it");
      scheduleClearThreadFlagsError();
      return;
    }
    const myId = String(getAuthUser()?.id || "").trim();
    const at = new Date().toISOString();
    const openThreadAtStart = String(selectedThreadIdRef.current || "").trim();
    setSendInProgress(true);
    patchMessageEverywhere(editing.threadId, editing.id, (m) =>
      editing.kind !== "whatsapp"
        ? applyOptimisticInternalEdit(m, { text, mentions: mentionIds, byUserId: myId, at })
        : applyOptimisticWhatsAppEdit(m, { text, byUserId: myId, at }),
    );
    // An edited LAST message refreshes its row preview at once. WhatsApp waits for the verdict
    // frame — the patient still sees the original until WhatsApp confirms.
    if (editing.kind !== "whatsapp") {
      updateThreadRowEverywhere(editing.threadId, (t) =>
        String(t?.lastMessageId || "") === editing.id
          ? { ...t, lastMessagePreview: text || t.lastMessagePreview }
          : t,
      );
    }
    exitEditMode({ restoreDraft: true });
    try {
      setThreadFlagsError("");
      const result = await editThreadMessage({
        threadId: editing.threadId,
        messageId: editing.id,
        text,
        mentions: mentionIds,
      });
      // Internal 200 carries the updated message — apply it (belt and braces if the WS frame is
      // late); WhatsApp 202 waits for the verdict frame. Only a copy that still identifies as THIS
      // message is applied — an id-less body would leave a bubble no later frame can match.
      if (result.message && !result.pending && String(result.message.id || "").trim() === editing.id) {
        patchMessageEverywhere(editing.threadId, editing.id, (m) =>
          m?.clientId ? { ...result.message, clientId: m.clientId } : result.message,
        );
      }
    } catch (error) {
      // Row preview has no stamp of its own — consult the held copy BEFORE the bubble rollback
      // below touches it (the warm-cache copy is patched synchronously, and that is the only copy
      // left once the user has moved to another thread). Refs reflect the last commit, and a failed
      // request always returns after the optimistic patch committed.
      const heldStillOptimistic = hasOptimisticEditStamp(findHeldMessage(editing.threadId, editing.id), at);
      // Bubble: the stamp check runs INSIDE the updater, against the state as actually committed.
      patchMessageEverywhere(editing.threadId, editing.id, (m) =>
        hasOptimisticEditStamp(m, at) ? revertOptimisticEdit(m, original) : m,
      );
      if (editing.kind !== "whatsapp" && heldStillOptimistic) {
        updateThreadRowEverywhere(editing.threadId, (t) =>
          String(t?.lastMessageId || "") === editing.id
            ? { ...t, lastMessagePreview: prevText || t.lastMessagePreview }
            : t,
        );
      }
      setThreadFlagsError(error instanceof Error ? error.message : "Could not edit this message");
      scheduleClearThreadFlagsError();
      // Re-open the edit with what was typed so the user can fix and retry rather than start over
      // — unless they moved on (another thread, or another edit) meanwhile.
      if (
        String(selectedThreadIdRef.current || "").trim() === openThreadAtStart &&
        !editingMessageRef.current
      ) {
        handleBeginEditMessage(original, {
          text,
          mentionIds: editing.kind === "internal" ? mentionIds : [],
        });
      }
    } finally {
      setSendInProgress(false);
    }
  };

  /**
   * Internal Chats "Delete for everyone" = unsend. The bubble goes at once wherever a copy is held;
   * a refused request puts it back in order — unless the server's own frame has meanwhile
   * re-delivered it. Other members drop it from the {deleted:true} broadcast; the thread list
   * re-syncs so the row's preview/time fall back to the previous message.
   */
  const handleUnsendMessage = useCallback(
    async (threadId, messageId) => {
      const tid = String(threadId || "").trim();
      const mid = String(messageId || "").trim();
      if (!tid || !mid) return;
      if (editingMessageRef.current?.id === mid) exitEditMode({ restoreDraft: true });
      // Captured BEFORE the splice (setState updaters run lazily, so the updater cannot hand it out).
      const removed = findHeldMessage(tid, mid);
      patchMessageEverywhere(tid, mid, () => null);
      try {
        setThreadFlagsError("");
        await unsendInternalMessage({ threadId: tid, messageId: mid });
        // Coalesces with the refresh the {deleted:true} frame schedules — one list reload, not two.
        scheduleThreadsRefreshRef.current();
      } catch (error) {
        // 404 = the message is already gone server-side (unsent from another device, or swept by a
        // DEV tool) — putting the bubble back would resurrect a deleted message until the next
        // refetch. Every other failure is a refusal: restore.
        const alreadyGone = error instanceof ApiError && error.status === 404;
        if (removed && !alreadyGone) restoreMessageEverywhere(tid, removed);
        setThreadFlagsError(error instanceof Error ? error.message : "Could not delete this message");
        scheduleClearThreadFlagsError();
      }
    },
    [exitEditMode, findHeldMessage, patchMessageEverywhere, restoreMessageEverywhere, scheduleClearThreadFlagsError],
  );

  // Switching conversations cancels an in-flight edit (and brings the parked draft back).
  useEffect(() => {
    if (editingMessageRef.current) exitEditMode({ restoreDraft: true });
  }, [selectedThreadId, exitEditMode]);

  const handleSendMessage = async () => {
    // Edit mode: the composer's Send/Enter saves the edit instead of sending a new message.
    if (editingMessageRef.current) {
      await handleSubmitEdit();
      return;
    }
    const trimmedMessage = (composerApiRef.current?.getDraft() || "").trim();
    if (!trimmedMessage && selectedAttachments.length === 0) return;
    if (!selectedThreadId) {
      return;
    }
    if (chatConnectionStatus !== "connected") {
      return;
    }
    if (sendInProgress) {
      return;
    }

    setSendInProgress(true);
    try {
      const sendAsPatientDev = import.meta.env.DEV && devSendAsPatient;
      // DISABLED (for now): sending no longer auto-applies `staff_handles` nor
      // assigns the thread to the sender — assignment lives on tasks only.
      // if (!sendAsPatientDev) {
      //   await ensureStaffHandlesOnWhatsappSend(selectedThreadId);
      //   await assignSelfOnSend(selectedThreadId);
      // }
      /** @type {Array<{ mimeType: string, data: string, filename: string }>} */
      const attachmentsPayload =
        selectedAttachments.length > 0
          ? await Promise.all(selectedAttachments.map((file) => readFileAsBase64Payload(file)))
          : [];
      /** @type {{ attachments: typeof attachmentsPayload, sender?: { userId: string, kind: 'user' }, metadata?: Record<string, unknown> }} */
      const sendOptions = { attachments: attachmentsPayload };
      if (sendAsPatientDev) {
        if (!devPatientImpersonationUserId) {
          toast.error(
            "No direct patient found for this thread. Open the participants panel to load members, or pick a thread with a direct member who is not you.",
          );
          return;
        }
        const staffId = String(getAuthUser()?.id || "").trim();
        const patientId = devPatientImpersonationUserId;
        sendOptions.sender = { userId: patientId, kind: "user" };
        sendOptions.metadata = {
          devImpersonatePatient: true,
          patientUserId: patientId,
          ...(staffId ? { impersonationActorUserId: staffId } : {}),
        };
      }
      // Internal Chats @mentions: attach the ids the draft still mentions so receivers can
      // highlight them and mention-notify through muted chats.
      if (scopeRef.current === "internal") {
        const mentionIds = composerApiRef.current?.getMentionUserIds?.() || [];
        if (mentionIds.length) {
          sendOptions.metadata = { ...(sendOptions.metadata || {}), mentions: mentionIds };
        }
      }
      // In the merged WhatsApp view the reply can target a sibling clinic thread (via the reply-from
      // picker or Auto); otherwise the target is just the open thread. `mergedReplyTargetRef`
      // resolves to the open thread when the merged view is inactive.
      const replyTarget = mergedReplyTargetRef.current || null;
      let targetThreadId = String(replyTarget?.threadId || "").trim();
      // Replying from a business number this patient has no thread with yet: find-or-create it
      // first (the same endpoint "New message" uses), then send into it. A failure aborts the send
      // rather than falling back to the open thread — sending from the wrong clinic silently would
      // be worse than not sending at all.
      let startedNewThread = false;
      if (!targetThreadId && replyTarget?.botId && replyTarget?.patientUserId) {
        try {
          const ensured = await ensurePatientThread({
            patientUserId: replyTarget.patientUserId,
            whatsappBotId: replyTarget.botId,
          });
          targetThreadId = String(ensured?.threadId || "").trim();
        } catch (error) {
          toast.error(
            error instanceof Error && error.message
              ? error.message
              : "Could not start a conversation from that number.",
          );
          return;
        }
        if (!targetThreadId) {
          toast.error("Could not start a conversation from that number.");
          return;
        }
        startedNewThread = true;
      }
      if (!targetThreadId) targetThreadId = String(selectedThreadId || "").trim();
      if (!targetThreadId) return;
      sendConversationMessage(targetThreadId, trimmedMessage, sendOptions);
      if (startedNewThread) {
        // The new clinic thread is not in any loaded list yet, so nothing on screen would show the
        // reply: refresh the list and invalidate the merged view's per-patient snapshot so the
        // thread joins the merged stream (its history then follows the usual sibling fetch).
        setMergedPatientRefreshTick((n) => n + 1);
        void loadThreads();
      }
      // Optimistic "fly from composer" for my own text sends. Dev patient-impersonation renders as
      // the patient (left-aligned), so it's excluded; attachment-only sends keep the prior behavior
      // and any attachments on a text send fill in when the echo lands. A cross-clinic merged send
      // skips the fly: WS echoes only enter the OPEN thread's history, so the sent message surfaces
      // when that clinic's page refreshes (its thread row bumps on the echo) rather than optimistically.
      if (!sendAsPatientDev && trimmedMessage && targetThreadId === String(selectedThreadId || "").trim()) {
        const myId = String(getAuthUser()?.id || "").trim();
        if (myId) {
          enqueueOptimisticMessage(targetThreadId, myId, trimmedMessage);
        }
      }
      // A manual reply-from pick applies to THIS reply only — fall back to Auto afterwards (no-op
      // when the merged view is inactive).
      setReplyFromBotId(null);
      composerApiRef.current?.clear();
      setSelectedAttachments([]);
    } catch (error) {
      console.error("[Chat] failed to send message:", error);
    } finally {
      setSendInProgress(false);
    }
  };

  const handleClearSelectedThreadMessages = useCallback(async () => {
    const threadId = String(selectedThreadIdRef.current || selectedThreadId || "").trim();
    if (!threadId || clearThreadMessagesInProgress) {
      return;
    }

    setClearThreadMessagesInProgress(true);
    setHistoryError("");
    try {
      const result = await postAdminClearThreadMessages({ threadId });
      await loadThreads();
      const cleared = Math.max(0, Number(result?.messagesCleared) || 0);
      if (cleared > 0) {
        // Empty the row's cached preview/sender but keep lastMessageAt so the just-cleared thread
        // keeps its place in the list (the server now preserves it too, instead of dropping the
        // thread out of the inbox).
        setThreads((prev) =>
          prev.map((t) =>
            getThreadRowId(t) === threadId
              ? {
                ...t,
                lastMessagePreview: "",
                lastMessageSender: null,
              }
              : t,
          ),
        );
      }
      // Clearing soft-deletes the messages server-side. Drop this thread's client history cache and
      // the visible list before re-opening, so it fetches the now-empty history instead of
      // mergeHistoryNewest keeping the stale cached rows (it returns the cached list unchanged when
      // the fresh page comes back empty) — which left cleared messages lingering in the view.
      historyCacheRef.current.delete(threadId);
      setHistoryItems([]);
      handleThreadClickRef.current(threadId);
      toast.success(
        `Thread ${result.threadId}: cleared ${result.messagesCleared} message${result.messagesCleared === 1 ? "" : "s"} and ${result.triageRedisKeysCleared} triage Redis key${result.triageRedisKeysCleared === 1 ? "" : "s"}.`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not clear thread messages.";
      toast.error(message);
    } finally {
      setClearThreadMessagesInProgress(false);
    }
  }, [clearThreadMessagesInProgress, loadThreads, selectedThreadId]);

  const handleResetSelectedThreadSession = useCallback(async () => {
    const threadId = String(selectedThreadIdRef.current || selectedThreadId || "").trim();
    if (!threadId || clearThreadMessagesInProgress) {
      return;
    }

    setClearThreadMessagesInProgress(true);
    setHistoryError("");
    try {
      const result = await postAdminClearThreadMessages({ threadId, resetSessionOnly: true });
      await loadThreads();
      if (import.meta.env.DEV) {
        toast.success(
          `Thread ${result.threadId}: reset flow session (${result.triageRedisKeysCleared} Redis key${result.triageRedisKeysCleared === 1 ? "" : "s"} cleared); chat history unchanged.`,
        );
      } else {
        toast.success("Session cleared. Chat history is unchanged.");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not reset thread session.";
      toast.error(message);
    } finally {
      setClearThreadMessagesInProgress(false);
    }
  }, [clearThreadMessagesInProgress, loadThreads, selectedThreadId]);

  // Effective "Rewrite with AI" persona: the stored preference when it still exists in the
  // (possibly backend-refreshed) persona list, else the backend-flagged default, else the first.
  const draftReplyAiPersona = useMemo(() => {
    const personas = draftReplyAiMeta.personas;
    if (draftReplyAiPersonaPref && personas.some((p) => p.id === draftReplyAiPersonaPref)) {
      return draftReplyAiPersonaPref;
    }
    const fallback = personas.find((p) => p.isDefault) || personas[0];
    return fallback ? fallback.id : "";
  }, [draftReplyAiMeta, draftReplyAiPersonaPref]);

  // Effective switch values: the sparse stored overrides overlaid on the SELECTED persona's
  // switchDefaults, so changing persona updates untouched toggles to that persona's defaults.
  const draftReplyAiOptions = useMemo(() => {
    const personaDefaults =
      draftReplyAiMeta.personas.find((p) => p.id === draftReplyAiPersona)?.switchDefaults || {};
    /** @type {Record<string, boolean>} */
    const resolved = {};
    for (const def of draftReplyAiMeta.switches) {
      resolved[def.id] =
        typeof draftReplyAiOptionsPref[def.id] === "boolean"
          ? draftReplyAiOptionsPref[def.id]
          : Boolean(personaDefaults[def.id]);
    }
    return resolved;
  }, [draftReplyAiMeta, draftReplyAiPersona, draftReplyAiOptionsPref]);

  // Persist the chosen tone persona (per device) and mirror it into state.
  const setDraftReplyAiPersona = useCallback((personaId) => {
    const prev = getUserPreferences();
    setUserPreferences({ ...prev, draftReplyAiPersona: personaId });
    setDraftReplyAiPersonaPref(getUserPreferences().draftReplyAiPersona);
  }, []);

  // Record an explicit switch override (sparse map — untouched switches keep following the
  // selected persona's defaults) and mirror it into state.
  const toggleDraftReplyAiOption = useCallback((switchId, value) => {
    const prev = getUserPreferences();
    setUserPreferences({
      ...prev,
      draftReplyAiOptions: { ...prev.draftReplyAiOptions, [switchId]: Boolean(value) },
    });
    setDraftReplyAiOptionsPref(getUserPreferences().draftReplyAiOptions);
  }, []);

  // Lazy, best-effort persona/switch metadata fetch (once per session). A failed or empty response
  // keeps the hardcoded fallback meta (no toast) and retries on the next call.
  const ensureDraftReplyMetaLoaded = useCallback(() => {
    if (draftReplyAiMetaCache || draftReplyAiMetaFetchRef.current) {
      return;
    }
    draftReplyAiMetaFetchRef.current = true;
    getAdminDraftReplyPersonas({ token: getAuthToken() })
      .then((meta) => {
        if (meta.personas.length > 0 && meta.switches.length > 0) {
          draftReplyAiMetaCache = meta;
          setDraftReplyAiMeta(meta);
        } else {
          draftReplyAiMetaFetchRef.current = false;
        }
      })
      .catch(() => {
        draftReplyAiMetaFetchRef.current = false;
      });
  }, []);

  const openDraftReplyAiModal = useCallback(() => {
    if (!selectedThreadId || sendInProgress) {
      return;
    }
    setDraftReplyAiModalSource(composerApiRef.current?.getDraft() || "");
    setDraftReplyAiModalOutput("");
    setDraftReplyAiModalOpen(true);
    ensureDraftReplyMetaLoaded();
  }, [selectedThreadId, sendInProgress, ensureDraftReplyMetaLoaded]);

  const handleDraftReplyAiModalOpenChange = useCallback((open) => {
    setDraftReplyAiModalOpen(open);
    if (!open) {
      setDraftReplyAiGenerating(false);
      setDraftReplyAiModalSource("");
      setDraftReplyAiModalOutput("");
    }
  }, []);

  const generateDraftReplyAi = useCallback(async () => {
    const trimmed = draftReplyAiModalSource.trim();
    if (!trimmed || !selectedThreadId) {
      return;
    }
    setDraftReplyAiGenerating(true);
    try {
      const { draft } = await postAdminDraftReply({
        threadId: selectedThreadId,
        text: trimmed,
        token: getAuthToken(),
        persona: draftReplyAiPersona,
        options: draftReplyAiOptions,
      });
      setDraftReplyAiModalOutput(draft);
    } catch (error) {
      console.error("[Chat] draft reply AI failed:", error);
      if (error instanceof ApiError && error.status === 403) {
        toast.error("Employee access required.");
      } else {
        toast.error(error instanceof Error ? error.message : "Could not rewrite draft.");
      }
    } finally {
      setDraftReplyAiGenerating(false);
    }
  }, [draftReplyAiModalSource, draftReplyAiOptions, draftReplyAiPersona, selectedThreadId]);

  const applyDraftReplyAiModal = useCallback(() => {
    const next = draftReplyAiModalOutput.trim();
    if (!next) {
      toast.error("Nothing to apply.");
      return;
    }
    composerApiRef.current?.setDraft(next);
    handleDraftReplyAiModalOpenChange(false);
  }, [draftReplyAiModalOutput, handleDraftReplyAiModalOpenChange]);

  // One-tap "quick rewrite": rewrite the given source using the last-used persona/switch settings
  // (no dialog) and return the polished draft, or null on empty/error. The composer owns the
  // in-flight/undo UI state; this only performs the network call and mirrors `generateDraftReplyAi`
  // error handling.
  const quickRewriteDraftReplyAi = useCallback(
    async (sourceText) => {
      const trimmed = (sourceText || "").trim();
      if (!trimmed || !selectedThreadId) {
        return null;
      }
      // Until the real persona/switch metadata has loaded from the backend, the resolved persona is
      // just the hardcoded fallback id, which THIS deployment's backend may not know (it rejects an
      // unknown persona). So when nothing has been established yet, use the backend's own defaults
      // (omit persona/options) — and kick off the lazy fetch so later clicks use the saved settings.
      const metaLoaded = Boolean(draftReplyAiMetaCache);
      if (!metaLoaded) {
        ensureDraftReplyMetaLoaded();
      }
      try {
        const { draft } = await postAdminDraftReply({
          threadId: selectedThreadId,
          text: trimmed,
          token: getAuthToken(),
          persona: metaLoaded ? draftReplyAiPersona : "",
          options: metaLoaded ? draftReplyAiOptions : undefined,
        });
        return draft;
      } catch (error) {
        console.error("[Chat] quick rewrite failed:", error);
        if (error instanceof ApiError && error.status === 403) {
          toast.error("Employee access required.");
        } else {
          toast.error(error instanceof Error ? error.message : "Could not rewrite draft.");
        }
        return null;
      }
    },
    [draftReplyAiOptions, draftReplyAiPersona, selectedThreadId, ensureDraftReplyMetaLoaded],
  );

  const handleComposerKeyDown = (event) => {
    // Confirming an IME composition also fires Enter — never send on it.
    if (event.nativeEvent?.isComposing) return;
    // Esc leaves edit mode. The mention/slash dropdowns consume their own Escape before delegating
    // here, so an open dropdown closes first and the edit is cancelled on the next press.
    if (event.key === "Escape" && editingMessageRef.current) {
      event.preventDefault();
      event.stopPropagation();
      handleCancelEdit();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  };

  const handleComposerDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleComposerDrop = (event) => {
    event.preventDefault();
    // Attachments are frozen while editing.
    if (editingMessageRef.current) return;
    if (chatConnectionStatus !== "connected") {
      return;
    }
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) {
      return;
    }
    setSelectedAttachments((prev) => [...prev, ...files]);
  };

  const handleComposerPaste = (event) => {
    // Attachments are frozen while editing: pasted images are not intercepted (plain text pastes
    // normally).
    if (editingMessageRef.current) return;
    if (chatConnectionStatus !== "connected") {
      return;
    }
    const images = imageFilesFromClipboardData(event.clipboardData);
    if (!images.length) {
      return;
    }
    event.preventDefault();
    setSelectedAttachments((prev) => [...prev, ...images]);
  };
  const getSenderLabel = (message) => {
    const senderId = String(message?.sender?.userId || "").trim();
    if (!senderId) return "Unknown sender";
    const resolvedName = userDisplayNamesById[senderId];
    // A WhatsApp number never matched/synced to a real patient is relayed under a nameless bridge
    // external-stub account, so its chat profile resolves to a bare login id ("ext-bot-<botId>-
    // <phone>"). When we have no real patient name like that (or none loaded yet), attribute the
    // bubble to the patient via the message's WhatsApp identity: name with phone in parentheses,
    // else the phone alone. A synced/named patient keeps their real name as-is.
    if (!resolvedName || isWhatsAppBridgeStubName(resolvedName)) {
      const whatsappPatientLabel = getWhatsAppPatientSenderLabel(message);
      if (whatsappPatientLabel) return whatsappPatientLabel;
    }
    return (resolvedName || senderId).replace(/_/g, " ");
  };

  const getSenderInitials = (message) => {
    const label = getSenderLabel(message);
    const normalized = String(label || "").trim();
    if (!normalized) return "U";
    return normalized
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");
  };

  const getSenderAvatarUrl = (message) => {
    const senderId = String(message?.sender?.userId || "").trim();
    if (!senderId) return "";
    return String(userAvatarUrlsById[senderId] || "").trim();
  };

  const isOwnMessage = (message) => {
    const senderId = String(message?.sender?.userId || "").trim();
    if (!senderId) return false;
    const currentUserId = String(getAuthUser()?.id || "").trim();
    if (currentUserId && senderId === currentUserId) return true;
    const tokenIds = getUserIdsFromJwt(getAuthToken());
    return tokenIds.includes(senderId);
  };

  const isBotSender = (message) => String(message?.sender?.kind || "").toLowerCase() === "bot";

  const handleMemberUserClick = useCallback(
    (row) => {
      if (row.isBot) return;
      const uid = String(row.id || "").trim();
      if (!uid) return;
      const data = row.adminUserSnapshot;
      if (!data) {
        toast.error("No profile loaded for this member.");
        return;
      }
      const matchIds = normalizeBotExternalMatchUserIds(data.metadata);
      const isDirectSectionMember = (() => {
        const ms = String(row.memberSource || "").toLowerCase();
        return ms === "direct" || ms === "both";
      })();
      if (matchIds.length > 0) {
        if (isDirectSectionMember) {
          // TODO: show user info and edit panel ...
          console.log("[GET /api/admin/users]", uid, data);
        }
        openAmbiguousMigrationModal({
          threadMemberUserId: uid,
          threadMemberSnapshot: data,
          matchUserIds: matchIds,
        });
        return;
      }
      if (isDirectSectionMember) {
        console.log("[GET /api/admin/users]", uid, data);
      }
      setThreadMemberDetailsUser(data);
      setThreadMemberDetailsAvatarUrl(String(row?.avatarUrl || "").trim());
      setThreadMemberDetailsAllowPhoneDuplicates(isDirectSectionMember);
      setThreadMemberDetailsOpen(true);
    },
    [openAmbiguousMigrationModal],
  );

  const onThreadMemberDetailsOpenChange = useCallback((next) => {
    setThreadMemberDetailsOpen(next);
    if (!next) {
      setThreadMemberDetailsUser(null);
      setThreadMemberDetailsAvatarUrl("");
      setThreadMemberDetailsAllowPhoneDuplicates(false);
      setRefreshingPhoneDuplicates(false);
    }
  }, []);

  const handleRefreshUserPhoneDuplicates = useCallback(async () => {
    const userId = String(threadMemberDetailsUser?.id || "").trim();
    if (!userId) {
      toast.error("Missing user ID for duplicate check.");
      return;
    }

    setThreadMemberDetailsOpen(false);
    setThreadMemberDetailsUser(null);
    setRefreshingPhoneDuplicates(true);
    try {
      await refreshAdminUserPhoneDuplicates({ userId });
      await fetchAdminUser({ userId });
      const activeThreadId = String(selectedThreadIdRef.current || "").trim();
      if (activeThreadId) {
        await loadAdminThreadMembers(activeThreadId);
      }
      toast.success("Duplicate metadata refreshed.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not refresh duplicate metadata.";
      toast.error(message);
    } finally {
      setRefreshingPhoneDuplicates(false);
    }
  }, [loadAdminThreadMembers, threadMemberDetailsUser]);

  const handleSaveCorporateInsurance = useCallback(
    async (nextValue) => {
      const userId = String(threadMemberDetailsUser?.id || "").trim();
      if (!userId) {
        toast.error("Missing user ID for corporate insurance update.");
        return;
      }

      setSavingCorporateInsurance(true);
      try {
        // PUT replaces `metadata` wholesale, so fetch the latest record and overlay only the
        // keys we own (mirrors healo_bot's _build_metadata_overlay) to avoid clobbering other
        // metadata (migrationSource, sourcePatientSnapshot, …).
        const fresh = await fetchAdminUser({ userId });
        const existing =
          fresh?.metadata && typeof fresh.metadata === "object" && !Array.isArray(fresh.metadata)
            ? fresh.metadata
            : {};
        const nextMetadata = {
          ...existing,
          corporate_insurance_status: Boolean(nextValue),
          corporate_insurance_check_flag: true,
        };
        const updated = await putAdminUser({ userId, metadata: nextMetadata });
        setThreadMemberDetailsUser(updated || { ...fresh, metadata: nextMetadata });
        const activeThreadId = String(selectedThreadIdRef.current || "").trim();
        if (activeThreadId) {
          await loadAdminThreadMembers(activeThreadId);
        }
        toast.success("Corporate insurance status saved.");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not save corporate insurance status.";
        toast.error(message);
      } finally {
        setSavingCorporateInsurance(false);
      }
    },
    [loadAdminThreadMembers, threadMemberDetailsUser],
  );

  // The selected thread may live outside the loaded ranges: in a server-fed attention section,
  // the search results, the grouped rows, or the pinned default rows. Check every row source so
  // the header never goes blank.
  const selectedThreadListRow =
    threadListWindow.getRowByThreadId(selectedThreadId) ||
    threads.find((item) => item?.threadId === selectedThreadId) ||
    attentionWaiting.find((item) => item?.threadId === selectedThreadId) ||
    attentionPatientReply.find((item) => item?.threadId === selectedThreadId) ||
    attentionBotAck.find((item) => item?.threadId === selectedThreadId) ||
    searchResults.find((item) => item?.threadId === selectedThreadId) ||
    (Array.isArray(groupedThreadRows)
      ? groupedThreadRows.find((item) => item?.threadId === selectedThreadId)
      : undefined) ||
    defaultThreads.find((item) => item?.threadId === selectedThreadId);
  // A thread opened from OUTSIDE the list (triage board, Tasks page, notification) may be in no
  // loaded list at all — the board/Tasks sources are server-complete while the list is windowed
  // and paginated. For those the row is fetched on demand (effect below) and used last-resort.
  const selectedThreadForHeader =
    selectedThreadListRow ||
    (selectedThreadOffListRow &&
    getThreadRowId(selectedThreadOffListRow) === String(selectedThreadId || "").trim()
      ? selectedThreadOffListRow
      : undefined);
  const selectedThreadLastHistoryMessage = selectedThreadForHeader?.lastMessageId
    ? historyItems.find((item) => String(item?.id || "") === String(selectedThreadForHeader.lastMessageId || ""))
    : null;
  const selectedThreadForHeaderWithLastMessage =
    selectedThreadForHeader && selectedThreadLastHistoryMessage
      ? {
          ...selectedThreadForHeader,
          lastMessage: selectedThreadLastHistoryMessage,
          lastMessageReactions: selectedThreadLastHistoryMessage.reactions,
        }
      : selectedThreadForHeader;
  const { label: headerThreadLabel, isPlaceholder: headerThreadPlaceholder } =
    selectedThreadId && selectedThreadForHeaderWithLastMessage
      ? getThreadDisplayName(selectedThreadForHeaderWithLastMessage, { isInternal: isScopedList })
      : { label: "", isPlaceholder: false };
  const conversationHeaderTitle = headerThreadLabel || "Conversation";
  // The identity fact not already in the header title — the patient's WhatsApp name next to a
  // phone title (falling back to the open thread's history until the server has a push name),
  // or the phone next to a push-name title. Rendered as a muted "(…)" suffix.
  const conversationHeaderSecondary = useMemo(() => {
    if (!selectedThreadId || !selectedThreadForHeaderWithLastMessage || isScopedList) return "";
    const fromRow = getThreadSecondaryIdentity(selectedThreadForHeaderWithLastMessage);
    if (fromRow && fromRow !== headerThreadLabel) return fromRow;
    if (
      String(selectedThreadForHeaderWithLastMessage?.titleSource || "") === "phone" &&
      whatsAppPatientNameFromHistory &&
      whatsAppPatientNameFromHistory !== headerThreadLabel
    ) {
      return whatsAppPatientNameFromHistory;
    }
    return "";
  }, [
    selectedThreadId,
    selectedThreadForHeaderWithLastMessage,
    scope,
    headerThreadLabel,
    whatsAppPatientNameFromHistory,
  ]);

  // ── WhatsApp group conversation state ─────────────────────────────────────────────────────
  // Whether the OPEN thread is a bridged WhatsApp group. The row (conversationType /
  // ThreadDetails flag) is authoritative; the history fallback covers a deep-linked thread whose
  // row has not loaded yet — every bridged group message carries the `@g.us` remoteJid.
  const activeThreadIsWaGroup = useMemo(() => {
    if (!selectedThreadId) return false;
    if (isWaGroupThread(selectedThreadForHeaderWithLastMessage)) return true;
    return (historyItems || []).some(isWaGroupMessage);
  }, [selectedThreadId, selectedThreadForHeaderWithLastMessage, historyItems]);

  // The open group's normalized roster snapshot (null for every non-group thread) — feeds the
  // participants panel's group branch and the composer's announce-only guard.
  const activeThreadWaGroupInfo = useMemo(
    () =>
      activeThreadIsWaGroup && selectedThreadForHeaderWithLastMessage
        ? getWaGroupInfo(selectedThreadForHeaderWithLastMessage)
        : null,
    [activeThreadIsWaGroup, selectedThreadForHeaderWithLastMessage],
  );

  // Identity-key → display-name map for the open group (reaction authors, receipt rows, the
  // roster panel). Built in wa-groups.js; the resolver mirrors getSenderLabel's identity: the
  // sender's synced profile name when it is a real one, else the pushName WhatsApp delivered.
  const waGroupNamesByJid = useMemo(() => {
    const resolveSenderName = (msg) => {
      const senderId = String(msg?.sender?.userId || "").trim();
      const resolvedName = senderId ? userDisplayNamesById[senderId] : "";
      const profileName =
        resolvedName && !isWhatsAppBridgeStubName(resolvedName)
          ? String(resolvedName).replace(/_/g, " ").trim()
          : "";
      const pushName = String(msg?.metadata?.whatsapp?.pushName || "").trim();
      return profileName || (pushName !== "Unknown" ? pushName : "");
    };
    return buildWaGroupNamesByJid(activeThreadWaGroupInfo, historyItems, resolveSenderName);
  }, [activeThreadWaGroupInfo, historyItems, userDisplayNamesById]);

  // The roster panel shows the same identity the bubbles do: a participant whose roster entry
  // carries no contact name picks up the conversation-established name from waGroupNamesByJid.
  const activeThreadWaGroupInfoForDisplay = useMemo(
    () => enrichWaGroupRosterNames(activeThreadWaGroupInfo, waGroupNamesByJid),
    [activeThreadWaGroupInfo, waGroupNamesByJid],
  );

  // ── Merged WhatsApp view ──────────────────────────────────────────────────────────────────
  // Per-device preference (on by default): fold every loaded WhatsApp thread for this patient —
  // one per clinic number — into a single chronological, per-clinic-marked stream with a
  // reply-from picker. Everything here is read-only: the threads stay separate on the backend;
  // only the rendered stream and the send target change. When the patient has just one thread
  // nothing below engages and the screen behaves exactly as before.

  // The patient whose threads the merged view needs — the open thread must itself be a patient
  // WhatsApp conversation bound to a clinic number, which is exactly what
  // collectPatientWhatsappThreadRows requires before it merges anything.
  const mergedLookupPatientUserId = useMemo(() => {
    const row = selectedThreadForHeaderWithLastMessage;
    if (!mergedWhatsAppView || !row) return "";
    // Hard guard: a WhatsApp GROUP is one shared conversation, never a per-clinic merge candidate
    // (collectPatientWhatsappThreadRows would already reject it on conversationType, but the
    // merged reply-routing must not hinge on that indirection).
    if (activeThreadIsWaGroup) return "";
    if (!isWhatsappThreadRow(row) || !threadWhatsappBotId(row)) return "";
    return String(row?.patientUserId || "").trim();
  }, [mergedWhatsAppView, selectedThreadForHeaderWithLastMessage, activeThreadIsWaGroup]);

  // Pull EVERY thread this patient is in, not just the ones that happen to be loaded. The list is
  // windowed and paginated, so a patient's other clinic conversation is routinely absent from it;
  // without this fetch the merged view would just show the open thread and quietly drop the past
  // conversations the user came looking for. Failures degrade to the loaded-rows-only behaviour.
  useEffect(() => {
    if (!mergedLookupPatientUserId) {
      mergedPatientFetchKeyRef.current = "";
      setMergedPatientThreadRows((prev) => (prev.length ? [] : prev));
      return undefined;
    }
    const fetchKey = `${mergedLookupPatientUserId}:${mergedPatientRefreshTick}`;
    if (mergedPatientFetchKeyRef.current === fetchKey) return undefined;
    mergedPatientFetchKeyRef.current = fetchKey;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchThreads({
          patientUserId: mergedLookupPatientUserId,
          windowDays: 0,
          limit: MERGED_PATIENT_THREAD_LIMIT,
        });
        if (cancelled) return;
        const rows = (Array.isArray(response?.threads) ? response.threads : []).map((t) =>
          normalizeApiThreadRow(t, null),
        );
        setMergedPatientThreadRows(rows);
      } catch {
        if (cancelled) return;
        // Retry on the next patient switch rather than pinning a failed lookup.
        mergedPatientFetchKeyRef.current = "";
        setMergedPatientThreadRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mergedLookupPatientUserId, mergedPatientRefreshTick]);

  const mergedSiblingThreads = useMemo(() => {
    if (!mergedWhatsAppView || !selectedThreadForHeaderWithLastMessage) return [];
    // Same hard guard as mergedLookupPatientUserId: group threads never merge, so mergedActive —
    // and with it the merged composer reply-target — can never engage on one.
    if (activeThreadIsWaGroup) return [];
    const candidates = [
      // The sparse window's loaded rows (the normal scope's list). Depends on
      // threadListWindow.snapshot below so WS patches to a sibling keep bumping the revision.
      ...threadListWindow.loadedRowsSnapshot(),
      ...threads,
      ...attentionWaiting,
      ...attentionPatientReply,
      ...attentionBotAck,
      ...searchResults,
      ...defaultThreads,
      selectedThreadOffListRow,
      // Last: collectPatientWhatsappThreadRows dedupes by thread id keeping the first copy, so a
      // live in-memory row always wins over this fetched snapshot.
      ...mergedPatientThreadRows,
    ];
    return collectPatientWhatsappThreadRows(selectedThreadForHeaderWithLastMessage, candidates);
  }, [
    mergedWhatsAppView,
    selectedThreadForHeaderWithLastMessage,
    activeThreadIsWaGroup,
    threadListWindow,
    threads,
    attentionWaiting,
    attentionPatientReply,
    attentionBotAck,
    searchResults,
    defaultThreads,
    selectedThreadOffListRow,
    mergedPatientThreadRows,
  ]);

  const mergedActive = mergedSiblingThreads.length > 1;

  // The merged clinics other than the open one — the streams `loadOlderHistory` has to page
  // alongside `historyItems`.
  const mergedOtherThreadIds = useMemo(() => {
    if (!mergedActive) return EMPTY_ATTENTION_LIST;
    const activeId = String(selectedThreadId || "").trim();
    return mergedSiblingThreads.map((t) => getThreadRowId(t)).filter((id) => id && id !== activeId);
  }, [mergedActive, mergedSiblingThreads, selectedThreadId]);
  mergedActiveRef.current = mergedActive;
  mergedSiblingIdsRef.current = mergedOtherThreadIds;

  // Stable dependency signatures: the set of sibling thread ids, and each sibling's last-message
  // id/time (so a new inbound on a NON-open clinic re-fetches that clinic's page and the merged
  // stream stays live between refreshes).
  const mergedSiblingKey = useMemo(
    () => mergedSiblingThreads.map((t) => getThreadRowId(t)).sort().join(","),
    [mergedSiblingThreads],
  );
  const mergedSiblingRevision = useMemo(
    () =>
      mergedSiblingThreads
        .map(
          (t) => `${getThreadRowId(t)}:${String(t?.lastMessageId || "")}:${String(t?.lastMessageAt || "")}`,
        )
        .sort()
        .join("|"),
    [mergedSiblingThreads],
  );

  useEffect(() => {
    if (!mergedActive) {
      if (siblingHistoriesRef.current.size) setSiblingHistories(new Map());
      if (siblingAllLoadedRef.current.size) setSiblingAllLoaded(new Map());
      return undefined;
    }
    let cancelled = false;
    const activeId = String(selectedThreadId || "").trim();
    const otherIds = mergedSiblingThreads
      .map((t) => getThreadRowId(t))
      .filter((id) => id && id !== activeId);

    // Instant seed from the warm cache so switching between a patient's clinics never flashes empty.
    const seeded = new Map();
    const seededAllLoaded = new Map();
    for (const id of otherIds) {
      const cached = historyCacheRef.current.get(id);
      if (cached && cached.items.length) {
        seeded.set(id, cached.items);
        seededAllLoaded.set(id, Boolean(cached.allLoaded));
      }
    }
    setSiblingHistories(seeded);
    setSiblingAllLoaded(seededAllLoaded);

    void (async () => {
      await Promise.all(
        otherIds.map(async (id) => {
          try {
            const response = await fetchThreadHistory({
              threadId: id,
              maxMessageCount: HISTORY_PAGE_SIZE,
              skip: 0,
            });
            if (cancelled) return;
            const ascending = (Array.isArray(response?.history) ? response.history : [])
              .slice()
              .reverse();
            // Merge rather than replace: this effect re-runs whenever a sibling's last message
            // changes, and replacing would throw away every older page "Load older" pulled in.
            const previous = siblingHistoriesRef.current.get(id) || [];
            const merged = mergeHistoryNewest(previous, ascending);
            // A wholesale replacement (gap) or a first load re-derives "reached the start" from
            // this page; an overlapping refresh keeps whatever "Load older" already established.
            const allLoaded =
              merged.gap || previous.length === 0
                ? ascending.length < HISTORY_PAGE_SIZE
                : Boolean(siblingAllLoadedRef.current.get(id));
            writeHistoryCache(id, merged.items, allLoaded);
            void resolveSenderProfiles(ascending, "merge");
            setSiblingHistories((prev) => {
              const next = new Map(prev);
              next.set(id, merged.items);
              return next;
            });
            setSiblingAllLoaded((prev) => {
              const next = new Map(prev);
              next.set(id, allLoaded);
              return next;
            });
          } catch {
            /* skip this clinic's page; the rest of the merged stream still renders */
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedActive, selectedThreadId, mergedSiblingKey, mergedSiblingRevision]);

  // "Load older" hides only once EVERY merged clinic has reached the start of its conversation —
  // the open thread running out first must not strand the others at their newest page.
  const mergedAllOlderLoaded = useMemo(() => {
    if (!mergedActive) return historyAllOlderLoaded;
    if (!historyAllOlderLoaded) return false;
    return mergedOtherThreadIds.every((id) => Boolean(siblingAllLoaded.get(id)));
  }, [mergedActive, historyAllOlderLoaded, mergedOtherThreadIds, siblingAllLoaded]);

  // Every clinic thread this patient has, merged or not — the reply-from picker needs it even
  // when there is only one (you can still reply from a DIFFERENT number, see mergedReplySources).
  const mergedSourcesAll = useMemo(
    () => buildMergedSources(mergedSiblingThreads, whatsappNumbers),
    [mergedSiblingThreads, whatsappNumbers],
  );
  // What the merged VIEW is built from: empty unless the stream is actually merged, so the source
  // filter, segment colors and header subline stay exactly as they were.
  const mergedSources = useMemo(
    () => (mergedActive ? mergedSourcesAll : EMPTY_ARRAY),
    [mergedActive, mergedSourcesAll],
  );

  // botId → source meta, so the renderer resolves a cluster's clinic short-name/colors.
  const mergedSourceByBotId = useMemo(() => {
    const map = new Map();
    for (const s of mergedSources) map.set(s.botId, s);
    return map;
  }, [mergedSources]);

  // Source visibility (the header's "All sources" filter): clinics the user unchecked are dropped
  // from the merged stream AND from the reply-from picker. Keyed to the PATIENT, not the open
  // thread, so clicking between the same patient's clinics keeps the filter. Empty = show all.
  const mergedPatientUserId = String(selectedThreadForHeaderWithLastMessage?.patientUserId || "");
  const [hiddenSourceBotIds, setHiddenSourceBotIds] = useState(() => new Set());
  useEffect(() => {
    setHiddenSourceBotIds(new Set());
  }, [mergedPatientUserId, mergedActive]);

  // Defensive: never let the filter empty the conversation (the UI also disables the last one).
  const mergedVisibleSources = useMemo(() => {
    const visible = mergedSources.filter((s) => !hiddenSourceBotIds.has(s.botId));
    return visible.length ? visible : mergedSources;
  }, [mergedSources, hiddenSourceBotIds]);
  const mergedVisibleBotIds = useMemo(
    () => new Set(mergedVisibleSources.map((s) => s.botId)),
    [mergedVisibleSources],
  );
  // Reply-from options: the clinic threads that exist (visible ones only) PLUS every other
  // configured business number. Staff are not limited to the numbers already used with this
  // patient — picking an unused one creates that number's thread on send (see handleSendMessage).
  // Threadless entries are send targets only: they carry no history, so they never reach the
  // rendered stream or the source filter. Gated on mergedLookupPatientUserId, which is already
  // "the open thread is a patient WhatsApp conversation bound to a number, merged view on".
  const mergedReplySources = useMemo(() => {
    if (!mergedLookupPatientUserId) return EMPTY_ARRAY;
    const base = mergedActive ? mergedVisibleSources : mergedSourcesAll;
    if (!base.length) return EMPTY_ARRAY;
    // A clinic the user unchecked in the source filter stays out of the picker entirely — it must
    // not come back round as an "other number", which would label an existing conversation as a
    // new one. So the extras are drawn from the numbers no thread exists for, not merely the ones
    // missing from `base`.
    const started = new Set(mergedSourcesAll.map((source) => source.botId));
    const unused = whatsappNumbers.filter((n) => !started.has(String(n?.botId || "").trim()));
    return buildReplyFromSources(base, unused);
  }, [
    mergedLookupPatientUserId,
    mergedActive,
    mergedVisibleSources,
    mergedSourcesAll,
    whatsappNumbers,
  ]);
  const mergedReplyByBotId = useMemo(() => {
    const map = new Map();
    for (const s of mergedReplySources) map.set(s.botId, s);
    return map;
  }, [mergedReplySources]);

  // Every source plus its current visibility — what the filter popover renders.
  const mergedSourceOptions = useMemo(
    () => mergedSources.map((s) => ({ ...s, visible: mergedVisibleBotIds.has(s.botId) })),
    [mergedSources, mergedVisibleBotIds],
  );

  const handleToggleSourceVisibility = useCallback(
    (botId) => {
      const id = String(botId || "").trim();
      if (!id) return;
      setHiddenSourceBotIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        // Keep at least one clinic on screen.
        if (mergedSources.length - next.size <= 1) return prev;
        next.add(id);
        return next;
      });
    },
    [mergedSources],
  );
  const handleShowAllSources = useCallback(() => setHiddenSourceBotIds(new Set()), []);

  const mergedClusters = useMemo(() => {
    if (!mergedActive) return null;
    const activeId = String(selectedThreadId || "").trim();
    const buckets = mergedSiblingThreads
      .filter((thread) => mergedVisibleBotIds.has(threadWhatsappBotId(thread)))
      .map((thread) => {
        const id = getThreadRowId(thread);
        return {
          threadId: id,
          botId: threadWhatsappBotId(thread),
          items: id === activeId ? historyItems : siblingHistories.get(id) || [],
        };
      });
    return buildMergedClusters(buckets);
  }, [
    mergedActive,
    mergedSiblingThreads,
    mergedVisibleBotIds,
    selectedThreadId,
    historyItems,
    siblingHistories,
  ]);

  // Reply-from selection. null = Auto (follow the last message's clinic). A manual pick overrides
  // for the current reply only; it is cleared on send (see handleSendMessage) and whenever the open
  // patient changes.
  const [replyFromBotId, setReplyFromBotId] = useState(null);
  useEffect(() => {
    setReplyFromBotId(null);
  }, [selectedThreadId, mergedActive]);

  // Auto always lands on a clinic the conversation already used, never on an unused number.
  const mergedAutoBotId =
    mergedClusters && mergedClusters.length
      ? mergedClusters[mergedClusters.length - 1].botId
      : threadWhatsappBotId(selectedThreadForHeaderWithLastMessage);
  // A manual pick only counts while it is still offered (its clinic was not filtered out, and the
  // number is still configured); otherwise fall back to Auto.
  const mergedEffectiveBotId =
    (replyFromBotId && mergedReplyByBotId.has(replyFromBotId) ? replyFromBotId : "") ||
    mergedAutoBotId;
  const mergedReplySource = mergedReplyByBotId.get(mergedEffectiveBotId) || null;
  // Where this reply goes. An empty threadId with a botId set means "that number has no thread
  // with this patient yet" — handleSendMessage creates it before sending, and must NOT fall back
  // to the open thread, which would quietly send from the wrong number. With no source at all
  // (merged view inactive) the target is just the open thread.
  const mergedReplyTarget = mergedReplySource
    ? {
      threadId: mergedReplySource.threadId,
      botId: mergedReplySource.hasThread === false ? mergedReplySource.botId : "",
      patientUserId: mergedLookupPatientUserId,
    }
    : { threadId: String(selectedThreadId || ""), botId: "", patientUserId: "" };
  // Read at send time (handleSendMessage is defined earlier in this hook); a ref avoids a stale
  // closure. Falls back to the open thread when the merged view is inactive.
  const mergedReplyTargetRef = useRef(mergedReplyTarget);
  // Assigned below the group block too — see the mergedGroupActive override further down.

  const mergedGetSegmentMeta = useCallback(
    (botId) => mergedSourceByBotId.get(String(botId || "")) || null,
    [mergedSourceByBotId],
  );
  const handleSelectReplyFrom = useCallback((botId) => {
    setReplyFromBotId(botId ? String(botId) : null);
  }, []);

  // Auto whenever there is no *usable* manual pick — including when the picked clinic was just
  // filtered out, so the pill's AUTO tag always matches where the reply actually goes.
  const mergedReplyIsAuto = !(replyFromBotId && mergedReplyByBotId.has(replyFromBotId));
  const mergedHeaderSubline = mergedActive
    ? `${mergedSources.length} WhatsApp threads · merged view`
    : "";

  // ── Merged WhatsApp GROUP reply-from ──────────────────────────────────────────────────────
  // DELIBERATELY SEPARATE from `mergedActive` above, and it never sets it. A merged group shows
  // ONE history: every clinic gateway is a member of the same WhatsApp group, so each sibling
  // thread holds a near-duplicate copy of the same messages, and running them through
  // buildMergedClusters would render every message N times. The ONLY thing the two merges
  // share is ReplyFromPicker.
  //
  // The two are mutually exclusive by construction, not by convention: mergedActive derives
  // from collectPatientWhatsappThreadRows, which rejects anything whose conversationType is not
  // exactly "whatsapp" — so it is always false for a group. The guards that already forbid
  // groups from the patient merge stay exactly as they are.
  const mergedGroupRow =
    mergedWaGroupsView && activeThreadIsWaGroup ? selectedThreadForHeaderWithLastMessage : null;
  const mergedGroupSources = useMemo(
    () => (mergedGroupRow ? getMergedGroupSources(mergedGroupRow, whatsappNumbers) : EMPTY_ARRAY),
    [mergedGroupRow, whatsappNumbers],
  );
  const mergedGroupActive = mergedGroupSources.length > 1;
  const [groupReplyFromBotId, setGroupReplyFromBotId] = useState(null);
  useEffect(() => {
    setGroupReplyFromBotId(null);
  }, [selectedThreadId, mergedGroupActive]);
  // Auto = the canonical sibling's own gateway (the thread whose history is on screen). The
  // patient path's "whichever clinic the conversation was last on" is meaningless here, because
  // there is only one shared conversation.
  const groupAutoBotId = threadWhatsappBotId(mergedGroupRow);
  const groupEffectiveBotId = groupReplyFromBotId || groupAutoBotId;
  const groupReplySource =
    mergedGroupSources.find((source) => source.botId === groupEffectiveBotId) ||
    mergedGroupSources[0] ||
    null;
  const handleSelectGroupReplyFrom = useCallback((botId) => {
    setGroupReplyFromBotId(botId ? String(botId) : null);
  }, []);

  // handleSendMessage reads this ref and sends into whatever thread id it names, so routing a
  // group reply out of a chosen clinic needs no change there at all: it is just "which sibling
  // thread do we post into".
  mergedReplyTargetRef.current = mergedGroupActive
    ? {
      threadId: groupReplySource?.threadId || String(selectedThreadId || ""),
      // A group reply always has a thread: every gateway in a merged group is already a member,
      // so there is never one to create.
      botId: "",
      patientUserId: "",
    }
    : mergedReplyTarget;

  // Read synchronously by revealMessage: a setState updater is the wrong place to learn whether a
  // clinic was hidden (it may run later, or twice under StrictMode).
  const hiddenSourceBotIdsRef = useRef(hiddenSourceBotIds);
  hiddenSourceBotIdsRef.current = hiddenSourceBotIds;

  // Pull a merged sibling clinic's WHOLE conversation into the interleaved stream so a jump target
  // older than its loaded page exists to scroll to. Always re-emits `siblingHistories` (a fresh Map
  // even when nothing was fetched) because that is the dependency the scroll-after-load effect
  // watches to drain a pending jump.
  const loadFullSiblingHistory = useCallback(
    async (threadId) => {
      const id = String(threadId || "").trim();
      if (!id) return;
      if (siblingAllLoadedRef.current.get(id)) {
        setSiblingHistories((prev) => new Map(prev));
        return;
      }
      try {
        const response = await fetchThreadHistory({ threadId: id, maxMessageCount: 0 });
        // A full load comes back ascending already (unlike a bounded page).
        const ascending = Array.isArray(response?.history) ? response.history : [];
        writeHistoryCache(id, ascending, true);
        void resolveSenderProfiles(ascending, "merge");
        setSiblingHistories((prev) => {
          const next = new Map(prev);
          next.set(id, ascending);
          return next;
        });
        setSiblingAllLoaded((prev) => {
          const next = new Map(prev);
          next.set(id, true);
          return next;
        });
      } catch {
        // Nothing to jump to — drop the pending target so it can't leak into the next thread.
        pendingScrollMessageIdRef.current = null;
      }
    },
    [writeHistoryCache, resolveSenderProfiles],
  );

  /**
   * Reveal a specific message: scroll to it and ring-highlight it, loading whatever history is
   * needed first. Backs in-chat search results (and any other "jump to this message" affordance).
   *
   * openToTarget alone is not enough: when the thread is ALREADY open it only tries a DOM scroll,
   * which fails silently for anything older than the loaded page — the common case for a search
   * hit. Each branch below loads the conversation that owns the target before scrolling.
   */
  const revealMessage = useCallback(
    (target) => {
      const threadId = String(target?.threadId || "").trim();
      const messageId = String(target?.messageId || "").trim();
      if (!threadId || !messageId) return;
      const botId = String(target?.botId || "").trim();

      const merged = mergedActiveRef.current;
      // A clinic filtered out of the merged stream has no bubble to scroll to — show it again
      // first, and treat the target as not-yet-rendered since the un-hide only lands next render.
      const wasHidden = merged && Boolean(botId) && hiddenSourceBotIdsRef.current.has(botId);
      if (wasHidden) {
        setHiddenSourceBotIds((prev) => {
          if (!prev.has(botId)) return prev;
          const next = new Set(prev);
          next.delete(botId);
          return next;
        });
      }

      if (!wasHidden && scrollToMessage(messageId)) return;

      pendingScrollMessageIdRef.current = messageId;

      const openThreadId = String(selectedThreadIdRef.current || "").trim();
      if (threadId === openThreadId) {
        // Already holding the whole conversation: the bubble either exists (handled above, or is
        // about to after the un-hide re-render) or never will. Re-opening would refetch every
        // message and re-render the un-virtualized list for nothing — which is what made stepping
        // through results crawl once the first jump had loaded everything.
        if (historyAllOlderLoadedRef.current) {
          if (wasHidden) {
            // Un-hiding a clinic only lands next render, so nudge the dependency the
            // scroll-after-load effect watches and let it drain the pending jump. (Frame callbacks
            // are not an option — they never fire in a hidden window.) Only reachable in the
            // merged view, which is the only place siblingHistories means anything.
            setSiblingHistories((prev) => new Map(prev));
          } else {
            // The whole conversation is loaded and the bubble still is not in the DOM, so nothing
            // is coming that would let this land. Drop the target rather than leaving it armed to
            // hijack the next unrelated render.
            pendingScrollMessageIdRef.current = null;
          }
          return;
        }
        // Re-open the same thread WITH the jump pending: handleThreadClick then bypasses the warm
        // cache and fetches the full history (maxMessageCount 0), so any hit can land.
        handleThreadClickRef.current(threadId);
        return;
      }
      if (merged && mergedSiblingIdsRef.current.includes(threadId)) {
        void loadFullSiblingHistory(threadId);
        return;
      }
      // A different conversation entirely (an all-chats hit): the existing open-and-jump path
      // already forces a full history load for a pending target.
      openToTarget({ threadId, messageId });
    },
    [scrollToMessage, openToTarget, loadFullSiblingHistory],
  );

  // Whether the header's thread is an internal GROUP chat — its name is the thread's own `name`
  // (editable), unlike a 1:1 (the other person) or a patient thread. direct/group is encoded on
  // `type` and `metadata.internalKind`; anything internal that isn't direct is a group.
  const headerIsInternalGroup = useMemo(() => {
    if (!isInternalScope || !selectedThreadForHeaderWithLastMessage) return false;
    return !isInternalDirectThread(selectedThreadForHeaderWithLastMessage);
  }, [scope, selectedThreadForHeaderWithLastMessage]);

  // Fetch-on-miss for the header row (see selectedThreadOffListRow). The row joins the
  // updateThreadRowEverywhere optimistic patches; if a later refresh/search loads the thread
  // into a list, that copy wins above. Fetch failures keep today's generic header.
  useEffect(() => {
    const tid = String(selectedThreadId || "").trim();
    if (!tid || selectedThreadListRow) return undefined;
    if (selectedThreadOffListRow && getThreadRowId(selectedThreadOffListRow) === tid) {
      return undefined;
    }
    let cancelled = false;
    fetchThread({
      threadId: tid,
      // A restored selection is being probed, not opened by the user — its 403 must not toast.
      suppressPermissionDeniedEvent: String(sessionRestoredThreadIdRef.current || "") === tid,
    })
      .then((row) => {
        if (cancelled || !row) return;
        if (String(selectedThreadIdRef.current || "").trim() !== tid) return;
        setSelectedThreadOffListRow(row);
      })
      .catch((error) => {
        if (cancelled) return;
        // A definitive 404 means the thread is gone (deleted / repointed) — clear a selection
        // that only survives as a persisted restore, instead of leaving the generic
        // "Conversation" header forever. Transient failures keep today's generic header.
        if (
          error?.status === 404 &&
          String(selectedThreadIdRef.current || "").trim() === tid
        ) {
          setSelectedThreadId(null);
          persistSelectedThreadId(null, scopeRef.current);
          setHistoryItems([]);
          setHistoryError("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedThreadId, selectedThreadListRow, selectedThreadOffListRow]);

  // The contact a 1:1 thread's title resolves to (the DTO `title`/MemberTitle is the other
  // party's display name). We offer an inline rename only when that contact's admin record is
  // loaded AND it is NOT yet synced/merged with a Plato patient — a Plato-synced patient's name
  // is owned by Plato, so it must not be edited here. `metadata.migrationSource === "plato.patients"`
  // is the server-set sync marker (written by plato_sync's PatientProcessor).
  const headerRenameTarget = useMemo(() => {
    const rows = Array.isArray(threadMembersGrouped?.direct) ? threadMembersGrouped.direct : [];
    const nonSelf = rows.filter((r) => r?.kind === "user" && !r.isSelf && String(r?.id || "").trim());
    if (!nonSelf.length) return null;
    const patientRow = nonSelf.find(
      (r) => String(r?.adminUserSnapshot?.userType || "").trim().toLowerCase() === "patient",
    );
    // A 1:1 has a single counterpart; for multi-member threads only act on a clear patient row.
    const row = patientRow || (nonSelf.length === 1 ? nonSelf[0] : null);
    if (!row) return null;
    const admin = row.adminUserSnapshot;
    if (!admin) return null; // admin snapshot not loaded yet → can't confirm Plato-sync status
    if (isPlatoSynced(admin)) return null;
    return { userId: String(row.id).trim(), currentName: String(row.label || "").trim() };
  }, [threadMembersGrouped]);

  // Rename the thread's (non-Plato) contact. For a 1:1 the contact's display name IS the thread
  // title (MemberTitle), so this is how staff retitle a thread not yet synced with a Plato patient.
  // We patch the contact's profile.displayName (field-merged server-side), optimistically reflect
  // it on the active thread's title, and refresh member snapshots so labels/sync status stay current.
  const handleRenameThreadContact = useCallback(
    async (userId, nextName) => {
      const uid = String(userId || "").trim();
      const name = String(nextName || "").trim();
      if (!uid || !name) return;
      try {
        await putAdminUser({ userId: uid, profile: { displayName: name } });
        const tid = String(selectedThreadIdRef.current || "").trim();
        if (tid) {
          // Everywhere (window, attention, search, off-list header) — not just the legacy list —
          // so the new title shows wherever the row renders.
          updateThreadRowEverywhere(tid, (t) => ({ ...t, title: name }));
          void loadAdminThreadMembers(tid);
        }
        toast.success("Name updated.");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not update name.";
        toast.error(message);
      }
    },
    [loadAdminThreadMembers, updateThreadRowEverywhere],
  );

  // Rename an internal GROUP chat. The group name is the thread's own `name` (the server row's
  // `title` is the participant list), so we PUT the thread name, optimistically patch the row
  // everywhere (list + off-list header), and let the WS refresh reconcile.
  const handleRenameGroup = useCallback(
    async (nextName) => {
      const tid = String(selectedThreadIdRef.current || "").trim();
      const name = String(nextName || "").trim();
      if (!tid || !name) return;
      try {
        await updateAdminThread({ threadId: tid, name });
        updateThreadRowEverywhere(tid, (t) => ({ ...t, name }));
        toast.success("Group name updated.");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not update group name.");
      }
    },
    [updateThreadRowEverywhere],
  );

  const apiMembersIsEmpty =
    memberListSource === "api" &&
    threadMembersGrouped.direct.length === 0 &&
    threadMembersGrouped.team.length === 0 &&
    threadMembersGrouped.bots.length === 0;

  const showEmptyMembersPanel =
    !membersPanelLoading &&
    memberListSource !== "error" &&
    ((memberListSource === "api" && apiMembersIsEmpty) ||
      (memberListSource === "history" && historyDerivedMemberRows.length === 0));

  const handleConfirmAmbiguousMigrationChoice = useCallback(async () => {
    const sourceUserId = String(ambiguousMigrationModal.threadMemberUserId || "").trim();
    const targetUserId = String(ambiguousMigrationModal.selectedUserId || "").trim();
    if (!sourceUserId || !targetUserId) {
      setMergeResultModal({
        open: true,
        success: false,
        title: "Merge failed",
        description: "Missing source or target user.",
        sourceName: "",
        targetName: "",
      });
      return;
    }

    // The merge consumes the source record, so it is only legal on a record Plato does not own:
    // plato_sync would recreate a synced patient on its next pass and re-split the conversation.
    // The dialog already disables the button for this case — this is the last line of defence.
    if (isPlatoSynced(ambiguousMigrationModal.threadMemberSnapshot)) {
      setMergeResultModal({
        open: true,
        success: false,
        title: "Merge not allowed",
        description:
          "This patient record is synced with Plato, so it cannot be merged into another record. Only records that are not Plato-synced can be merged.",
        sourceName: "",
        targetName: "",
      });
      return;
    }

    setAmbiguousMigrationModal((prev) => ({ ...prev, submitting: true }));
    try {
      await postAdminUserMerge({ fromUserId: sourceUserId, toUserId: targetUserId });
      closeAmbiguousMigrationModal();

      const sourceName =
        String(formatAdminUserDisplayName(ambiguousMigrationModal.threadMemberSnapshot) || "").trim() || sourceUserId;
      const targetUserSnapshot =
        ambiguousMigrationModal.candidates.find((entry) => String(entry?.userId || "").trim() === targetUserId)?.user ?? null;
      const targetName = String(formatAdminUserDisplayName(targetUserSnapshot) || "").trim() || targetUserId;

      const refreshedThreads = await loadThreads();
      const candidateThreadIds = [];
      const activeThreadId = String(selectedThreadIdRef.current || "").trim();
      if (activeThreadId) {
        candidateThreadIds.push(activeThreadId);
      }
      for (const thread of refreshedThreads) {
        const tid = getThreadRowId(thread);
        if (tid && !candidateThreadIds.includes(tid)) {
          candidateThreadIds.push(tid);
        }
      }

      let nextThreadId = activeThreadId;
      for (const tid of candidateThreadIds) {
        try {
          const members = await fetchAdminThreadMembers({ threadId: tid });
          const memberSnapshot = parseThreadMemberIdsFromMembersPayload(members.users, members.userIds, members.botIds);
          const mergedDirectIds = new Set(memberSnapshot.directUserIds || []);
          if (mergedDirectIds.has(targetUserId)) {
            nextThreadId = tid;
            break;
          }
        } catch {
          // Ignore single-thread membership read errors while searching for the survivor chat.
        }
      }

      if (nextThreadId) {
        handleThreadClickRef.current(nextThreadId);
      }

      setMergeResultModal({
        open: true,
        success: true,
        title: "User merged successfully",
        description: "Patient data migration completed.",
        sourceName,
        targetName,
      });
    } catch (error) {
      closeAmbiguousMigrationModal();
      setMergeResultModal({
        open: true,
        success: false,
        title: "Merge failed",
        description: error instanceof Error ? error.message : "Could not merge users.",
        sourceName: "",
        targetName: "",
      });
    } finally {
      setAmbiguousMigrationModal((prev) => ({ ...prev, submitting: false }));
    }
  }, [ambiguousMigrationModal, closeAmbiguousMigrationModal, loadThreads]);

  const handleConfirmPatientCorrect = useCallback(async () => {
    const threadMemberUserId = String(ambiguousMigrationModal.threadMemberUserId || "").trim();
    if (!threadMemberUserId) {
      setMergeResultModal({
        open: true,
        success: false,
        title: "Update failed",
        description: "Missing thread member user.",
        sourceName: "",
        targetName: "",
      });
      return;
    }

    setAmbiguousMigrationModal((prev) => ({ ...prev, submitting: true }));
    try {
      const selectedUserSnapshot = ambiguousMigrationModal.threadMemberSnapshot;
      const currentMetadata =
        selectedUserSnapshot?.metadata && typeof selectedUserSnapshot.metadata === "object"
          ? selectedUserSnapshot.metadata
          : {};
      const nextMetadata = { ...currentMetadata };
      delete nextMetadata.botExternalAmbiguousLookupDuplicate;
      delete nextMetadata.botExternalAmbiguousMatchUserIds;

      await putAdminUser({
        userId: threadMemberUserId,
        metadata: nextMetadata,
      });

      const selectedName = String(formatAdminUserDisplayName(selectedUserSnapshot) || "").trim() || threadMemberUserId;

      closeAmbiguousMigrationModal();
      await loadThreads();
      const activeThreadId = String(selectedThreadIdRef.current || "").trim();
      if (activeThreadId) {
        await loadAdminThreadMembers(activeThreadId);
      }

      setMergeResultModal({
        open: true,
        success: true,
        title: "Patient confirmed",
        description: "Cleared duplicate-ambiguity metadata for this patient.",
        sourceName: selectedName,
        targetName: "",
      });
    } catch (error) {
      closeAmbiguousMigrationModal();
      setMergeResultModal({
        open: true,
        success: false,
        title: "Update failed",
        description: error instanceof Error ? error.message : "Could not update user metadata.",
        sourceName: "",
        targetName: "",
      });
    } finally {
      setAmbiguousMigrationModal((prev) => ({ ...prev, submitting: false }));
    }
  }, [ambiguousMigrationModal, closeAmbiguousMigrationModal, loadAdminThreadMembers, loadThreads]);

  // ── Delivery/seen receipts: inputs the bubble indicator needs ──────────────────────────────
  //
  // WhatsApp threads report the CHAT's status with no per-person identity, so the indicator reads
  // channelStatus there instead of the per-recipient arrays.
  const activeThreadIsWhatsApp = useMemo(() => {
    const tid = String(selectedThreadId || "").trim();
    if (!tid) return false;
    // The merged patient view stitches WhatsApp threads together by construction.
    if (mergedActive) return true;
    // A WhatsApp GROUP is a WhatsApp conversation for every channel-status purpose (ticks read
    // channelStatus, revoke offered on our sends) even though its conversationType differs.
    if (activeThreadIsWaGroup) return true;
    // Resolve across EVERY row source, not just `threads` — the open thread may live in the
    // windowed list, an attention section, or search results (which is exactly how a WhatsApp
    // thread ended up rendering the internal staff-roster popover).
    const thread = findKnownThreadRow(tid);
    if (String(thread?.metadata?.conversationType || "").toLowerCase() === "whatsapp") {
      return true;
    }
    // Last resort when no loaded row matches (deep link, stale cache): messages that went through
    // the gateway carry `metadata.whatsapp`, which no internal message ever does.
    return (historyItems || []).some((m) => m?.metadata?.whatsapp);
    // `threads` is a recompute trigger, not read directly: findKnownThreadRow reads refs.
  }, [selectedThreadId, mergedActive, activeThreadIsWaGroup, threads, historyItems, findKnownThreadRow]);

  // Every OTHER member of the open thread. Ticks escalate only when all of them have the receipt,
  // so this is the denominator. null (not []) while the roster is unknown — an empty array would
  // read as "no recipients", which trivially satisfies "all".
  const activeThreadRecipientIds = useMemo(() => {
    const tid = String(selectedThreadId || "").trim();
    if (!tid || activeThreadIsWhatsApp) return null;
    const membership = threadMemberIdsByThreadId[tid];
    if (!membership) return null;
    const bots = new Set((membership.botIds || []).map((id) => String(id || "").trim()));
    const ids = new Set();
    for (const raw of [...(membership.directUserIds || []), ...(membership.teamUserIds || [])]) {
      const id = String(raw || "").trim();
      if (!id || bots.has(id) || isCurrentUserId(id)) continue;
      ids.add(id);
    }
    return [...ids];
  }, [selectedThreadId, activeThreadIsWhatsApp, threadMemberIdsByThreadId, isCurrentUserId]);

  // In a Healo-app patient conversation the TICK answers "did the patient get it": membership
  // there is exactly the patient as a direct member, with staff reaching the thread through the
  // channel team — so escalate the tick on the direct members alone. Null everywhere else
  // (internal chats, WhatsApp channel mode, unknown rosters) keeps the all-recipients rule.
  // The receipts popover still shows the full roster either way.
  const activeThreadPrimaryRecipientIds = useMemo(() => {
    const tid = String(selectedThreadId || "").trim();
    if (!tid || activeThreadIsWhatsApp || isScopedList) return null;
    const thread = findKnownThreadRow(tid);
    if (String(thread?.metadata?.conversationType || "").toLowerCase() !== "app") return null;
    const membership = threadMemberIdsByThreadId[tid];
    if (!membership) return null;
    const bots = new Set((membership.botIds || []).map((id) => String(id || "").trim()));
    const ids = new Set();
    for (const raw of membership.directUserIds || []) {
      const id = String(raw || "").trim();
      if (!id || bots.has(id) || isCurrentUserId(id)) continue;
      ids.add(id);
    }
    return ids.size > 0 ? [...ids] : null;
    // `threads` is a recompute trigger, not read directly: findKnownThreadRow reads refs.
  }, [
    selectedThreadId,
    activeThreadIsWhatsApp,
    isInternalScope,
    isScopedList,
    threads,
    findKnownThreadRow,
    threadMemberIdsByThreadId,
    isCurrentUserId,
  ]);

  // Id → name/avatar for the receipt detail popover. Built primarily from the member roster rather
  // than userDisplayNamesById, which is populated from message SENDERS only — a silent member who
  // read the message but never posted would otherwise render as a raw user id.
  const memberDirectory = useMemo(() => {
    const out = {};
    const groups = threadMembersGroupedResolved || {};
    for (const row of [...(groups.direct || []), ...(groups.team || [])]) {
      const id = String(row?.id || "").trim();
      if (!id) continue;
      out[id] = { label: row.label || "", avatarUrl: row.avatarUrl || "" };
    }
    for (const [id, label] of Object.entries(userDisplayNamesById || {})) {
      if (!out[id]) {
        out[id] = { label, avatarUrl: String(userAvatarUrlsById?.[id] || "").trim() };
      }
    }
    return out;
  }, [threadMembersGroupedResolved, userDisplayNamesById, userAvatarUrlsById]);
  // Read by handleBeginEditMessage (declared above) to re-seed a message's @mentions by label.
  memberDirectoryRef.current = memberDirectory;
  activeThreadIsWhatsAppRef.current = activeThreadIsWhatsApp;

  return {
    activeThreadIsWhatsApp,
    activeThreadRecipientIds,
    activeThreadPrimaryRecipientIds,
    memberDirectory,
    // WhatsApp group conversation: rendering mode for the cluster list, the roster snapshot for
    // the participants panel / composer guard, and jid→name resolution for reactions + receipts.
    activeThreadIsWaGroup,
    activeThreadWaGroupInfo,
    activeThreadWaGroupInfoForDisplay,
    waGroupNamesByJid,
    scope,
    isInternalScope,
    isDirectScope,
    isScopedList,
    directChatDisabled,
    onlineUserIds,
    availabilityByUserId,
    createInternalChat,
    startPatientMessage,
    handleDeleteChat,
    deleteChatInProgress,
    handleLeaveGroup,
    leaveGroupInProgress,
    handleMarkThreadRead,
    handleMarkThreadUnread,
    notifyComposerTyping,
    typingThreadIds,
    headerTypingUserIds,
    newMessagesDividerId,
    mutedChatThreadIds,
    handleToggleChatMute,
    deleteThreadTarget,
    deleteThreadInProgress,
    requestDeleteThread,
    handleDeleteThreadDialogOpenChange,
    confirmDeleteThread,
    archivedMessages,
    archivedLoading,
    archivedError,
    archivedAllLoaded,
    archiveTriggered,
    loadNextArchivedPage,
    triggerArchiveLoad,
    sortedThreads,
    filteredSortedThreads,
    attentionWaiting: filteredAttentionWaiting,
    attentionPatientReply: filteredAttentionPatientReply,
    attentionBotAck: filteredAttentionBotAck,
    // The fully-virtual list's data window (normal scope): exact total + index-addressed rows.
    threadListWindow,
    threadUnfilteredTotal,
    // Groups view: server-complete grouped rows (null outside the view) + cap indicator.
    groupedThreadRows,
    groupedThreadsTruncated,
    threadSearchTruncated: searchTruncated,
    threadSearchLoading: searchLoading,
    threadSearchActive: searching,
    threadFilterLockedOnly,
    setThreadFilterLockedOnly,
    // DISABLED (for now): staff_handles + assignee filters are hidden.
    // threadFilterStaffHandlesOnly,
    // setThreadFilterStaffHandlesOnly,
    threadFilterAwaitingReplyOnly,
    setThreadFilterAwaitingReplyOnly,
    threadFilterWhatsappOnly,
    setThreadFilterWhatsappOnly: toggleThreadFilterWhatsappOnly,
    threadFilterAppOnly,
    setThreadFilterAppOnly: toggleThreadFilterAppOnly,
    // threadFilterAssignedToMe,
    // setThreadFilterAssignedToMe,
    // threadFilterAssigneeId,
    // setThreadFilterAssigneeId,
    whatsappNumbers,
    threadFilterWhatsappBotId,
    setThreadFilterWhatsappBotId,
    threadSearchQuery,
    setThreadSearchQuery,
    threadFilterLastMessageRange,
    setThreadFilterLastMessageRange,
    clearThreadFilters,
    listClockMs,
    threadMemberIdsByThreadId,
    selectedThreadId,
    historyItems,
    historyLoading,
    historyError,
    // Merged view: the "Load older" affordance tracks ALL clinics in the stream, not just the
    // open thread (mergedAllOlderLoaded falls back to the plain flag when not merged).
    historyAllOlderLoaded: mergedAllOlderLoaded,
    historyLoadingOlder,
    loadOlderHistory,
    threadFlagsError,
    localUnreadByThreadId,
    rightPanelOpen,
    setRightPanelOpen,
    chatScrollAreaRef,
    chatContentRef,
    fileInputRef,
    historyEndRef,
    // Merged WhatsApp view swaps in the interleaved multi-clinic stream; otherwise the plain
    // single-thread clusters.
    messageClusters: mergedActive ? mergedClusters : messageClusters,
    mergedActive,
    mergedSourceOptions,
    mergedVisibleSourceCount: mergedVisibleSources.length,
    mergedAllSourcesVisible: hiddenSourceBotIds.size === 0,
    onToggleSourceVisibility: handleToggleSourceVisibility,
    onShowAllSources: handleShowAllSources,
    mergedGetSegmentMeta,
    // In-chat search scope: the conversations the open view is actually showing. In the merged
    // view that is every VISIBLE clinic thread, so searching matches what the reader can see;
    // otherwise it is just the open thread.
    searchableThreadIds: mergedActive
      ? mergedVisibleSources.map((source) => source.threadId).filter(Boolean)
      : [selectedThreadId].filter(Boolean),
    revealMessage,
    // The composer's reply-from picker serves BOTH merges; every other merged-* value below
    // stays on the patient-merge flag and is therefore provably off for groups.
    composerReplyPickerActive: mergedGroupActive || mergedReplySources.length > 1,
    // Hidden clinics can't be replied from; the filter popover gets the full list with flags.
    // Unused business numbers CAN (they start a thread on send). For a merged group the sources
    // ARE the clinic gateways carrying the group — all of them already have a thread.
    mergedSources: mergedGroupActive ? mergedGroupSources : mergedReplySources,
    mergedReplySource: mergedGroupActive ? groupReplySource : mergedReplySource,
    mergedReplyBotId: mergedGroupActive ? groupEffectiveBotId : mergedEffectiveBotId,
    mergedReplyIsAuto: mergedGroupActive ? !groupReplyFromBotId : mergedReplyIsAuto,
    onSelectReplyFrom: mergedGroupActive ? handleSelectGroupReplyFrom : handleSelectReplyFrom,
    mergedHeaderSubline,
    ambiguousMigrationModal,
    setAmbiguousMigrationModal,
    mergeResultModal,
    composerStickerPreviews,
    composerAttachmentPreviews,
    composerOtherAttachments,
    composerApiRef,
    selectedAttachments,
    chatConnectionStatus,
    sendInProgress,
    devSendAsPatient,
    setDevSendAsPatient,
    devPatientImpersonationUserId,
    devPatientImpersonationLabel,
    clearThreadMessagesInProgress,
    draftReplyAiModalOpen,
    draftReplyAiModalSource,
    setDraftReplyAiModalSource,
    draftReplyAiModalOutput,
    setDraftReplyAiModalOutput,
    draftReplyAiGenerating,
    draftReplyAiPersonas: draftReplyAiMeta.personas,
    draftReplyAiSwitchDefs: draftReplyAiMeta.switches,
    draftReplyAiPersona,
    setDraftReplyAiPersona,
    draftReplyAiOptions,
    toggleDraftReplyAiOption,
    openDraftReplyAiModal,
    handleDraftReplyAiModalOpenChange,
    generateDraftReplyAi,
    applyDraftReplyAiModal,
    quickRewriteDraftReplyAi,
    threadMembersNotice,
    threadMembersError,
    membersPanelLoading,
    showEmptyMembersPanel,
    memberListSource,
    threadMembersGrouped: threadMembersGroupedResolved,
    historyDerivedMemberRows,
    selectedThreadForHeader: selectedThreadForHeaderWithLastMessage,
    headerThreadPlaceholder,
    conversationHeaderTitle,
    conversationHeaderSecondary,
    headerRenameTarget,
    handleRenameThreadContact,
    headerIsInternalGroup,
    handleRenameGroup,
    handleThreadFlagCheckedChange,
    handleAiHandlingClear,
    handleAcknowledgeMessage,
    handleToggleBusinessReaction,
    handleRevokeMessage,
    // Internal Chats "Delete for everyone" (unsend) + edit mode (both scopes).
    handleUnsendMessage,
    editingMessage,
    handleBeginEditMessage,
    handleCancelEdit,
    handleDeleteMessage,
    handleClearMessagesAfter,
    botAckThresholdMinutes,
    botAckMaxHours,
    patientReplyThresholdMinutes,
    patientReplyMaxHours,
    attentionTruncated,
    flowSessionResetDialogOpen,
    handleFlowSessionResetDialogOpenChange,
    confirmFlowSessionReset,
    handleThreadGroupChange,
    // DISABLED (for now): thread assignment — assignment lives on tasks only.
    // handleAssignThread,
    // handleUnassignThread,
    // handleClaimThread,
    // handleReleaseThread,
    handleStatusChange,
    handleSnoozeThread,
    handlePatientTagToggle,
    threadTasks,
    threadTasksLoading,
    loadThreadTasks,
    handleTaskCreate,
    handleTaskToggle,
    handleTaskClaim,
    handleTaskAssign,
    handleTaskDelete,
    threadLens,
    setThreadLens,
    // WhatsApp Groups lens: active flag + the unread badge for the lens control.
    waGroupsLensActive,
    waGroupsUnreadCount,
    viewMode,
    setViewMode,
    needsCount,
    // DISABLED (for now): the "Mine" lens is hidden with thread assignment.
    // mineCount,
    rightPanelMode,
    setRightPanelMode,
    isCurrentUserId,
    threadGroups,
    threadGroupsError,
    threadListView,
    setThreadListView,
    collapsedSections,
    toggleSectionCollapsed,
    collapseAllSections,
    expandAllSections,
    participantsCollapsedSections,
    toggleParticipantsSection,
    handleThreadClick,
    handleThreadClickStable,
    highlightedMessageId,
    handleAttachmentPick,
    handleRemoveAttachment,
    handleSendMessage,
    handleClearSelectedThreadMessages,
    handleResetSelectedThreadSession,
    handleComposerKeyDown,
    handleComposerDragOver,
    handleComposerDrop,
    handleComposerPaste,
    getSenderLabel,
    getSenderInitials,
    getSenderAvatarUrl,
    isOwnMessage,
    isBotSender,
    handleMemberUserClick,
    threadMemberDetailsOpen,
    threadMemberDetailsUser,
    threadMemberDetailsAvatarUrl,
    threadMemberDetailsAllowPhoneDuplicates,
    refreshingPhoneDuplicates,
    savingCorporateInsurance,
    onThreadMemberDetailsOpenChange,
    handleRefreshUserPhoneDuplicates,
    handleSaveCorporateInsurance,
    closeAmbiguousMigrationModal,
    closeMergeResultModal,
    handleConfirmAmbiguousMigrationChoice,
    handleConfirmPatientCorrect,
    // Exposed so the patient editor / switch-patient dialogs can refresh the open thread's
    // member snapshots (header tags + participants panel) and the thread list after a change.
    loadAdminThreadMembers,
    loadThreads,
  };
}
